// Advanced Calculator Shared Functions
// These functions are used by both desktop and mobile versions
// DESKTOP IS THE SINGLE SOURCE OF TRUTH - these functions are extracted from desktop
// Updated: Added preservation age and minimum withdrawal enforcement

import { SimulationEngine } from './SimulationEngine';
import { FinancialConfig, calculateAfterTaxIncome, calculateSGContributions, resolveTransferBalanceCapForCalendarYear } from './FinancialConfig';
import { historicalData } from './data';
import { getLumpSumAmountForYear, parseLumpSumWithdrawals } from './lumpSumWithdrawals.js';
import {
    getLumpSumContributionsForYear,
    parseLumpSumContributions,
    summarizeYearContributions,
} from './lumpSumContributions.js';

export { calculateAfterTaxIncome };
export { parseLumpSumWithdrawals, getLumpSumAmountForYear, MAX_LUMP_SUM_WITHDRAWALS } from './lumpSumWithdrawals.js';
export { parseLumpSumContributions, MAX_LUMP_SUM_CONTRIBUTIONS } from './lumpSumContributions.js';

/**
 * Constant real returns for Adviser Basic View (same Advanced engine, flat assumed return/inflation).
 * Uses Fisher deflation to match historical providedReturns: (1+r)/(1+i) - 1.
 */
export function buildConstantAssumedReturns(startYear, periodLength, assumedReturnPct, assumedInflationPct) {
    const years = Math.max(0, Math.floor(Number(periodLength) || 0));
    const start = Number(startYear) || 0;
    const nominal = (Number(assumedReturnPct) || 0) / 100;
    const infl = (Number(assumedInflationPct) || 0) / 100;
    const netR = ((1 + nominal) / (1 + infl)) - 1;
    const out = [];
    for (let i = 0; i < years; i++) {
        out.push({ year: start + i, r: netR, inflation: infl });
    }
    return out;
}

/**
 * Employment while working (phased salary) vs part-time Work Bonus income while retired.
 * Part-time fields are Advanced "Income & Home" inputs; previously UI-only.
 */
export function resolveEmploymentIncomesForYear({
    isARetired,
    isBRetired = true,
    salaryA = 0,
    salaryB = 0,
    partTimeA = 0,
    partTimeB = 0,
    useAfterTax = false,
}) {
    const rawA = isARetired ? (Number(partTimeA) || 0) : (Number(salaryA) || 0);
    const rawB = isBRetired ? (Number(partTimeB) || 0) : (Number(salaryB) || 0);
    const empIncomeA = useAfterTax && rawA > 0 ? calculateAfterTaxIncome(rawA) : rawA;
    const empIncomeB = useAfterTax && rawB > 0 ? calculateAfterTaxIncome(rawB) : rawB;
    return {
        empIncomeA,
        empIncomeB,
        employmentIncome: empIncomeA + empIncomeB,
    };
}

// Helper function: Determine if super can be accessed based on age and retirement settings
// This respects both preservation age (60) and the user's nominated retirement age
const canAccessSuperWithRetirementAge = (currentAge, retirementAge, allowWithdrawalsBeforeRetirementAge) => {
    const preservationAge = 60;
    const retAge = Number(retirementAge);
    const resolvedRetAge = Number.isFinite(retAge) && retAge > 0 ? retAge : preservationAge;

    // Opt-in: allow draws from preservation age while still working.
    if (allowWithdrawalsBeforeRetirementAge) {
        return currentAge >= preservationAge;
    }

    // Per-member nominated retirement age. Age 65+ is a condition of release in Australia,
    // but this planner only starts account-based pension draws (and SIS minimums) once that
    // member has reached their retirement age — so a still-working partner is not forced
    // into minimums while the other partner is already retired.
    return currentAge >= resolvedRetAge;
};

const resolveSuperAccessAge = (accessAge, retirementAge) => {
    const access = Number(accessAge);
    if (Number.isFinite(access) && access > 0) return access;
    return Number(retirementAge) || 60;
};

const canAccessSuperWithBucketStrategy = ({
    currentAge,
    retirementAge,
    accessAge,
    allowWithdrawalsBeforeRetirementAge,
    enableBridgeStrategy,
    householdRetirementTriggered = false,
}) => {
    // Bridge defers the start of drawdowns to the nominated access age so the outside-super
    // buckets are spent first. Age 65 is an unconditional condition of release, so nothing
    // defers past it, and the allow-before-retirement opt-in still wins.
    if (enableBridgeStrategy
        && !allowWithdrawalsBeforeRetirementAge
        && currentAge < 65
        && currentAge < resolveSuperAccessAge(accessAge, retirementAge)) {
        return false;
    }
    // Nominated-retirement (or allow-before) access always wins.
    // Bridge may expand EARLY access; it must never lock someone who is already eligible.
    if (canAccessSuperWithRetirementAge(currentAge, retirementAge, allowWithdrawalsBeforeRetirementAge)) {
        return true;
    }
    // Once the first partner retires the household draws on both balances. Each member still
    // needs their own condition of release, so this never reaches below preservation age.
    if (householdRetirementTriggered && currentAge >= 60) {
        return true;
    }
    if (enableBridgeStrategy) {
        // Early-retirement bridge path: allow from explicit access age (floor preservation 60)
        // while still below normal retirement-gated eligibility above.
        return currentAge >= Math.max(60, resolveSuperAccessAge(accessAge, 60));
    }
    return false;
};

const buildMortgageStrategyConfig = (inputs = {}) => {
    const enabled = !!inputs.enableMortgageComparison;
    const balance = Math.max(0, Number(inputs.mortgageBalance) || 0);
    if (!enabled || balance <= 0) {
        return {
            enabled: false,
            balance: 0,
            annualRepayment: 0,
            annualRate: 0,
            strategy: 'keep',
            payoffYearIndex: null,
            payoffAmount: 0,
        };
    }

    const strategy = String(inputs.mortgageStrategy || 'keep');
    const payoffSource = String(inputs.mortgagePayoffSource || 'super');
    const scheduleType = String(inputs.mortgagePayoffScheduleType || 'year');
    const annualRate = Math.max(0, Number(inputs.mortgageInterestRate) || 0) / 100;
    const annualRepayment = Math.max(0, Number(inputs.mortgageAnnualRepayment) || 0);
    let payoffYearIndex = null;

    if (strategy === 'immediatePayoff') {
        payoffYearIndex = 0;
    } else if (strategy === 'laterPayoff' || strategy === 'partialPayoff') {
        const rawValue = Number(inputs.mortgagePayoffScheduleValue);
        if (Number.isFinite(rawValue) && rawValue > 0) {
            if (scheduleType === 'age') {
                const startAge = Number(inputs.yourAge) || 0;
                payoffYearIndex = Math.max(0, Math.round(rawValue - startAge));
            } else {
                payoffYearIndex = Math.max(0, Math.round(rawValue - 1));
            }
        }
    }

    const payoffAmount = strategy === 'partialPayoff'
        ? Math.max(0, Number(inputs.mortgagePartialPayoffAmount) || 0)
        : balance;

    return {
        enabled: true,
        balance,
        annualRepayment,
        annualRate,
        strategy,
        payoffSource,
        payoffYearIndex,
        payoffAmount,
    };
};

const applyMortgageYear = (state, mortgageConfig, yearIndex) => {
    if (!mortgageConfig.enabled || state.mortgageBalance <= 0) {
        return {
            mortgageOpeningBalance: Math.max(0, state.mortgageBalance || 0),
            mortgageInterest: 0,
            mortgageRepayment: 0,
            mortgagePayoff: 0,
            mortgageOutflow: 0,
            mortgageBalance: Math.max(0, state.mortgageBalance || 0),
            mortgagePaidOff: Math.max(0, state.mortgageBalance || 0) <= 0,
        };
    }

    const openingBalance = Math.max(0, state.mortgageBalance || 0);
    let workingBalance = openingBalance;
    let payoff = 0;
    let interest = 0;
    let repayment = 0;

    const payoffDue = mortgageConfig.payoffYearIndex === yearIndex
        && (mortgageConfig.strategy === 'immediatePayoff'
            || mortgageConfig.strategy === 'laterPayoff'
            || mortgageConfig.strategy === 'partialPayoff');

    if (payoffDue) {
        payoff = Math.min(workingBalance, mortgageConfig.payoffAmount || workingBalance);
        workingBalance = Math.max(0, workingBalance - payoff);
    }

    if (workingBalance > 0) {
        interest = workingBalance * mortgageConfig.annualRate;
        workingBalance += interest;
        repayment = Math.min(workingBalance, mortgageConfig.annualRepayment);
        workingBalance = Math.max(0, workingBalance - repayment);
    }

    state.mortgageBalance = workingBalance;

    return {
        mortgageOpeningBalance: openingBalance,
        mortgageInterest: interest,
        mortgageRepayment: repayment,
        mortgagePayoff: payoff,
        mortgageOutflow: repayment + payoff,
        mortgageBalance: workingBalance,
        mortgagePaidOff: workingBalance <= 1,
    };
};

// Helper function: Calculate annuity payment (PMT) for Dynamic SWR
// Formula: PMT = PV × [r(1+r)^n] / [(1+r)^n - 1]
// This calculates the withdrawal amount that will deplete the balance to targetFinalBalance over n years
const calculateAnnuityPayment = (presentValue, annualRate, yearsRemaining, targetFinalBalance = 0) => {
  // Only withdraw from the amount above target final balance
  const withdrawableAmount = Math.max(0, presentValue - targetFinalBalance);
  if (withdrawableAmount <= 0 || yearsRemaining <= 0) return 0;
  if (annualRate === 0) return withdrawableAmount / yearsRemaining;
  
  const ratePerPeriod = annualRate;
  const numPeriods = yearsRemaining;
  const factor = Math.pow(1 + ratePerPeriod, numPeriods);
  
  return withdrawableAmount * (ratePerPeriod * factor) / (factor - 1);
};

const AGE_PENSION_AGE = 67;

const calculateAgePensionForHousehold = ({
    totalAssets,
    otherIncome,
    isCouple,
    isHomeowner,
    year = 0,
    pensionGrowthRate,
    rentPaidAnnual = 0,
    employmentIncome = 0,
    ageA,
    ageB,
    includePension = true
}) => {
    if (!includePension) return 0;

    if (!isCouple) {
        return ageA >= AGE_PENSION_AGE
            ? (Number(SimulationEngine.calculatePension(totalAssets, otherIncome, false, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome)) || 0)
            : 0;
    }

    const eligibleCount = (ageA >= AGE_PENSION_AGE ? 1 : 0) + (ageB >= AGE_PENSION_AGE ? 1 : 0);
    if (eligibleCount === 0) return 0;

    const coupleEntitlement = Number(
        SimulationEngine.calculatePension(totalAssets, otherIncome, true, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome)
    ) || 0;

    return coupleEntitlement * (eligibleCount / 2);
};

const shouldUsePhasedRetirementCalculations = ({
    phasedRetirement, isCouple, yourAge, partnerAge, yourRetirementAge, partnerRetirementAge,
}) => {
    // The same pre-retirement rules apply to singles; "phased" means at least one
    // member is still working, not necessarily that the household is a couple.
    // A member below their nominated retirement age is still working even when the toggle is off.
    const stillWorking = (age, retAge) => Number(age) < Number(retAge);
    return !!phasedRetirement
        || stillWorking(yourAge, yourRetirementAge)
        || (!!isCouple && stillWorking(partnerAge, partnerRetirementAge));
};

/** Geometric path from today's balance to the handed-off nest egg (no cliff at retirement). */
const interpolateHandoffBalance = (startBal, endBal, step, steps) => {
    if (steps <= 0) return Math.max(0, Number(endBal) || 0);
    const t = Math.min(1, Math.max(0, step / steps));
    const s = Math.max(0, Number(startBal) || 0);
    const e = Math.max(0, Number(endBal) || 0);
    if (s <= 0) return e * t;
    if (e <= 0) return s * (1 - t);
    return s * Math.pow(e / s, t);
};

/**
 * Drive each member along a smooth path to their Accumulation handoff nest egg,
 * then lock at retirement. skipGrowth* means returns/SG are already in the path.
 */
const applyAccumulationHandoffPath = ({
    handoff,
    applied,
    currentAge,
    partnerCurrentAge,
    retAgeA,
    retAgeB,
    balA,
    balB,
    isCouple,
    yearIndex,
    yourAgeStart,
    partnerAgeStart,
}) => {
    if (!handoff?.enabled) {
        return { balA, balB, applied, skipGrowthA: false, skipGrowthB: false };
    }
    const next = { ...(applied || { you: false, partner: false }) };
    let nextA = balA;
    let nextB = balB;
    let skipGrowthA = false;
    let skipGrowthB = false;

    if (handoff.you) {
        if (next.you) {
            // Locked — normal post-retirement growth / drawdown
        } else if (currentAge >= retAgeA) {
            nextA = Math.max(0, Number(handoff.you.balance) || 0);
            next.you = true;
        } else {
            const steps = Math.max(1, retAgeA - yourAgeStart);
            nextA = interpolateHandoffBalance(
                handoff.you.startBalance ?? balA,
                handoff.you.balance,
                yearIndex + 1,
                steps
            );
            skipGrowthA = true;
        }
    }
    if (isCouple && handoff.partner) {
        if (next.partner) {
            // Locked
        } else if (partnerCurrentAge >= retAgeB) {
            nextB = Math.max(0, Number(handoff.partner.balance) || 0);
            next.partner = true;
        } else {
            const steps = Math.max(1, retAgeB - partnerAgeStart);
            nextB = interpolateHandoffBalance(
                handoff.partner.startBalance ?? balB,
                handoff.partner.balance,
                yearIndex + 1,
                steps
            );
            skipGrowthB = true;
        }
    }
    return { balA: nextA, balB: nextB, applied: next, skipGrowthA, skipGrowthB };
};

const netOfContributionsTax = (gross, taxableIncome = 0) =>
    SimulationEngine.SGManager.netOfContributionsTax(gross, taxableIncome);

/** Combined handoff nest egg (0 when handoff is off). Used for MSI search bounds. */
const resolveAccumulationHandoffNestEgg = (handoff, isCouple) => {
    if (!handoff?.enabled) return 0;
    return Math.max(0, Number(handoff.you?.balance) || 0)
        + (isCouple ? Math.max(0, Number(handoff.partner?.balance) || 0) : 0);
};

/**
 * Starting supers for dynamic strategies when Accumulation handoff is armed:
 * already-retired members snap to nest egg; still-working members start from startBalance
 * (path years then interpolate to nest egg via applyAccumulationHandoffPath).
 */
const resolveSupersForAccumulationHandoff = (inputs) => {
    const yourSuperIn = Number(inputs?.yourSuper) || 0;
    const isCouple = String(inputs?.relationshipStatus || '') === 'couple';
    const partnerSuperIn = isCouple ? (Number(inputs?.partnerSuper) || 0) : 0;
    const h = inputs?.accumulationHandoff;
    if (!h?.enabled) {
        return { yourSuper: yourSuperIn, partnerSuper: partnerSuperIn };
    }
    const yourAge = Number(inputs.yourAge) || 0;
    const partnerAge = Number(inputs.partnerAge) || 0;
    const retA = Number(inputs.yourRetirementAge) || yourAge;
    const retB = Number(inputs.partnerRetirementAge) || partnerAge;
    let yourSuper = yourSuperIn;
    let partnerSuper = partnerSuperIn;
    if (h.you) {
        yourSuper = yourAge >= retA
            ? Math.max(0, Number(h.you.balance) || 0)
            : Math.max(0, Number(h.you.startBalance ?? yourSuperIn) || 0);
    }
    if (isCouple && h.partner) {
        partnerSuper = partnerAge >= retB
            ? Math.max(0, Number(h.partner.balance) || 0)
            : Math.max(0, Number(h.partner.startBalance ?? partnerSuperIn) || 0);
    } else if (!isCouple) {
        partnerSuper = 0;
    }
    return { yourSuper, partnerSuper };
};

/** Spendable non-super household income — must match the detailed table "Other Income" + pension columns. */
const computeHouseholdOtherIncome = ({
    pensionAnnual = 0,
    employmentIncome = 0,
    other = 0,
    otherNonTax = 0,
    annuityReal = 0,
    afterTaxDbReal = 0,
    dbReal = 0,
    rentalIncome = 0,
    infrastructureDividends = 0
}) => {
    const dbIncome = afterTaxDbReal > 0
        ? afterTaxDbReal
        : (dbReal > 0
            ? Math.max(0, calculateAfterTaxIncome(other + dbReal) - calculateAfterTaxIncome(other))
            : 0);
    return pensionAnnual + employmentIncome + other + otherNonTax
        + annuityReal + dbIncome + rentalIncome + infrastructureDividends;
};

/** Indexed annuity/DB for year i — advance cumulative index after each year. */
const getIndexedIncomeStreams = ({
    annuityPay = 0,
    dbPension = 0,
    annuityIsIndexed = true,
    definedBenefitIsIndexed = true,
    other = 0,
    cumulativeInflationIndex = 1
}) => {
    const annuityReal = annuityIsIndexed ? annuityPay : annuityPay / cumulativeInflationIndex;
    const dbReal = definedBenefitIsIndexed ? dbPension : dbPension / cumulativeInflationIndex;
    const dbIncomeForHousehold = dbReal > 0
        ? Math.max(0, calculateAfterTaxIncome(other + dbReal) - calculateAfterTaxIncome(other))
        : 0;
    return { annuityReal, dbReal, dbIncomeForHousehold };
};

const advanceInflationIndex = (cumulativeInflationIndex, yearInflation) =>
    cumulativeInflationIndex * (1 + (Number(yearInflation) || 0.025));

/** Assessable non-pension income for Age Pension means test (indexed gross streams). */
const computePensionAssessableOtherIncome = ({
    other = 0,
    otherNonTax = 0,
    annuityReal = 0,
    dbReal = 0,
    rentalIncome = 0,
    infrastructureDividends = 0
}) => other + otherNonTax + annuityReal + dbReal + rentalIncome + infrastructureDividends;

/** Detailed table columns — must sum with pension + super to match totalOtherIncome + withdrawal. */
const formatStrategyIncomeDisplay = ({
    isPhasedRetirement,
    currentAge,
    partnerCurrentAge,
    retAgeA,
    retAgeB,
    salaryA = 0,
    salaryB = 0,
    other = 0,
    otherNonTax = 0,
    annuityReal = 0,
    dbIncomeForHousehold = 0,
    rentalIncome = 0,
    infrastructureDividends = 0
}) => {
    // After-tax employment so Other Income + Super + Pension lines up with totalIncome.
    const employmentDisplay = isPhasedRetirement
        ? ((currentAge < retAgeA ? calculateAfterTaxIncome(salaryA) : 0)
            + (partnerCurrentAge < retAgeB ? calculateAfterTaxIncome(salaryB) : 0))
        : 0;
    return {
        otherTaxableIncome: Math.round(employmentDisplay + other + rentalIncome + infrastructureDividends),
        otherNonTaxableIncome: Math.round(otherNonTax),
        annuityIncome: Math.round(annuityReal),
        definedBenefitPension: Math.round(dbIncomeForHousehold)
    };
};

/** Split liquid/infrastructure by each partner's share of total super (avoids negative balA when one balance is small). */
const initPhasedSuperBalances = (yourSuper, partnerSuper, infrastructureVal, isCouple, isPhasedRetirement) => {
    const yourSuperVal = Number(yourSuper) || 0;
    const partnerSuperVal = isCouple ? (Number(partnerSuper) || 0) : 0;
    const totalSuper = yourSuperVal + partnerSuperVal;
    const ownerRatioA = totalSuper > 0 ? yourSuperVal / totalSuper : (isCouple ? 0.5 : 1);
    const ownerRatioB = isCouple ? 1 - ownerRatioA : 0;
    const infraVal = Number(infrastructureVal) || 0;
    if (!isPhasedRetirement) {
        return { balA: 0, balB: 0, infraBalA: infraVal, infraBalB: 0 };
    }
    const infraBalA = infraVal * ownerRatioA;
    const infraBalB = isCouple ? infraVal * ownerRatioB : 0;
    return {
        balA: Math.max(0, yourSuperVal - infraBalA),
        balB: Math.max(0, partnerSuperVal - infraBalB),
        infraBalA,
        infraBalB
    };
};

/** Phased retirement: withdraw from liquid super first, then infrastructure. Never exceeds available balance. */
const applyPhasedSuperWithdrawal = ({
    balA, balB, infraBalA, infraBalB, amount, allowA = true, allowB = true,
}) => {
    const accessibleBalA = allowA ? balA : 0;
    const accessibleBalB = allowB ? balB : 0;
    const accessibleInfraA = allowA ? infraBalA : 0;
    const accessibleInfraB = allowB ? infraBalB : 0;
    const liquid = accessibleBalA + accessibleBalB;
    const infra = accessibleInfraA + accessibleInfraB;
    const total = liquid + infra;
    const withdrawal = Math.min(Math.max(0, Number(amount) || 0), total);
    if (withdrawal <= 0) {
        return { balA, balB, infraBalA, infraBalB, actualWithdrawal: 0 };
    }
    if (liquid >= withdrawal) {
        const ratioA = liquid > 0 ? accessibleBalA / liquid : 0;
        return {
            balA: Math.max(0, balA - withdrawal * ratioA),
            balB: Math.max(0, balB - withdrawal * (1 - ratioA)),
            infraBalA,
            infraBalB,
            actualWithdrawal: withdrawal
        };
    }
    const fromInfra = withdrawal - liquid;
    const infraRatioA = infra > 0 ? accessibleInfraA / infra : 0;
    return {
        balA: Math.max(0, balA - accessibleBalA),
        balB: Math.max(0, balB - accessibleBalB),
        infraBalA: Math.max(0, infraBalA - fromInfra * infraRatioA),
        infraBalB: Math.max(0, infraBalB - fromInfra * (1 - infraRatioA)),
        actualWithdrawal: withdrawal
    };
};

/**
 * On-top lump sums: pro-rata from accessible liquid + infrastructure (same split as MSI withdrawals).
 */
const applyYearLumpSumWithdrawals = ({
    lumpSums,
    yearIndex,
    yourAge,
    partnerAge,
    isPhasedRetirement,
    isCouple,
    currentAge,
    partnerCurrentAge,
    yourRetirementAge,
    partnerRetirementAge,
    allowWithdrawalsBeforeRetirementAge,
    balA,
    balB,
    infraBalA,
    infraBalB,
    accounts,
    infraBal,
}) => {
    const requested = getLumpSumAmountForYear(lumpSums, { yearIndex, yourAge, partnerAge: isCouple ? partnerAge : yourAge });
    if (requested <= 0) {
        return {
            balA, balB, infraBalA, infraBalB, accounts, infraBal,
            lumpSumWithdrawal: 0,
            lumpSumShortfall: 0,
        };
    }

    if (isPhasedRetirement) {
        const canA = canAccessSuperWithRetirementAge(currentAge, yourRetirementAge, allowWithdrawalsBeforeRetirementAge);
        const canB = isCouple && canAccessSuperWithRetirementAge(partnerCurrentAge, partnerRetirementAge, allowWithdrawalsBeforeRetirementAge);
        const w = applyPhasedSuperWithdrawal({
            balA: canA ? balA : 0,
            balB: canB ? balB : 0,
            infraBalA: canA ? infraBalA : 0,
            infraBalB: canB ? infraBalB : 0,
            amount: requested,
        });
        return {
            balA: canA ? w.balA : balA,
            balB: canB ? w.balB : balB,
            infraBalA: canA ? w.infraBalA : infraBalA,
            infraBalB: canB ? w.infraBalB : infraBalB,
            accounts,
            infraBal,
            lumpSumWithdrawal: w.actualWithdrawal,
            lumpSumShortfall: requested - w.actualWithdrawal,
        };
    }

    const liquidAccessible = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
    const infraAccessible = infraBal || 0;
    const totalAccessible = liquidAccessible + infraAccessible;
    const withdrawal = Math.min(requested, totalAccessible);
    const shortfall = requested - withdrawal;
    let nextInfra = infraAccessible;
    if (withdrawal > 0) {
        const husbandProportion = liquidAccessible > 0
            ? (accounts.husband.retire + accounts.husband.accum) / liquidAccessible
            : 0.5;
        const fromLiquid = Math.min(withdrawal, liquidAccessible);
        if (fromLiquid > 0) {
            SimulationEngine.SuperannuationManager.performWithdrawal(accounts, fromLiquid, husbandProportion);
        }
        const fromInfra = withdrawal - fromLiquid;
        if (fromInfra > 0) {
            nextInfra = Math.max(0, nextInfra - fromInfra);
        }
    }
    return {
        balA, balB, infraBalA, infraBalB, accounts, infraBal: nextInfra,
        lumpSumWithdrawal: withdrawal,
        lumpSumShortfall: shortfall,
    };
};

const buildStrategyIncomeRow = (base, lumpSumWithdrawal, strategyWithdrawal, totalOtherIncome) => ({
    ...base,
    withdrawalFromSuper: Math.round(strategyWithdrawal),
    lumpSumWithdrawal: Math.round(lumpSumWithdrawal || 0),
    totalIncome: Math.round(totalOtherIncome + strategyWithdrawal + (lumpSumWithdrawal || 0)),
});

const buildBuildLumpSumContext = (inputs, lumpSums, i, yourAgeVal, partnerAgeVal) => ({
    lumpSums,
    yearIndex: i,
    yourAge: yourAgeVal,
    partnerAge: partnerAgeVal,
    isPhasedRetirement: !!inputs.phasedRetirement,
    isCouple: String(inputs.relationshipStatus) === 'couple',
    currentAge: yourAgeVal + i,
    partnerCurrentAge: partnerAgeVal + i,
    yourRetirementAge: Number(inputs.yourRetirementAge) || yourAgeVal,
    partnerRetirementAge: Number(inputs.partnerRetirementAge) || partnerAgeVal,
    allowWithdrawalsBeforeRetirementAge: !!inputs.allowWithdrawalsBeforeRetirementAge,
});

export const applyYearLumpSumContributions = ({
    inputs,
    contributions,
    yearIndex,
    yourAge,
    partnerAge,
    isCouple,
    balA,
    balB,
    accounts,
    sgA = 0,
    sgB = 0,
    capStateA,
    capStateB,
}) => {
    const rows = getLumpSumContributionsForYear(contributions, {
        yearIndex,
        yourAge,
        partnerAge,
        isCouple,
    });
    const currentAge = Number(yourAge) + yearIndex;
    const partnerCurrentAge = Number(partnerAge ?? yourAge) + yearIndex;
    const summary = summarizeYearContributions({
        scheduledContributions: rows,
        sgA,
        sgB,
        yourAge: currentAge,
        partnerAge: partnerCurrentAge,
        balA,
        balB,
        isCouple,
    });

    const applyMember = (member, balance, age, salaryInput, state) => {
        const totals = summary.byMember[member];
        const memberRetAge = Number(member === 'partner' ? inputs?.partnerRetirementAge : inputs?.yourRetirementAge);
        const salary = Number.isFinite(memberRetAge) && memberRetAge > 0 && age >= memberRetAge ? 0 : salaryInput;
        const allowed = SimulationEngine.SGManager.capAnnualContributions({
            concessional: totals.concessional,
            nonConcessional: totals.nonConcessional,
            totalSuperBalance: balance,
            age,
            state,
            workTestMet: member === 'partner'
                ? inputs?.partnerEnableWorkTest !== false
                : inputs?.enableWorkTest !== false,
            enableWorkTest: true,
        });
        const downsizerCfg = FinancialConfig.CONTRIBUTION_CAPS.DOWNSIZER || {};
        const downsizer = age >= (Number(downsizerCfg.MIN_AGE) || 55)
            ? Math.min(Math.max(0, Number(totals.downsizer) || 0), Number(downsizerCfg.MAX_CONTRIBUTION_PER_PERSON) || 300000)
            : 0;
        let credited = netOfContributionsTax(allowed.concessional, salary)
            + allowed.nonConcessional
            + downsizer;
        if (inputs?.enableLISTO !== false && inputs?.enableLISTO !== 'false') {
            const atiInput = member === 'partner'
                ? inputs?.partnerListoAdjustedTaxableIncome
                : inputs?.listoAdjustedTaxableIncome;
            const ati = atiInput != null && atiInput !== '' ? Number(atiInput) : salary;
            const listo = SimulationEngine.SGManager.calculateLISTO(ati, allowed.concessional);
            if (listo.eligible && listo.amount > 0) credited += listo.amount;
        }
        const personalNccAllowed = Math.min(
            Math.max(0, totals.nonConcessional - totals.spouse),
            allowed.nonConcessional
        );
        if (inputs?.enableCoContribution && personalNccAllowed > 0) {
            const co = SimulationEngine.SGManager.calculateCoContribution(
                salary, personalNccAllowed, age
            );
            if (co.eligible && co.amount > 0) credited += co.amount;
        }
        return { balance: balance + credited, credited, allowed, downsizer };
    };

    const appliedA = applyMember(
        'you', balA, currentAge, Number(inputs?.yourSalary) || 0,
        capStateA || SimulationEngine.SGManager.createAnnualCapState(inputs?.carryForwardBalances)
    );
    const appliedB = isCouple
        ? applyMember(
            'partner', balB, partnerCurrentAge, Number(inputs?.partnerSalary) || 0,
            capStateB || SimulationEngine.SGManager.createAnnualCapState(inputs?.partnerCarryForwardBalances)
        )
        : { balance: balB, credited: 0 };
    const nextBalA = appliedA.balance;
    const nextBalB = appliedB.balance;
    if (accounts?.husband) accounts.husband.accum += appliedA.credited;
    if (accounts?.wife) accounts.wife.accum += appliedB.credited;

    return {
        balA: nextBalA,
        balB: nextBalB,
        accounts,
        scheduledContributions: rows,
        contributionSummary: summary,
        lumpSumContribution: summary.totalScheduled,
        contributionsA: summary.byMember.you.scheduled + summary.byMember.you.sg,
        contributionsB: isCouple ? summary.byMember.partner.scheduled + summary.byMember.partner.sg : 0,
        concessionalContributionsA: summary.byMember.you.concessional,
        concessionalContributionsB: isCouple ? summary.byMember.partner.concessional : 0,
        nonConcessionalContributionsA: summary.byMember.you.nonConcessional,
        nonConcessionalContributionsB: isCouple ? summary.byMember.partner.nonConcessional : 0,
        contributionFlags: summary.flags,
    };
};

/**
 * Fees are charged per member account (flat admin, admin-% cap and investment-fee cap each
 * apply per account); the household insurance premium is split evenly, as in accumulation.
 */
const memberFee = (balance, feeInputs, isCouple) =>
    SimulationEngine.FeeManager.calculateHouseholdFees(balance, feeInputs, isCouple, isCouple ? [1, 0] : null);

/** Per-member fees on liquid super + infrastructure, split pro rata within each member. */
const chargeMemberFees = ({ balA, balB, infraBalA = 0, infraBalB = 0, isCouple, feeInputs }) => {
    const charge = (liquid, infra) => {
        const total = Math.max(0, liquid) + Math.max(0, infra);
        if (!(total > 0)) return [Math.max(0, liquid), Math.max(0, infra)];
        const keep = Math.max(0, 1 - memberFee(total, feeInputs, isCouple) / total);
        return [Math.max(0, liquid) * keep, Math.max(0, infra) * keep];
    };
    const [a, ia] = charge(balA, infraBalA);
    const [b, ib] = isCouple ? charge(balB, infraBalB) : [balB, infraBalB];
    return { balA: a, balB: b, infraBalA: ia, infraBalB: ib };
};

/** Per-member fees on retire/accum sub-accounts; household infrastructure is shared pro rata. */
const applyFeesToAccounts = (accounts, infraBal, isCouple, feeInputs) => {
    const members = isCouple ? [accounts.husband, accounts.wife] : [accounts.husband];
    const liquidOf = (acct) => Math.max(0, Number(acct.retire) || 0) + Math.max(0, Number(acct.accum) || 0);
    const liquid = members.reduce((sum, acct) => sum + liquidOf(acct), 0);
    const infra = Math.max(0, Number(infraBal) || 0);
    let infraAfter = 0;
    for (const acct of members) {
        const ownLiquid = liquidOf(acct);
        const ownInfra = liquid > 0 ? infra * (ownLiquid / liquid) : infra / members.length;
        const total = ownLiquid + ownInfra;
        const keep = total > 0 ? Math.max(0, 1 - memberFee(total, feeInputs, isCouple) / total) : 1;
        acct.retire = Math.max(0, Number(acct.retire) || 0) * keep;
        acct.accum = Math.max(0, Number(acct.accum) || 0) * keep;
        infraAfter += ownInfra * keep;
    }
    return infraAfter;
};

const emptyContributionResult = () => ({
    lumpSumContribution: 0,
    contributionsA: 0,
    contributionsB: 0,
    concessionalContributionsA: 0,
    concessionalContributionsB: 0,
    nonConcessionalContributionsA: 0,
    nonConcessionalContributionsB: 0,
    contributionFlags: [],
});

const buildAccumTaxPortfolio = (sp500Val, ausSharesVal, intlSharesVal, bondsVal, cashVal) => ({
    sp500: sp500Val,
    ausShares: ausSharesVal,
    intlShares: intlSharesVal,
    bonds: bondsVal,
    cash: cashVal,
    ...SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS,
});

const resolveYearInflation = (inflationOverride, simulationCalendarYear) => (
    Number.isFinite(Number(inflationOverride))
        ? Number(inflationOverride)
        : (Number(historicalData[simulationCalendarYear]?.inflation) || 0)
);

/** Tax is assessed on nominal earnings; returned in real (deflated) dollars. */
const realAccumulationTax = (accumBalance, r, portfolio, tsbEnd, simulationCalendarYear, inflationOverride) => {
    if (!(accumBalance > 0)) return 0;
    const inflation = resolveYearInflation(inflationOverride, simulationCalendarYear);
    const nominalEarnings = accumBalance * (((1 + r) * (1 + inflation)) - 1);
    if (!(nominalEarnings > 0)) return 0;
    const nominalTax = SimulationEngine.calculateAccumulationTax(nominalEarnings, portfolio, tsbEnd, { simulationCalendarYear });
    return nominalTax / (1 + inflation);
};

/** Phased retirement: split member balance at personal TBC; tax accumulation earnings only. */
const applyPhasedMemberReturnWithTax = (balance, r, personalTbcRemaining, portfolio, simulationCalendarYear, inflationOverride) => {
    if (balance <= 0) return 0;
    const remainTbc = Math.max(0, Number(personalTbcRemaining) || 0);
    const retire = Math.min(balance, remainTbc);
    const accum = balance - retire;
    const retireEarnings = retire * r;
    const accumEarnings = accum * r;
    const memberTsbEnd = balance + retireEarnings + accumEarnings;
    const realAccumTax = realAccumulationTax(accum, r, portfolio, memberTsbEnd, simulationCalendarYear, inflationOverride);
    return Math.max(0, retire + retireEarnings + accum + accumEarnings - realAccumTax);
};

/**
 * Single-member projection with SG (stopping at retirement age), contribution caps
 * and tax, nominal earnings tax and fees. Scheduled lump sums are left to the caller.
 */
export const projectWorkingMemberBalance = ({
    startBalance,
    startAge,
    targetAge,
    salary,
    retirementAge,
    returns,
    portfolio,
    calculateFees = () => 0,
    carryForwardBalances,
    workTestFlag,
}) => {
    let bal = Math.max(0, Number(startBalance) || 0);
    const capState = SimulationEngine.SGManager.createAnnualCapState(carryForwardBalances);
    const retAge = Number(retirementAge) || targetAge;
    const wage = Number(salary) || 0;
    for (let i = 0; i < targetAge - startAge && i < returns.length; i++) {
        const { year, r, inflation } = returns[i];
        const age = startAge + i;
        bal = applyPhasedMemberReturnWithTax(bal, r, 0, portfolio, year, inflation);
        bal = Math.max(0, bal - calculateFees(bal));
        if (age < retAge && wage > 0) {
            const allowed = SimulationEngine.SGManager.capAnnualContributions({
                concessional: calculateSGContributions(wage),
                nonConcessional: 0,
                totalSuperBalance: bal,
                age,
                state: capState,
                workTestMet: workTestFlag !== false,
                enableWorkTest: true,
            });
            bal += netOfContributionsTax(allowed.concessional, wage);
        }
    }
    return bal;
};

// A member still working has no retirement-phase (tax-free) portion yet.
const applyPhasedCoupleReturnsWithTax = ({
    balA, balB, r, year, inflation, generalTbc, yourTbcUsed, partnerTbcUsed, portfolio,
    workingA = false, workingB = false,
}) => ({
    balA: applyPhasedMemberReturnWithTax(balA, r, workingA ? 0 : generalTbc - yourTbcUsed, portfolio, year, inflation),
    balB: applyPhasedMemberReturnWithTax(balB, r, workingB ? 0 : generalTbc - partnerTbcUsed, portfolio, year, inflation),
});

// Calculate comprehensive fees (admin + investment + insurance) - EXACT DESKTOP COPY
export const calculateAdvancedFees = (balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap = null) =>
    SimulationEngine.FeeManager.calculateMemberFees(balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap);

// ============================================================================
// DESKTOP FLOOR & CEILING CALCULATION - SINGLE SOURCE OF TRUTH
// Extracted from App_advanced_min.js lines 6021-6319
// ============================================================================
export const computeFloorCeilingSeries = (inputs) => {
    const {
        otherTaxableIncome,
        otherNonTaxableIncome,
        otherAssets,
        annuityAnnualPayment,
        annuityIsIndexed = true,
        definedBenefitPensionAmount,
        definedBenefitIsIndexed = true,
        adminFees,
        adminFeePercentage,
        investmentFees,
        insuranceFees,
        investmentFeeCap,
        relationshipStatus,
        partnerSuper,
        yourSuper,
        includePension,
        isHomeowner,
        sp500,
        ausShares,
        intlShares,
        bonds,
        cash,
        phasedRetirement,
        yourRetirementAge,
        partnerRetirementAge,
        yourAge,
        partnerAge,
        yourSalary,
        partnerSalary,
        periodLength,
        startYear,
        pensionGrowthRate,
        generalTransferBalanceCap,
        yourTbcUsed,
        partnerTbcUsed,
        fcInitPct,
        fcFloorPct,
        fcCeilPct,
        targetFinalBalance,
        providedReturns = null,
        // Alternative Assets from SMSF
        residentialPropertyValue,
        residentialRentalIncome,
        residentialPropertyCapitalGrowth,
        commercialPropertyValue,
        commercialRentalIncome,
        commercialPropertyCapitalGrowth,
        collectiblesValue,
        collectiblesCapitalGrowth,
        otherAlternativesValue,
        otherAlternativesCapitalGrowth,
        // Infrastructure from SMSF
        infrastructureValue,
        infrastructurePct,
        infrastructureReturnRate,
        // Rent Assistance
        rentPaidFortnightly,
        // Super withdrawal age control
        allowWithdrawalsBeforeRetirementAge,
        // Bridge / bucket strategy
        enableBridgeStrategy,
        yourSuperAccessAge,
        partnerSuperAccessAge,
        bridgeCashBucket,
        bridgeInvestmentBucket,
        bridgeInvestmentReturnRate,
        // Mortgage payoff comparison
        enableMortgageComparison,
        mortgageBalance,
        mortgageInterestRate,
        mortgageAnnualRepayment,
        mortgageStrategy,
        mortgagePayoffScheduleType,
        mortgagePayoffScheduleValue,
        mortgagePartialPayoffAmount
    } = inputs;
    
    const lumpSums = parseLumpSumWithdrawals(inputs);
    const lumpContributions = parseLumpSumContributions(inputs);
    
    // Define all required variables within function scope
    const other = Number(otherTaxableIncome) || 0;
    const otherNonTax = Number(otherNonTaxableIncome) || 0;
    const otherAssetsValue = Number(otherAssets) || 0;
    const annuityPay = Number(annuityAnnualPayment) || 0;
    const dbPension = Number(definedBenefitPensionAmount) || 0;
    const rentPaidAnnual = (Number(rentPaidFortnightly) || 0) * 26;
    // Alternative Assets - Initialize values and growth rates
    let residentialPropValue = Number(residentialPropertyValue) || 0;
    let commercialPropValue = Number(commercialPropertyValue) || 0;
    let collectiblesVal = Number(collectiblesValue) || 0;
    let otherAlternativesVal = Number(otherAlternativesValue) || 0;
    const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
    const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
    const residentialGrowthRate = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
    const commercialGrowthRate = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
    const collectiblesGrowthRate = (Number(collectiblesCapitalGrowth) || 0) / 100;
    const otherAlternativesGrowthRate = (Number(otherAlternativesCapitalGrowth) || 0) / 100;
    
    // CRITICAL: Define partnerSuper and totalSuper within function scope
    const isCouple = relationshipStatus === 'couple';
    // Accumulation handoff: snap retired members to nest egg / start from startBalance
    const handoffSupers = resolveSupersForAccumulationHandoff(inputs);
    const yourSuperEffective = handoffSupers.yourSuper;
    // Force partner super to 0 if single relationship status
    const partnerSuperVal = isCouple ? handoffSupers.partnerSuper : 0;
    const totalSuper = yourSuperEffective + partnerSuperVal;
    
    // Super withdrawal age control - default to false (must reach retirement age)
    const allowWithdrawalsBeforeRetirement = allowWithdrawalsBeforeRetirementAge !== undefined 
        ? !!allowWithdrawalsBeforeRetirementAge 
        : false;
    
    // CRITICAL: Separate infrastructure from liquid super (same as main calculation)
    const infrastructurePctVal = Number(infrastructurePct) || 0;
    const infrastructureValueFromPct = infrastructurePctVal > 0 ? (totalSuper * (infrastructurePctVal / 100)) : 0;
    const infrastructureVal = Number(infrastructureValue) || infrastructureValueFromPct;
    const infrastructureRate = Number(infrastructureReturnRate) || 6;
    const liquidSuper = totalSuper - infrastructureVal;
    let infraBal = infrastructureVal;
    
    // Define additional required variables
    const includeAgePension = !!includePension;
    const homeowner = !!isHomeowner;
    const sp500Val = Number(sp500) || 0;
    const ausSharesVal = Number(ausShares) || 0;
    const intlSharesVal = Number(intlShares) || 0;
    const bondsVal = Number(bonds) || 0;
    const cashVal = Number(cash) || 0;
    
    const accumTaxPortfolio = buildAccumTaxPortfolio(sp500Val, ausSharesVal, intlSharesVal, bondsVal, cashVal);
    
    // Phased retirement variables
    const isPhasedRetirement = shouldUsePhasedRetirementCalculations({
        phasedRetirement,
        isCouple,
        yourAge,
        partnerAge,
        yourRetirementAge,
        partnerRetirementAge,
        yourSalary,
        partnerSalary
    });
    const phasedBals = initPhasedSuperBalances(yourSuperEffective, partnerSuperVal, infrastructureVal, isCouple, isPhasedRetirement);
    let balA = phasedBals.balA;
    let balB = phasedBals.balB;
    let infraBalA = phasedBals.infraBalA;
    let infraBalB = phasedBals.infraBalB;
    const retAgeA = isPhasedRetirement ? (Number(yourRetirementAge) || yourAge) : yourAge;
    const retAgeB = isPhasedRetirement ? (Number(partnerRetirementAge) || partnerAge) : (partnerAge || yourAge);
    const salaryA = isPhasedRetirement ? (Number(yourSalary) || 0) : 0;
    const salaryB = isPhasedRetirement ? (Number(partnerSalary) || 0) : 0;
    const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
    
    // Define loop variables
    // CRITICAL: If providedReturns is given (from what-if), use its length, otherwise use periodLength
    const years = providedReturns ? providedReturns.length : Math.max(0, Number(periodLength) || 0);
    const start = Number(startYear) || 1980;
    const yourAgeVal = Number(yourAge) || 0;
    const partnerAgeVal = Number(partnerAge) || 0;
    const pensionGrowthRateVal = Number(pensionGrowthRate) || 0;
    
    // Define TBC variables
    const generalTransferBalanceCapVal = resolveTransferBalanceCapForCalendarYear(start, generalTransferBalanceCap);
    const yourTbcUsedVal = Number(yourTbcUsed) || 0;
    const partnerTbcUsedVal = Number(partnerTbcUsed) || 0;
    
    // Fee calculation function
    const calculateFees = (balance) => {
        return calculateAdvancedFees(balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap);
    };
    const feeInputs = { adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap };
    
    // Use provided returns or calculate from historical data
    const returnsByYear = providedReturns || (() => {
        const returns = [];
    for (let i = 0; i < years; i++) {
        const year = start + i;
        const yearData = historicalData[year];
        if (!yearData) {
                // No historical data found for year - handled silently
                return [];
            }
            const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, frankingCredits: Number(inputs.frankingCredits) || 0 };
        const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
            const histInfl = (historicalData[year]?.inflation) || 0;
            // DO NOT deduct investment fees here - calculateFees will apply them (avoiding double-counting)
        const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
            returns.push({ year, r: netR });
        }
        return returns;
    })();
    
    if (!returnsByYear || returnsByYear.length === 0) return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
    
    // EXACT COPY FROM WORKING CODE - FloorAndCeiling.calculate
    // Fix: Set minimum partner super for couples to avoid NaN in calculations
    const yourSuperVal = yourSuperEffective;
    const adjustedPartnerSuper = isCouple && (partnerSuperVal === 0 || !partnerSuperVal) ? 1 : partnerSuperVal;
    let accounts = SimulationEngine.SuperannuationManager.initializeAccounts(
        yourSuperVal, adjustedPartnerSuper, isCouple, generalTransferBalanceCapVal, yourTbcUsedVal, partnerTbcUsedVal
    );
    
    const initPct = Math.max(0, Number(fcInitPct)||0)/100;
    // Apply target final balance to preserve legacy/estate goals
    const __tbIn = Number(targetFinalBalance) || 0;
    const targetBalance = __tbIn < 0 ? 0 : __tbIn;
    const withdrawableBalance = Math.max(0, totalSuper - targetBalance);
    const floorAmount = withdrawableBalance * (Math.max(0, Number(fcFloorPct)||0)/100);
    const ceilingAmount = withdrawableBalance * (Math.max(0, Number(fcCeilPct)||0)/100);
    // Calculate initial withdrawal from withdrawable balance
    const desiredInitialWithdrawal = withdrawableBalance * initPct;
    const initialMinimumRequired = totalSuper * SimulationEngine.getMinimumWithdrawalRate(yourAgeVal);
    let income = Math.max(desiredInitialWithdrawal, initialMinimumRequired);
    const incomeByYear = [];
    let totalIncomeSum = 0; // Sum of all years' total income
    let lastSuperWithdrawal = 0; // Track last year's super withdrawal for Floor/Ceiling methodology
    let accumHandoffApplied = { you: false, partner: false };
    let bridgeCashBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeCashBucket) || 0) : 0;
    let bridgeInvestmentBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeInvestmentBucket) || 0) : 0;
    const capStateA = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
    const capStateB = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
    
    const loopYears = returnsByYear.length; // Use actual returns length (supports both main and rolling)
    let _ciFc = 1.0;
    for (let i = 0; i < loopYears; i++) {
            const { year, r, inflation } = returnsByYear[i];
        const currentAge = yourAgeVal + i;
        const partnerCurrentAge = partnerAgeVal + i;
        const _yInflFc = historicalData[year]?.inflation ?? 0.025;
        const { annuityReal, dbReal, dbIncomeForHousehold } = getIndexedIncomeStreams({
            annuityPay, dbPension, annuityIsIndexed, definedBenefitIsIndexed, other,
            cumulativeInflationIndex: _ciFc
        });
            
        let currentBalance, pensionAnnual = 0, totalOtherIncome = 0, actualNeedFromSuper = 0;
        let rentalIncome = 0;
        let infrastructureDividends = 0;
        let lumpSumWithdrawal = 0;
        let bridgeWithdrawal = 0;
        let contributionApplied = emptyContributionResult();
        // Balance at the START of this year (after contributions, before this year's return) —
        // this is what the statutory minimum and initial % are actually calculated on. Displayed
        // in the table so "withdrawal ÷ balance shown" always matches the real rate applied.
        let openingBalanceForRow = 0;
        
        if (isPhasedRetirement) {
            // ===== PHASED RETIREMENT: Same logic as Fixed strategy =====
                const isARetired = currentAge >= retAgeA;
                const isBRetired = partnerCurrentAge >= retAgeB;
                const handed = applyAccumulationHandoffPath({
                    handoff: inputs.accumulationHandoff,
                    applied: accumHandoffApplied,
                    currentAge,
                    partnerCurrentAge,
                    retAgeA,
                    retAgeB,
                    balA,
                    balB,
                    isCouple,
                    yearIndex: i,
                    yourAgeStart: yourAgeVal,
                    partnerAgeStart: partnerAgeVal || yourAgeVal,
                });
                balA = handed.balA;
                balB = handed.balB;
                accumHandoffApplied = handed.applied;
                const skipGrowthA = !!handed.skipGrowthA;
                const skipGrowthB = !!handed.skipGrowthB;
                const canDrawA = !skipGrowthA && canAccessSuperWithBucketStrategy({
                    currentAge,
                    retirementAge: retAgeA,
                    accessAge: yourSuperAccessAge,
                    allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                    enableBridgeStrategy: !!enableBridgeStrategy,
                    householdRetirementTriggered: isARetired || (isCouple && isBRetired),
                });
                const canDrawB = isCouple && !skipGrowthB && canAccessSuperWithBucketStrategy({
                    currentAge: partnerCurrentAge,
                    retirementAge: retAgeB,
                    accessAge: partnerSuperAccessAge,
                    allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                    enableBridgeStrategy: !!enableBridgeStrategy,
                    householdRetirementTriggered: isARetired || (isCouple && isBRetired),
                });
                
            const empIncomeA = isARetired ? 0 : salaryA;
            const empIncomeB = isBRetired ? 0 : salaryB;
                const sgA = (isARetired || skipGrowthA) ? 0 : calculateSGContributions(salaryA, sgRate);
                const sgB = (isBRetired || skipGrowthB) ? 0 : calculateSGContributions(salaryB, sgRate);
                
            const openingLiquid = balA + balB;
            const openingInfra = infraBalA + infraBalB;
            openingBalanceForRow = openingLiquid + openingInfra;
            
            const resVal0 = Number(residentialPropertyValue) || 0;
            const comVal0 = Number(commercialPropertyValue) || 0;
            const historicalInflation = historicalData[year]?.inflation || 0.025;
            const resRealG = residentialGrowthRate - historicalInflation;
            const comRealG = commercialGrowthRate - historicalInflation;
            const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
            const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
            const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
              ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
              : 0;
            const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
              ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
              : 0;
            rentalIncome = currentResidentialRental + currentCommercialRental;
            infrastructureDividends = openingInfra * (infrastructureRate / 100);
            
            const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
            const estimatedAssetsOpening = otherAssetsValue + Math.max(0, openingLiquid) + totalAlternativeAssets + openingInfra;
            const employmentIncome = empIncomeA + empIncomeB;
            const otherIncome = computePensionAssessableOtherIncome({
                other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
            });
                
            pensionAnnual = calculateAgePensionForHousehold({
                totalAssets: estimatedAssetsOpening,
                otherIncome,
                isCouple: true,
                isHomeowner: homeowner,
                year: i,
                pensionGrowthRate: pensionGrowthRateVal,
                rentPaidAnnual,
                employmentIncome,
                ageA: currentAge,
                ageB: partnerCurrentAge,
                includePension: includeAgePension
            });
                
            totalOtherIncome = computeHouseholdOtherIncome({
                pensionAnnual,
                employmentIncome: empIncomeA + empIncomeB,
                other,
                otherNonTax,
                annuityReal,
                afterTaxDbReal: dbIncomeForHousehold,
                dbReal: 0,
                rentalIncome,
                infrastructureDividends
            });
            
            const __tbInFc = Number(targetFinalBalance) || 0;
            const targetBalanceFc = __tbInFc < 0 ? 0 : __tbInFc;
            const accessibleOpeningFc = (canDrawA ? balA : 0) + (canDrawB ? balB : 0);
            const withdrawableOpening = Math.max(0, accessibleOpeningFc - targetBalanceFc);
            const minWithdrawalAgeFc = Math.min(currentAge, partnerCurrentAge);
            const minimumRequiredFc =
                (canDrawA ? (balA + infraBalA) * SimulationEngine.getMinimumWithdrawalRate(currentAge) : 0)
                + (canDrawB ? (balB + infraBalB) * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge) : 0);
            
            if (i > 0) {
                const floorPct = Math.max(0, Number(fcFloorPct)||0)/100;
                const ceilPct = Math.max(0, Number(fcCeilPct)||0)/100;
                const floorAmount = withdrawableOpening * floorPct;
                const ceilingAmount = withdrawableOpening * ceilPct;
                const constrainedWithdrawal = Math.max(floorAmount, Math.min(ceilingAmount, lastSuperWithdrawal));
                actualNeedFromSuper = Math.max(constrainedWithdrawal, minimumRequiredFc);
            } else {
                const fcInitPctVal = Math.max(0, Number(fcInitPct)||0)/100;
                actualNeedFromSuper = Math.max(withdrawableOpening * fcInitPctVal, minimumRequiredFc);
            }
            if (enableBridgeStrategy && (isARetired || isBRetired) && actualNeedFromSuper > 0) {
                const fromCash = Math.min(actualNeedFromSuper, bridgeCashBalance);
                bridgeCashBalance -= fromCash;
                actualNeedFromSuper -= fromCash;
                const fromInvestment = Math.min(actualNeedFromSuper, bridgeInvestmentBalance);
                bridgeInvestmentBalance -= fromInvestment;
                actualNeedFromSuper -= fromInvestment;
                bridgeWithdrawal = fromCash + fromInvestment;
            }
            if (!canDrawA && !canDrawB) actualNeedFromSuper = 0;
            income = totalOtherIncome + bridgeWithdrawal + actualNeedFromSuper;
            
            if (!skipGrowthA || !skipGrowthB) {
                const grown = applyPhasedCoupleReturnsWithTax({
                    balA, balB, r, year, inflation,
                    generalTbc: generalTransferBalanceCapVal,
                    yourTbcUsed: yourTbcUsedVal,
                    partnerTbcUsed: partnerTbcUsedVal,
                    portfolio: accumTaxPortfolio,
                    workingA: !isARetired,
                    workingB: !isBRetired,
                });
                if (!skipGrowthA) balA = grown.balA;
                if (!skipGrowthB) balB = grown.balB;
            }
            residentialPropValue *= (1 + residentialGrowthRate);
            commercialPropValue *= (1 + commercialGrowthRate);
            collectiblesVal *= (1 + collectiblesGrowthRate);
            otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
            if (enableBridgeStrategy && bridgeInvestmentBalance > 0) {
                bridgeInvestmentBalance *= (1 + (Number(bridgeInvestmentReturnRate) || 0) / 100);
            }
            currentBalance = balA + balB;
            
            // Withdraw proportionally (capped at liquid + infrastructure)
            if (actualNeedFromSuper > 0) {
                const withdrawn = applyPhasedSuperWithdrawal({
                    balA, balB, infraBalA, infraBalB, amount: actualNeedFromSuper,
                    allowA: canDrawA,
                    allowB: canDrawB,
                });
                balA = withdrawn.balA;
                balB = withdrawn.balB;
                infraBalA = withdrawn.infraBalA;
                infraBalB = withdrawn.infraBalB;
                actualNeedFromSuper = withdrawn.actualWithdrawal;
            }

            const lumpCtx = buildBuildLumpSumContext(inputs, lumpSums, i, yourAgeVal, partnerAgeVal);
            const lumpApplied = applyYearLumpSumWithdrawals({
                ...lumpCtx,
                balA, balB, infraBalA, infraBalB, accounts, infraBal,
            });
            balA = lumpApplied.balA;
            balB = lumpApplied.balB;
            infraBalA = lumpApplied.infraBalA;
            infraBalB = lumpApplied.infraBalB;
            infraBal = lumpApplied.infraBal;
            lumpSumWithdrawal = lumpApplied.lumpSumWithdrawal;
                
            // Apply fees to total balance (liquid + infrastructure)
                ({ balA, balB, infraBalA, infraBalB } = chargeMemberFees({ balA, balB, infraBalA, infraBalB, isCouple, feeInputs }));

            contributionApplied = applyYearLumpSumContributions({
                inputs,
                contributions: lumpContributions,
                yearIndex: i,
                yourAge: yourAgeVal,
                partnerAge: partnerAgeVal,
                isCouple,
                balA,
                balB,
                sgA,
                sgB,
                capStateA,
                capStateB,
            });
            if (!skipGrowthA) balA = contributionApplied.balA;
            if (!skipGrowthB) balB = contributionApplied.balB;
                
            currentBalance = balA + balB;
            infraBal = infraBalA + infraBalB;
                
            } else {
            // ===== STANDARD (NON-PHASED): Original logic =====
            // Opening (1 July) balance — BEFORE this year's returns/fees. The initial %, floor
            // and ceiling, and statutory minimum are computed on this balance (matches phased FC).
            const openingLiquidFcNP = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
            const openingInfraFcNP = infraBal;
            openingBalanceForRow = openingLiquidFcNP + openingInfraFcNP;
            // Apply returns with proper tax treatment FIRST
            const husbandRetireEarnings = accounts.husband.retire * r;
            const husbandAccumEarnings = accounts.husband.accum * r;
            const wifeRetireEarnings = isCouple ? accounts.wife.retire * r : 0;
            const wifeAccumEarnings = isCouple ? accounts.wife.accum * r : 0;
            
            // Apply earnings tax to accumulation accounts only
            const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, ...SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS };
            const husbandTsbEnd = SimulationEngine.memberTsbAtYearEnd(accounts, 'husband', husbandRetireEarnings, husbandAccumEarnings);
            const husbandAccumTax = realAccumulationTax(accounts.husband.accum, r, portfolio, husbandTsbEnd, year, inflation);
            const wifeTsbEnd = isCouple ? SimulationEngine.memberTsbAtYearEnd(accounts, 'wife', wifeRetireEarnings, wifeAccumEarnings) : null;
            const wifeAccumTax = isCouple ? realAccumulationTax(accounts.wife.accum, r, portfolio, wifeTsbEnd, year, inflation) : 0;
            
            // Update balances
            accounts.husband.retire += husbandRetireEarnings;
            accounts.husband.accum += husbandAccumEarnings - husbandAccumTax;
            if (isCouple) {
                accounts.wife.retire += wifeRetireEarnings;
                accounts.wife.accum += wifeAccumEarnings - wifeAccumTax;
            }
            
            infraBal = applyFeesToAccounts(accounts, infraBal, isCouple, feeInputs);
            contributionApplied = applyYearLumpSumContributions({
                inputs,
                contributions: lumpContributions,
                yearIndex: i,
                yourAge: yourAgeVal,
                partnerAge: partnerAgeVal,
                isCouple,
                balA: (accounts.husband.retire + accounts.husband.accum),
                balB: isCouple ? (accounts.wife.retire + accounts.wife.accum) : 0,
                accounts,
                capStateA,
                capStateB,
            });
            
            // Apply capital growth to alternative assets
            residentialPropValue *= (1 + residentialGrowthRate);
            commercialPropValue *= (1 + commercialGrowthRate);
            collectiblesVal *= (1 + collectiblesGrowthRate);
            otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
            
            // NOW calculate income based on updated balance
            currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
            
            // Calculate rental income using historical inflation for the year
            const resVal0 = Number(residentialPropertyValue) || 0;
            const comVal0 = Number(commercialPropertyValue) || 0;
            const historicalInflation = historicalData[year]?.inflation || 0.025; // Fallback to 2.5% if data missing
            const resRealG = residentialGrowthRate - historicalInflation;
            const comRealG = commercialGrowthRate - historicalInflation;
            const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
            const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
            const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
              ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
              : 0;
            const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
              ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
              : 0;
            rentalIncome = currentResidentialRental + currentCommercialRental;
            
            // Calculate infrastructure dividends
            infrastructureDividends = infraBal * (infrastructureRate / 100);
            
            // Calculate minimum required withdrawals from pension accounts (mandatory compliance)
            const minHusbandWithdrawal = accounts.husband.retire * SimulationEngine.getMinimumWithdrawalRate(currentAge);
            const minWifeWithdrawal = isCouple ? accounts.wife.retire * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge || currentAge) : 0;
            const minimumRequired = minHusbandWithdrawal + minWifeWithdrawal;
            
            // Include alternative assets in total assets for age pension calculations
            const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
            const estimatedAssets = otherAssetsValue + Math.max(0, currentBalance) + totalAlternativeAssets + infraBal;
            
            const incomeForPension = computePensionAssessableOtherIncome({
                other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
            });
            pensionAnnual = calculateAgePensionForHousehold({
                totalAssets: estimatedAssets,
                otherIncome: incomeForPension,
                isCouple,
                isHomeowner: homeowner,
                year: i,
                pensionGrowthRate: pensionGrowthRateVal,
                rentPaidAnnual,
                ageA: currentAge,
                ageB: (partnerAge || yourAge) + i,
                includePension: includeAgePension
            });
            totalOtherIncome = computeHouseholdOtherIncome({
                pensionAnnual,
                other,
                otherNonTax,
                annuityReal,
                afterTaxDbReal: dbIncomeForHousehold,
                dbReal: 0,
                rentalIncome,
                infrastructureDividends
            });
            
            // FLOOR AND CEILING LOGIC
            // Methodology: Maintain last withdrawal DOLLAR amount, constrain to floor/ceiling % of withdrawable balance
            // Only withdraw from amount above target final balance to preserve legacy/estate goals
            if (i > 0) {
                // Use the actual dollar amount withdrawn from super in the previous year
                const baseWithdrawal = lastSuperWithdrawal;
                
                // Calculate withdrawable balance (preserve target final balance) — OPENING balance,
                // before this year's returns, so a strong-return year doesn't inflate the % taken.
                const __tbInA = Number(targetFinalBalance) || 0;
                const targetBalance = __tbInA < 0 ? 0 : __tbInA;
                const withdrawableBalance = Math.max(0, openingLiquidFcNP - targetBalance);
                
                // Apply floor and ceiling to withdrawable balance only
                const floorPct = Math.max(0, Number(fcFloorPct)||0)/100;
                const ceilPct = Math.max(0, Number(fcCeilPct)||0)/100;
                const floorAmount = withdrawableBalance * floorPct;
                const ceilingAmount = withdrawableBalance * ceilPct;
                
                // Constrain to floor/ceiling
                const constrainedWithdrawal = Math.max(floorAmount, Math.min(ceilingAmount, baseWithdrawal));
                
                // Apply minimum withdrawal requirements (regulatory minimums still apply to full opening balance)
                const fcMinimumWithdrawalRate = SimulationEngine.getMinimumWithdrawalRate(currentAge);
                const minimumRequired = (openingLiquidFcNP + openingInfraFcNP) * fcMinimumWithdrawalRate;
                
                actualNeedFromSuper = Math.max(constrainedWithdrawal, minimumRequired);
                income = totalOtherIncome + actualNeedFromSuper;
            } else {
                // Year 0: use initial rate on the OPENING balance
                const __tbInB = Number(targetFinalBalance) || 0;
                const targetBalance = __tbInB < 0 ? 0 : __tbInB;
                const withdrawableBalance = Math.max(0, openingLiquidFcNP - targetBalance);
                const initPct = Math.max(0, Number(fcInitPct)||0)/100;
                const fcMinimumWithdrawalRate = SimulationEngine.getMinimumWithdrawalRate(currentAge);
                const minimumRequired = (openingLiquidFcNP + openingInfraFcNP) * fcMinimumWithdrawalRate;
                actualNeedFromSuper = Math.max(withdrawableBalance * initPct, minimumRequired);
                income = totalOtherIncome + actualNeedFromSuper;
            }
            
            // Perform withdrawal
            if (actualNeedFromSuper > 0) {
                const husbandProportion = (accounts.husband.retire + accounts.husband.accum) / currentBalance;
                SimulationEngine.SuperannuationManager.performWithdrawal(accounts, actualNeedFromSuper, husbandProportion);
            }
            
            // Recalculate balance after withdrawal for accurate logging
            currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);

            const lumpAppliedFc = applyYearLumpSumWithdrawals({
                ...buildBuildLumpSumContext(inputs, lumpSums, i, yourAgeVal, partnerAgeVal),
                balA, balB, infraBalA, infraBalB, accounts, infraBal,
            });
            infraBal = lumpAppliedFc.infraBal;
            lumpSumWithdrawal = lumpAppliedFc.lumpSumWithdrawal;
            currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
        }
        
        const yearTotalIncome = totalOtherIncome + bridgeWithdrawal + actualNeedFromSuper + lumpSumWithdrawal;
        totalIncomeSum += yearTotalIncome;
        
        // Store this year's super withdrawal for next year's Floor/Ceiling calculation
        lastSuperWithdrawal = actualNeedFromSuper;
        
        incomeByYear.push(buildStrategyIncomeRow({
            year: start + i,
            yearIndex: i + 1,
            balance: Math.round(currentBalance + infraBal),
            openingBalance: Math.round(openingBalanceForRow),
            pension: Math.round(pensionAnnual),
            ...formatStrategyIncomeDisplay({
                isPhasedRetirement,
                currentAge,
                partnerCurrentAge,
                retAgeA,
                retAgeB,
                salaryA,
                salaryB,
                other,
                otherNonTax,
                annuityReal,
                dbIncomeForHousehold,
                rentalIncome,
                infrastructureDividends
            }),
            realReturn: r,
            lumpSumContribution: Math.round(contributionApplied.lumpSumContribution || 0),
            contributionsA: Math.round(contributionApplied.contributionsA || 0),
            contributionsB: Math.round(contributionApplied.contributionsB || 0),
            concessionalContributionsA: Math.round(contributionApplied.concessionalContributionsA || 0),
            concessionalContributionsB: Math.round(contributionApplied.concessionalContributionsB || 0),
            nonConcessionalContributionsA: Math.round(contributionApplied.nonConcessionalContributionsA || 0),
            nonConcessionalContributionsB: Math.round(contributionApplied.nonConcessionalContributionsB || 0),
            contributionFlags: contributionApplied.contributionFlags || [],
            bridgeWithdrawal: Math.round(bridgeWithdrawal),
        }, lumpSumWithdrawal, actualNeedFromSuper, totalOtherIncome + bridgeWithdrawal));
        _ciFc = advanceInflationIndex(_ciFc, _yInflFc);
    }
    
    // Calculate MSI as the average of all years' total income
    // INCLUDE ALL YEARS - phased retirement with contributions is part of the sustainable strategy
    const averageIncome = loopYears > 0 ? totalIncomeSum / loopYears : 0;
    // Include infrastructure in final balance
    const finalLiquidBalance = isPhasedRetirement ? (balA + balB) : SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
    const finalInfraBalance = isPhasedRetirement ? (infraBalA + infraBalB) : infraBal;
    const finalBalance = finalLiquidBalance + finalInfraBalance;
    
    return {
        avgIncome: Math.round(averageIncome),
        incomeByYear: incomeByYear,
        finalBalance: Math.round(finalBalance)
    };
};


// ============================================================================
// VANGUARD GUARDRAILS - DESKTOP SOURCE OF TRUTH
// Extracted from App_advanced_min.js computeVanguardGuardrailsSeries
// ============================================================================
export const computeVanguardGuardrailsSeries = (inputs) => {
    const {
        otherTaxableIncome, otherNonTaxableIncome, otherAssets,
        annuityAnnualPayment,
        annuityIsIndexed = true,
        definedBenefitPensionAmount,
        definedBenefitIsIndexed = true,
        adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap,
        relationshipStatus, partnerSuper, yourSuper,
        includePension, isHomeowner,
        sp500, ausShares, intlShares, bonds, cash,
        phasedRetirement, yourRetirementAge, partnerRetirementAge,
        yourAge, partnerAge, yourSalary, partnerSalary,
        periodLength, startYear, pensionGrowthRate,
        generalTransferBalanceCap, yourTbcUsed, partnerTbcUsed,
        vgInitPct, vgUpperPct, vgLowerPct,
        targetFinalBalance,
        providedReturns = null,
        // Alternative Assets from SMSF
        residentialPropertyValue,
        residentialRentalIncome,
        residentialPropertyCapitalGrowth,
        commercialPropertyValue,
        commercialRentalIncome,
        commercialPropertyCapitalGrowth,
        collectiblesValue,
        collectiblesCapitalGrowth,
        otherAlternativesValue,
        otherAlternativesCapitalGrowth,
        // Infrastructure from SMSF
        infrastructureValue,
        infrastructurePct,
        infrastructureReturnRate,
        // Rent Assistance
        rentPaidFortnightly,
        // Super withdrawal age control
        allowWithdrawalsBeforeRetirementAge,
        // Bridge / bucket strategy
        enableBridgeStrategy,
        yourSuperAccessAge,
        partnerSuperAccessAge,
        bridgeCashBucket,
        bridgeInvestmentBucket,
        bridgeInvestmentReturnRate
    } = inputs;
    
    const lumpSums = parseLumpSumWithdrawals(inputs);
    const lumpContributions = parseLumpSumContributions(inputs);
    
    // Define all required variables within function scope
    const other = Number(otherTaxableIncome) || 0;
    const otherNonTax = Number(otherNonTaxableIncome) || 0;
    const otherAssetsValue = Number(otherAssets) || 0;
    const annuityPay = Number(annuityAnnualPayment) || 0;
    const dbPension = Number(definedBenefitPensionAmount) || 0;
    const rentPaidAnnual = (Number(rentPaidFortnightly) || 0) * 26;
    // Alternative Assets - Initialize values and growth rates
    let residentialPropValue = Number(residentialPropertyValue) || 0;
    let commercialPropValue = Number(commercialPropertyValue) || 0;
    let collectiblesVal = Number(collectiblesValue) || 0;
    let otherAlternativesVal = Number(otherAlternativesValue) || 0;
    const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
    const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
    const residentialGrowthRate = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
    const commercialGrowthRate = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
    const collectiblesGrowthRate = (Number(collectiblesCapitalGrowth) || 0) / 100;
    const otherAlternativesGrowthRate = (Number(otherAlternativesCapitalGrowth) || 0) / 100;
    
    // CRITICAL: Define partnerSuper and totalSuper within function scope
    const isCouple = relationshipStatus === 'couple';
    const handoffSupersVg = resolveSupersForAccumulationHandoff(inputs);
    const yourSuperEffectiveVg = handoffSupersVg.yourSuper;
    // Force partner super to 0 if single relationship status
    const partnerSuperVal = isCouple ? handoffSupersVg.partnerSuper : 0;
    const totalSuper = yourSuperEffectiveVg + partnerSuperVal;
    
    // Super withdrawal age control - default to false (must reach retirement age)
    const allowWithdrawalsBeforeRetirement = allowWithdrawalsBeforeRetirementAge !== undefined 
        ? !!allowWithdrawalsBeforeRetirementAge 
        : false;
    
    // CRITICAL: Separate infrastructure from liquid super (same as main calculation)
    const infrastructurePctVal = Number(infrastructurePct) || 0;
    const infrastructureValueFromPct = infrastructurePctVal > 0 ? (totalSuper * (infrastructurePctVal / 100)) : 0;
    const infrastructureVal = Number(infrastructureValue) || infrastructureValueFromPct;
    const infrastructureRate = Number(infrastructureReturnRate) || 6;
    const liquidSuper = totalSuper - infrastructureVal;
    let infraBal = infrastructureVal;
    
    // Define additional required variables
    const includeAgePension = !!includePension;
    const homeowner = !!isHomeowner;
    const sp500Val = Number(sp500) || 0;
    const ausSharesVal = Number(ausShares) || 0;
    const intlSharesVal = Number(intlShares) || 0;
    const bondsVal = Number(bonds) || 0;
    const cashVal = Number(cash) || 0;
    const accumTaxPortfolio = buildAccumTaxPortfolio(sp500Val, ausSharesVal, intlSharesVal, bondsVal, cashVal);
    
    // Phased retirement variables
    const isPhasedRetirement = shouldUsePhasedRetirementCalculations({
        phasedRetirement,
        isCouple,
        yourAge,
        partnerAge,
        yourRetirementAge,
        partnerRetirementAge,
        yourSalary,
        partnerSalary
    });
    const phasedBalsVg = initPhasedSuperBalances(yourSuperEffectiveVg, partnerSuperVal, infrastructureVal, isCouple, isPhasedRetirement);
    let balA = phasedBalsVg.balA;
    let balB = phasedBalsVg.balB;
    let infraBalA = phasedBalsVg.infraBalA;
    let infraBalB = phasedBalsVg.infraBalB;
                    const retAgeA = isPhasedRetirement ? (Number(yourRetirementAge) || yourAge) : yourAge;
                    const retAgeB = isPhasedRetirement ? (Number(partnerRetirementAge) || partnerAge) : (partnerAge || yourAge);
                    const salaryA = isPhasedRetirement ? (Number(yourSalary) || 0) : 0;
                    const salaryB = isPhasedRetirement ? (Number(partnerSalary) || 0) : 0;
                    const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
                    
                    // Define loop variables
                    // CRITICAL: If providedReturns is given (from what-if), use its length, otherwise use periodLength
                    const years = providedReturns ? providedReturns.length : Math.max(0, Number(periodLength) || 0);
                    const start = Number(startYear) || 1980;
                    const yourAgeVal = Number(yourAge) || 0;
                    const partnerAgeVal = Number(partnerAge) || 0;
                    const pensionGrowthRateVal = Number(pensionGrowthRate) || 0;
                    
                    // Define TBC variables
                    const generalTransferBalanceCapVal = resolveTransferBalanceCapForCalendarYear(start, generalTransferBalanceCap);
                    const yourTbcUsedVal = Number(yourTbcUsed) || 0;
                    const partnerTbcUsedVal = Number(partnerTbcUsed) || 0;
                    
                    // Use provided returns or calculate from historical data
                    const returnsByYear = providedReturns || (() => {
                      const returns = [];
                      for (let i = 0; i < years; i++) {
                        const year = start + i;
                        const yearData = historicalData[year];
                        if (!yearData) {
                          // No historical data found for year - handled silently
                          return 0;
                        }
                        const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, frankingCredits: Number(inputs.frankingCredits) || 0 };
                        const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
                        const histInfl = (historicalData[year]?.inflation) || 0;
                        // DO NOT deduct investment fees here - calculateFees will apply them (avoiding double-counting)
                        const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
                        returns.push({ year, r: netR });
                      }
                      return returns;
                    })();
                    
                    if (!returnsByYear || returnsByYear.length === 0) return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
                    
                    // EXACT COPY FROM WORKING CODE - VanguardGuardrails.calculate
                    // Fix: Set minimum partner super for couples to avoid NaN in calculations
                    const yourSuperVal = yourSuperEffectiveVg;
                    const adjustedPartnerSuper = isCouple && (partnerSuperVal === 0 || !partnerSuperVal) ? 1 : partnerSuperVal;
                    let accounts = SimulationEngine.SuperannuationManager.initializeAccounts(
                      yourSuperVal, adjustedPartnerSuper, isCouple, generalTransferBalanceCapVal, yourTbcUsedVal, partnerTbcUsedVal
                    );
                    
                  const calculateFees = (balance) => calculateAdvancedFees(
                    balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap
                  );
                  const feeInputs = { adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap };
                    
                    const initPct = Math.max(0, Number(vgInitPct)||0)/100;
                    
                    // CRITICAL FIX: Calculate initial income with minimum withdrawal requirements
                    // Apply target final balance to preserve legacy/estate goals
                    const __tbInC = Number(targetFinalBalance) || 0;
                    const targetBalance = __tbInC < 0 ? 0 : __tbInC;
                    const withdrawableBalance = Math.max(0, totalSuper - targetBalance);
                    const initialMinimumRate = SimulationEngine.getMinimumWithdrawalRate(yourAgeVal);
                    const initialMinimumRequired = totalSuper * initialMinimumRate;
                    
                    // Calculate desired withdrawal from withdrawable balance
                    const desiredInitialWithdrawal = withdrawableBalance * initPct;
                    
                    // Use the higher of desired or regulatory minimum
                    let income = Math.max(desiredInitialWithdrawal, initialMinimumRequired);
                    
                    
    const incomeByYear = [];
                    let totalIncomeSum = 0; // Sum of all years' total income
                    let accumHandoffApplied = { you: false, partner: false };
                    let bridgeCashBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeCashBucket) || 0) : 0;
                    let bridgeInvestmentBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeInvestmentBucket) || 0) : 0;
                    const capStateA = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
                    const capStateB = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
                    
                    const loopYears = returnsByYear.length; // Use actual returns length (supports both main and rolling)
                    let _ciVg = 1.0;
                    for (let i = 0; i < loopYears; i++) {
        const { year, r, inflation } = returnsByYear[i];
                      const currentAge = yourAgeVal + i;
                      const partnerCurrentAge = partnerAgeVal + i;
                      const _yInflVg = historicalData[year]?.inflation ?? 0.025;
                      const { annuityReal, dbReal, dbIncomeForHousehold } = getIndexedIncomeStreams({
                          annuityPay, dbPension, annuityIsIndexed, definedBenefitIsIndexed, other,
                          cumulativeInflationIndex: _ciVg
                      });
        
                      let currentBalance, pensionAnnual = 0, totalOtherIncome = 0, actualNeedFromSuper = 0;
                      let rentalIncome = 0;
                      let infrastructureDividends = 0;
                      let lumpSumWithdrawal = 0;
                      let bridgeWithdrawal = 0;
                      let contributionApplied = emptyContributionResult();
                      // Balance at the START of this year (after contributions, before this year's return) —
                      // what the statutory minimum / init % are actually calculated on. Shown in the table.
                      let openingBalanceForRow = 0;
        
                      if (isPhasedRetirement) {
                        // ===== PHASED RETIREMENT: Same logic as Fixed strategy =====
            const isARetired = currentAge >= retAgeA;
            const isBRetired = partnerCurrentAge >= retAgeB;
            const handed = applyAccumulationHandoffPath({
                handoff: inputs.accumulationHandoff,
                applied: accumHandoffApplied,
                currentAge,
                partnerCurrentAge,
                retAgeA,
                retAgeB,
                balA,
                balB,
                isCouple,
                yearIndex: i,
                yourAgeStart: yourAgeVal,
                partnerAgeStart: partnerAgeVal || yourAgeVal,
            });
            balA = handed.balA;
            balB = handed.balB;
            accumHandoffApplied = handed.applied;
            const skipGrowthA = !!handed.skipGrowthA;
            const skipGrowthB = !!handed.skipGrowthB;
            const canDrawA = !skipGrowthA && canAccessSuperWithBucketStrategy({
                currentAge, retirementAge: retAgeA, accessAge: yourSuperAccessAge,
                allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                enableBridgeStrategy: !!enableBridgeStrategy,
                householdRetirementTriggered: isARetired || (isCouple && isBRetired),
            });
            const canDrawB = isCouple && !skipGrowthB && canAccessSuperWithBucketStrategy({
                currentAge: partnerCurrentAge, retirementAge: retAgeB, accessAge: partnerSuperAccessAge,
                allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                enableBridgeStrategy: !!enableBridgeStrategy,
                householdRetirementTriggered: isARetired || (isCouple && isBRetired),
            });
            
                        const empIncomeA = isARetired ? 0 : salaryA;
                        const empIncomeB = isBRetired ? 0 : salaryB;
            const sgA = (isARetired || skipGrowthA) ? 0 : calculateSGContributions(salaryA, sgRate);
            const sgB = (isBRetired || skipGrowthB) ? 0 : calculateSGContributions(salaryB, sgRate);
            
            const openingLiquid = balA + balB;
            const openingInfra = infraBalA + infraBalB;
            openingBalanceForRow = openingLiquid + openingInfra;
            
            const resVal0 = Number(residentialPropertyValue) || 0;
            const comVal0 = Number(commercialPropertyValue) || 0;
            const historicalInflation = historicalData[year]?.inflation || 0.025;
            const resRealG = residentialGrowthRate - historicalInflation;
            const comRealG = commercialGrowthRate - historicalInflation;
            const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
            const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
            const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
              ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
              : 0;
            const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
              ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
              : 0;
            rentalIncome = currentResidentialRental + currentCommercialRental;
            infrastructureDividends = openingInfra * (infrastructureRate / 100);
                        
            const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
            const estimatedAssetsOpening = otherAssetsValue + Math.max(0, openingLiquid) + totalAlternativeAssets + openingInfra;
            const employmentIncome = empIncomeA + empIncomeB;
            const otherIncome = computePensionAssessableOtherIncome({
                other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
            });
            
            pensionAnnual = calculateAgePensionForHousehold({
                totalAssets: estimatedAssetsOpening,
                otherIncome,
                isCouple: true,
                isHomeowner: homeowner,
                year: i,
                pensionGrowthRate: pensionGrowthRateVal,
                employmentIncome,
                ageA: currentAge,
                ageB: partnerCurrentAge,
                includePension: includeAgePension
            });
            
            totalOtherIncome = computeHouseholdOtherIncome({
                pensionAnnual,
                employmentIncome,
                other,
                otherNonTax,
                annuityReal,
                afterTaxDbReal: dbIncomeForHousehold,
                dbReal: 0,
                rentalIncome,
                infrastructureDividends
            });
                        
            const __tbInVg = Number(targetFinalBalance) || 0;
            const targetBalanceVg = __tbInVg < 0 ? 0 : __tbInVg;
            const accessibleOpeningVg = (canDrawA ? balA : 0) + (canDrawB ? balB : 0);
            const withdrawableOpening = Math.max(0, accessibleOpeningVg - targetBalanceVg);
            const minWithdrawalAgeVg = Math.min(currentAge, partnerCurrentAge);
            const minimumRequiredVg =
                (canDrawA ? (balA + infraBalA) * SimulationEngine.getMinimumWithdrawalRate(currentAge) : 0)
                + (canDrawB ? (balB + infraBalB) * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge) : 0);
                        
            if (i > 0) {
                          const lastWithdrawal = income;
                          const currentRate = withdrawableOpening > 0 ? (lastWithdrawal / withdrawableOpening) : 0;
                          const upperPct = (Number(vgUpperPct) || 20) / 100;
                          const lowerPct = (Number(vgLowerPct) || 20) / 100;
                          const upperGuardrail = initPct * (1 + upperPct);
                          const lowerGuardrail = initPct * (1 - lowerPct);
                          let newRate = currentRate;
                          if (currentRate > upperGuardrail) {
                            newRate = upperGuardrail;
                          } else if (currentRate < lowerGuardrail) {
                            newRate = lowerGuardrail;
                          }
                          income = Math.max(withdrawableOpening * newRate, minimumRequiredVg);
            } else {
                          income = Math.max(withdrawableOpening * initPct, minimumRequiredVg);
            }
                        
            const desiredSuperWithdrawal = Math.max(0, income - totalOtherIncome);
            actualNeedFromSuper = Math.max(desiredSuperWithdrawal, minimumRequiredVg);
            if (enableBridgeStrategy && (isARetired || isBRetired) && actualNeedFromSuper > 0) {
                const fromCash = Math.min(actualNeedFromSuper, bridgeCashBalance);
                bridgeCashBalance -= fromCash;
                actualNeedFromSuper -= fromCash;
                const fromInvestment = Math.min(actualNeedFromSuper, bridgeInvestmentBalance);
                bridgeInvestmentBalance -= fromInvestment;
                actualNeedFromSuper -= fromInvestment;
                bridgeWithdrawal = fromCash + fromInvestment;
            }
            if (!canDrawA && !canDrawB) actualNeedFromSuper = 0;
            
            if (!skipGrowthA || !skipGrowthB) {
                const grown = applyPhasedCoupleReturnsWithTax({
                    balA, balB, r, year, inflation,
                    generalTbc: generalTransferBalanceCapVal,
                    yourTbcUsed: yourTbcUsedVal,
                    partnerTbcUsed: partnerTbcUsedVal,
                    portfolio: accumTaxPortfolio,
                    workingA: !isARetired,
                    workingB: !isBRetired,
                });
                if (!skipGrowthA) balA = grown.balA;
                if (!skipGrowthB) balB = grown.balB;
            }
            residentialPropValue *= (1 + residentialGrowthRate);
            commercialPropValue *= (1 + commercialGrowthRate);
            collectiblesVal *= (1 + collectiblesGrowthRate);
            otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
            if (enableBridgeStrategy && bridgeInvestmentBalance > 0) {
                bridgeInvestmentBalance *= (1 + (Number(bridgeInvestmentReturnRate) || 0) / 100);
            }
            currentBalance = balA + balB;
                        
                        // Withdraw proportionally (capped at liquid + infrastructure)
                        if (actualNeedFromSuper > 0) {
                            const withdrawn = applyPhasedSuperWithdrawal({
                                balA, balB, infraBalA, infraBalB, amount: actualNeedFromSuper,
                                allowA: canDrawA,
                                allowB: canDrawB,
                            });
                            balA = withdrawn.balA;
                            balB = withdrawn.balB;
                            infraBalA = withdrawn.infraBalA;
                            infraBalB = withdrawn.infraBalB;
                            actualNeedFromSuper = withdrawn.actualWithdrawal;
                        }

            const lumpAppliedVg = applyYearLumpSumWithdrawals({
                ...buildBuildLumpSumContext(inputs, lumpSums, i, yourAgeVal, partnerAgeVal),
                balA, balB, infraBalA, infraBalB, accounts, infraBal,
            });
            balA = lumpAppliedVg.balA;
            balB = lumpAppliedVg.balB;
            infraBalA = lumpAppliedVg.infraBalA;
            infraBalB = lumpAppliedVg.infraBalB;
            infraBal = lumpAppliedVg.infraBal;
            lumpSumWithdrawal = lumpAppliedVg.lumpSumWithdrawal;
            
                        // Apply fees to total balance (liquid + infrastructure)
            ({ balA, balB, infraBalA, infraBalB } = chargeMemberFees({ balA, balB, infraBalA, infraBalB, isCouple, feeInputs }));

            contributionApplied = applyYearLumpSumContributions({
                inputs,
                contributions: lumpContributions,
                yearIndex: i,
                yourAge: yourAgeVal,
                partnerAge: partnerAgeVal,
                isCouple,
                balA,
                balB,
                sgA,
                sgB,
                capStateA,
                capStateB,
            });
            if (!skipGrowthA) balA = contributionApplied.balA;
            if (!skipGrowthB) balB = contributionApplied.balB;
            
            currentBalance = balA + balB;
            infraBal = infraBalA + infraBalB;
                        
        } else {
                        // ===== STANDARD (NON-PHASED): Original logic =====
                        // Opening (1 July) balance — BEFORE this year's returns/fees.
                        // Guardrail %, current-rate check, and statutory minimum all use this, not the
                        // post-return balance (a strong-return year must not inflate the % taken).
                        const openingLiquidVgNP = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                        const openingInfraVgNP = infraBal;
                        openingBalanceForRow = openingLiquidVgNP + openingInfraVgNP;
                        // Apply returns with proper tax treatment FIRST
                        const husbandRetireEarnings = accounts.husband.retire * r;
                        const husbandAccumEarnings = accounts.husband.accum * r;
                        const wifeRetireEarnings = isCouple ? accounts.wife.retire * r : 0;
                        const wifeAccumEarnings = isCouple ? accounts.wife.accum * r : 0;
                        
                        // Apply earnings tax to accumulation accounts only
                        const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, ...SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS };
                        const husbandTsbEnd = SimulationEngine.memberTsbAtYearEnd(accounts, 'husband', husbandRetireEarnings, husbandAccumEarnings);
                        const husbandAccumTax = realAccumulationTax(accounts.husband.accum, r, portfolio, husbandTsbEnd, year, inflation);
                        const wifeTsbEnd = isCouple ? SimulationEngine.memberTsbAtYearEnd(accounts, 'wife', wifeRetireEarnings, wifeAccumEarnings) : null;
                        const wifeAccumTax = isCouple ? realAccumulationTax(accounts.wife.accum, r, portfolio, wifeTsbEnd, year, inflation) : 0;
                        
                        // Update balances
                        accounts.husband.retire += husbandRetireEarnings;
                        accounts.husband.accum += husbandAccumEarnings - husbandAccumTax;
                        if (isCouple) {
                          accounts.wife.retire += wifeRetireEarnings;
                          accounts.wife.accum += wifeAccumEarnings - wifeAccumTax;
                        }
                        
                        infraBal = applyFeesToAccounts(accounts, infraBal, isCouple, feeInputs);
                        contributionApplied = applyYearLumpSumContributions({
                            inputs,
                            contributions: lumpContributions,
                            yearIndex: i,
                            yourAge: yourAgeVal,
                            partnerAge: partnerAgeVal,
                            isCouple,
                            balA: (accounts.husband.retire + accounts.husband.accum),
                            balB: isCouple ? (accounts.wife.retire + accounts.wife.accum) : 0,
                            accounts,
                            capStateA,
                            capStateB,
                        });
                        
                        // NOW calculate income based on updated balance
                        currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                        
                        // Calculate minimum required withdrawals from OPENING super balance (mandatory compliance)
                        const minimumWithdrawalRate = SimulationEngine.getMinimumWithdrawalRate(currentAge);
                        const minimumRequired = (openingLiquidVgNP + openingInfraVgNP) * minimumWithdrawalRate;
                        
                        // VANGUARD GUARDRAILS: Actual Vanguard methodology implementation
                        // Methodology: Maintain last withdrawal unless current rate breaches guardrails
                        // Only withdraw from amount above target final balance to preserve legacy/estate goals
                        if (i > 0) {
                          // Calculate withdrawable balance (preserve target final balance) — OPENING balance
                          const __tbInE = Number(targetFinalBalance) || 0;
                          const targetBalance = __tbInE < 10 ? 10 : __tbInE;
                          const withdrawableBalance = Math.max(0, openingLiquidVgNP - targetBalance);
                          
                          // Calculate current withdrawal rate based on last withdrawal and withdrawable balance
                          const currentRate = withdrawableBalance > 0 ? (income / withdrawableBalance) : 0;
                          const baseRate = initPct;
                          
                          // User-defined guardrails as percentage deviations from base rate
                          const upperPct = (Number(vgUpperPct) || 20) / 100;
                          const lowerPct = (Number(vgLowerPct) || 20) / 100;
                          const upperGuardrail = baseRate * (1 + upperPct);
                          const lowerGuardrail = baseRate * (1 - lowerPct);
                          
                          let newRate = currentRate;
                          
                          // If rate exceeds upper guardrail, reduce to upper guardrail
                          if (currentRate > upperGuardrail) {
                            newRate = upperGuardrail;
                          }
                          // If rate falls below lower guardrail, increase to lower guardrail
                          else if (currentRate < lowerGuardrail) {
                            newRate = lowerGuardrail;
                          }
                          // Otherwise, maintain last withdrawal amount (which gives us currentRate)
                          
                          // Apply minimum withdrawal requirements (regulatory minimums still apply to full opening balance)
                          const finalWithdrawalRate = Math.max(newRate, minimumWithdrawalRate);
                          const desiredWithdrawal = withdrawableBalance * finalWithdrawalRate;
                          income = Math.max(desiredWithdrawal, minimumRequired);
                        }
                        
            // Apply capital growth to alternative assets
            residentialPropValue *= (1 + residentialGrowthRate);
            commercialPropValue *= (1 + commercialGrowthRate);
            collectiblesVal *= (1 + collectiblesGrowthRate);
            otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
            
            // Calculate rental income using historical inflation for the year
            const resVal0 = Number(residentialPropertyValue) || 0;
            const comVal0 = Number(commercialPropertyValue) || 0;
            const historicalInflation = historicalData[year]?.inflation || 0.025; // Fallback to 2.5% if data missing
            const resRealG = residentialGrowthRate - historicalInflation;
            const comRealG = commercialGrowthRate - historicalInflation;
            const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
            const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
            const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
              ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
              : 0;
            const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
              ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
              : 0;
            rentalIncome = currentResidentialRental + currentCommercialRental;
            
            // Calculate infrastructure dividends
            infrastructureDividends = infraBal * (infrastructureRate / 100);
                        
                        // Calculate pension based on current balance (before withdrawal) - include infrastructure
            const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
            const estimatedAssets = otherAssetsValue + Math.max(0, currentBalance) + totalAlternativeAssets + infraBal;
            const incomeForPension = computePensionAssessableOtherIncome({
                other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
            });
            pensionAnnual = calculateAgePensionForHousehold({
                totalAssets: estimatedAssets,
                otherIncome: incomeForPension,
                isCouple,
                isHomeowner: homeowner,
                year: i,
                pensionGrowthRate: pensionGrowthRateVal,
                rentPaidAnnual,
                ageA: currentAge,
                ageB: (partnerAge || yourAge) + i,
                includePension: includeAgePension
            });
                        totalOtherIncome = computeHouseholdOtherIncome({
                            pensionAnnual,
                            other,
                            otherNonTax,
                            annuityReal,
                            afterTaxDbReal: dbIncomeForHousehold,
                            dbReal: 0,
                            rentalIncome,
                            infrastructureDividends
                        });
                        
                        // Calculate minimum required withdrawal from OPENING super balance (mandatory compliance)
                        const vgMinimumWithdrawalRate = SimulationEngine.getMinimumWithdrawalRate(currentAge);
                        const minimumSuperWithdrawal = (openingLiquidVgNP + openingInfraVgNP) * vgMinimumWithdrawalRate;
                        
                        // Calculate desired withdrawal from super (based on strategy)
                        const desiredSuperWithdrawal = Math.max(0, income - totalOtherIncome);
                        
                        // Use the HIGHER of minimum required or desired withdrawal
                        const finalWithdrawal = Math.max(minimumSuperWithdrawal, desiredSuperWithdrawal);
                        actualNeedFromSuper = finalWithdrawal;
                        
                        // Perform withdrawal
                        if (finalWithdrawal > 0) {
                          const husbandProportion = (accounts.husband.retire + accounts.husband.accum) / currentBalance;
                          SimulationEngine.SuperannuationManager.performWithdrawal(accounts, finalWithdrawal, husbandProportion);
                        }
                        
                        // Recalculate balance after withdrawal for accurate logging
                        currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);

                        const lumpAppliedVgStd = applyYearLumpSumWithdrawals({
                            ...buildBuildLumpSumContext(inputs, lumpSums, i, yourAgeVal, partnerAgeVal),
                            balA, balB, infraBalA, infraBalB, accounts, infraBal,
                        });
                        infraBal = lumpAppliedVgStd.infraBal;
                        lumpSumWithdrawal = lumpAppliedVgStd.lumpSumWithdrawal;
                        currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                      }
                      
                      const yearTotalIncome = totalOtherIncome + bridgeWithdrawal + actualNeedFromSuper + lumpSumWithdrawal;
                      totalIncomeSum += yearTotalIncome;
                      
                      if (i === 0) {
        }
        
        incomeByYear.push(buildStrategyIncomeRow({
            year: start + i,
            yearIndex: i + 1,
            balance: Math.round(currentBalance + infraBal),
            openingBalance: Math.round(openingBalanceForRow),
            pension: Math.round(pensionAnnual),
                        ...formatStrategyIncomeDisplay({
                            isPhasedRetirement,
                            currentAge,
                            partnerCurrentAge,
                            retAgeA,
                            retAgeB,
                            salaryA,
                            salaryB,
                            other,
                            otherNonTax,
                            annuityReal,
                            dbIncomeForHousehold,
                            rentalIncome,
                            infrastructureDividends
                        }),
            realReturn: r,
            lumpSumContribution: Math.round(contributionApplied.lumpSumContribution || 0),
            contributionsA: Math.round(contributionApplied.contributionsA || 0),
            contributionsB: Math.round(contributionApplied.contributionsB || 0),
            concessionalContributionsA: Math.round(contributionApplied.concessionalContributionsA || 0),
            concessionalContributionsB: Math.round(contributionApplied.concessionalContributionsB || 0),
            nonConcessionalContributionsA: Math.round(contributionApplied.nonConcessionalContributionsA || 0),
            nonConcessionalContributionsB: Math.round(contributionApplied.nonConcessionalContributionsB || 0),
            contributionFlags: contributionApplied.contributionFlags || [],
            bridgeWithdrawal: Math.round(bridgeWithdrawal),
        }, lumpSumWithdrawal, actualNeedFromSuper, totalOtherIncome + bridgeWithdrawal));
                      _ciVg = advanceInflationIndex(_ciVg, _yInflVg);
                    }
                    
                    // Calculate MSI as the average of all years' total income
                    // For phased retirement: exclude working years if working income > retirement income
                    let effectiveYears = loopYears;
                    let effectiveTotalIncome = totalIncomeSum;
                    
                    if (isPhasedRetirement && incomeByYear.length > 0) {
                      // Find the year when BOTH partners are retired
                      const firstFullRetirementYear = incomeByYear.findIndex(row => {
                        const yearAge = yourAgeVal + row.yearIndex - 1;
                        const partnerYearAge = partnerAgeVal + row.yearIndex - 1;
                        return yearAge >= retAgeA && partnerYearAge >= retAgeB;
                      });
                      
                      if (firstFullRetirementYear > 0) {
                        // Calculate average with and without working years
                        const allYearsAvg = totalIncomeSum / loopYears;
                        const retirementYears = loopYears - firstFullRetirementYear;
                        const retirementIncome = incomeByYear.slice(firstFullRetirementYear)
                          .reduce((sum, row) => sum + (row.totalIncome || 0), 0);
                        const retirementAvg = retirementYears > 0 ? retirementIncome / retirementYears : 0;
                        
                        // Exclude working years if they inflate the average (working income > retirement income)
                        if (retirementAvg < allYearsAvg) {
                          effectiveYears = retirementYears;
                          effectiveTotalIncome = retirementIncome;
                        }
                      }
                    }
                    
                    const averageIncome = effectiveYears > 0 ? effectiveTotalIncome / effectiveYears : 0;
                    
                    // CRITICAL: Return object with avgIncome, incomeByYear, and finalBalance (not just a number)
                    // Mobile calculator expects this format
                    // Include infrastructure in final balance
                    const finalLiquidBalance = isPhasedRetirement ? (balA + balB) : SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                    const finalInfraBalance = isPhasedRetirement ? (infraBalA + infraBalB) : infraBal;
                    const finalBalance = finalLiquidBalance + finalInfraBalance;
                    
                    return {
                        avgIncome: Math.round(averageIncome),
                        incomeByYear: incomeByYear,
                        finalBalance: Math.round(finalBalance)
                    };
};


// ============================================================================
// DYNAMIC SWR - DESKTOP SOURCE OF TRUTH
// Extracted from App_advanced_min.js computeDynamicSWRSeries
// ============================================================================
export const computeDynamicSWRSeries = (inputs) => {
    const {
        otherTaxableIncome, otherNonTaxableIncome, otherAssets,
        annuityAnnualPayment,
        annuityIsIndexed = true,
        definedBenefitPensionAmount,
        definedBenefitIsIndexed = true,
        adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap,
        relationshipStatus, partnerSuper, yourSuper,
        includePension, isHomeowner,
        sp500, ausShares, intlShares, bonds, cash,
        phasedRetirement, yourRetirementAge, partnerRetirementAge,
        yourAge, partnerAge, yourSalary, partnerSalary,
        periodLength, startYear, pensionGrowthRate,
        generalTransferBalanceCap, yourTbcUsed, partnerTbcUsed,
        dynInitPct,
        targetFinalBalance,
        providedReturns = null,
        // Alternative Assets from SMSF
        residentialPropertyValue,
        residentialRentalIncome,
        residentialPropertyCapitalGrowth,
        commercialPropertyValue,
        commercialRentalIncome,
        commercialPropertyCapitalGrowth,
        collectiblesValue,
        collectiblesCapitalGrowth,
        otherAlternativesValue,
        otherAlternativesCapitalGrowth,
        // Infrastructure from SMSF
        infrastructureValue,
        infrastructurePct,
        infrastructureReturnRate,
        // Rent Assistance
        rentPaidFortnightly,
        // Super withdrawal age control
        allowWithdrawalsBeforeRetirementAge,
        // Bridge / bucket strategy
        enableBridgeStrategy,
        yourSuperAccessAge,
        partnerSuperAccessAge,
        bridgeCashBucket,
        bridgeInvestmentBucket,
        bridgeInvestmentReturnRate
    } = inputs;
    
    const lumpSums = parseLumpSumWithdrawals(inputs);
    const lumpContributions = parseLumpSumContributions(inputs);
    
// Define all required variables within function scope
                    const other = Number(otherTaxableIncome) || 0;
                    const otherNonTax = Number(otherNonTaxableIncome) || 0;
                    const otherAssetsValue = Number(otherAssets) || 0;
                    const annuityPay = Number(annuityAnnualPayment) || 0;
                    const dbPension = Number(definedBenefitPensionAmount) || 0;
                    const rentPaidAnnual = (Number(rentPaidFortnightly) || 0) * 26;
                    
                    // Super withdrawal age control - default to false (must reach retirement age)
                    const allowWithdrawalsBeforeRetirement = allowWithdrawalsBeforeRetirementAge !== undefined 
                        ? !!allowWithdrawalsBeforeRetirementAge 
                        : false;
                    
                    // Alternative Assets - Initialize values and growth rates
                    let residentialPropValue = Number(residentialPropertyValue) || 0;
                    let commercialPropValue = Number(commercialPropertyValue) || 0;
                    let collectiblesVal = Number(collectiblesValue) || 0;
                    let otherAlternativesVal = Number(otherAlternativesValue) || 0;
                    const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
                    const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
                    const residentialGrowthRate = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
                    const commercialGrowthRate = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
                    const collectiblesGrowthRate = (Number(collectiblesCapitalGrowth) || 0) / 100;
                    const otherAlternativesGrowthRate = (Number(otherAlternativesCapitalGrowth) || 0) / 100;
                    
                    // CRITICAL: Define partnerSuper and totalSuper within function scope
                    const isCouple = relationshipStatus === 'couple';
                    const handoffSupersDyn = resolveSupersForAccumulationHandoff(inputs);
                    const yourSuperEffectiveDyn = handoffSupersDyn.yourSuper;
                    // Force partner super to 0 if single relationship status
                    const partnerSuperVal = isCouple ? handoffSupersDyn.partnerSuper : 0;
                    const totalSuper = yourSuperEffectiveDyn + partnerSuperVal;
                    
                    // CRITICAL: Separate infrastructure from liquid super (same as main calculation)
                    // Accept infrastructureValue directly, or calculate from infrastructurePct if provided
                    const infrastructurePctVal = Number(infrastructurePct) || 0;
                    const infrastructureValueFromPct = infrastructurePctVal > 0 ? (totalSuper * (infrastructurePctVal / 100)) : 0;
                    const infrastructureVal = Number(infrastructureValue) || infrastructureValueFromPct;
                    const infrastructureRate = Number(infrastructureReturnRate) || 6;
                    const liquidSuper = totalSuper - infrastructureVal;
                    let infraBal = infrastructureVal;
                    
                    // Define additional required variables
                    const includeAgePension = !!includePension;
                    const homeowner = !!isHomeowner;
                    const sp500Val = Number(sp500) || 0;
                    const ausSharesVal = Number(ausShares) || 0;
                    const intlSharesVal = Number(intlShares) || 0;
                    const bondsVal = Number(bonds) || 0;
                    const cashVal = Number(cash) || 0;
                    const accumTaxPortfolio = buildAccumTaxPortfolio(sp500Val, ausSharesVal, intlSharesVal, bondsVal, cashVal);
                    
                    // Phased retirement variables
                    const isPhasedRetirement = shouldUsePhasedRetirementCalculations({
                        phasedRetirement,
                        isCouple,
                        yourAge,
                        partnerAge,
                        yourRetirementAge,
                        partnerRetirementAge,
                        yourSalary,
                        partnerSalary
                    });
                    const phasedBalsDyn = initPhasedSuperBalances(yourSuperEffectiveDyn, partnerSuperVal, infrastructureVal, isCouple, isPhasedRetirement);
                    let balA = phasedBalsDyn.balA;
                    let balB = phasedBalsDyn.balB;
                    let infraBalA = phasedBalsDyn.infraBalA;
                    let infraBalB = phasedBalsDyn.infraBalB;
                    const retAgeA = isPhasedRetirement ? (Number(yourRetirementAge) || yourAge) : yourAge;
                    const retAgeB = isPhasedRetirement ? (Number(partnerRetirementAge) || partnerAge) : (partnerAge || yourAge);
                    const salaryA = isPhasedRetirement ? (Number(yourSalary) || 0) : 0;
                    const salaryB = isPhasedRetirement ? (Number(partnerSalary) || 0) : 0;
                    const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
                    
                    // Define loop variables
                    // CRITICAL: If providedReturns is given (from what-if), use its length, otherwise use periodLength
                    const years = providedReturns ? providedReturns.length : Math.max(0, Number(periodLength) || 0);
                    const start = Number(startYear) || 1980;
                    const yourAgeVal = Number(yourAge) || 0;
                    const partnerAgeVal = Number(partnerAge) || 0;
                    const pensionGrowthRateVal = Number(pensionGrowthRate) || 0;
                    
                    // Define TBC variables
                    const generalTransferBalanceCapVal = resolveTransferBalanceCapForCalendarYear(start, generalTransferBalanceCap);
                    const yourTbcUsedVal = Number(yourTbcUsed) || 0;
                    const partnerTbcUsedVal = Number(partnerTbcUsed) || 0;
                    
                    // Use provided returns or calculate from historical data
                    let returnsByYear;
                    if (providedReturns) {
                      returnsByYear = providedReturns;
                    } else {
                      // Calculate returns by year from historical data
                      returnsByYear = [];
    for (let i = 0; i < years; i++) {
        const year = start + i;
        const yearData = historicalData[year];
                      if (!yearData) {
                        // No historical data found for year - handled silently
                        return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
                      }
                      const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, frankingCredits: Number(inputs.frankingCredits) || 0 };
        const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
                      const histInfl = (historicalData[year]?.inflation) || 0;
                      // DO NOT deduct investment fees here - calculateFees will apply them (avoiding double-counting)
        const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
        returnsByYear.push({ year, r: netR });
                      }
                    }
                    
                    // Initialize accounts with TBC separation (Accumulation handoff nest egg when armed)
                    const yourSuperVal = yourSuperEffectiveDyn;
                    const adjustedPartnerSuper = isCouple && (partnerSuperVal === 0 || !partnerSuperVal) ? 1 : partnerSuperVal;
                    let accounts = SimulationEngine.SuperannuationManager.initializeAccounts(
                      yourSuperVal, adjustedPartnerSuper, isCouple, generalTransferBalanceCapVal, yourTbcUsedVal, partnerTbcUsedVal
                    );
                    
    const calculateFees = (balance) => calculateAdvancedFees(
      balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap
    );
    const feeInputs = { adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap };
                    
                    
                    // Force explicit conversion of dynInitPct from React state (this is expected return assumption, not withdrawal rate)
                    const userDynRate = parseFloat(String(dynInitPct)) || 4;
                    const initPct = userDynRate / 100;
                    
                    // Reset incomeByYear for this strategy's data
                    const incomeByYear = [];
                    
                    // Initial income is just for reference - actual withdrawal is calculated in loop with minimum enforced
                    let income = totalSuper * initPct;
                    let totalIncomeSum = 0; // Sum of all years' total income
                    let accumHandoffApplied = { you: false, partner: false };
                    let bridgeCashBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeCashBucket) || 0) : 0;
                    let bridgeInvestmentBalance = enableBridgeStrategy ? Math.max(0, Number(bridgeInvestmentBucket) || 0) : 0;
                    const capStateA = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
                    const capStateB = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
                    
                    // Use returnsByYear.length to support both main calculation and rolling periods
                    const loopYears = returnsByYear.length;
                    let _ciDyn = 1.0;
                    for (let i = 0; i < loopYears; i++) {
                      const { year, r, inflation } = returnsByYear[i];
                      const currentAge = yourAgeVal + i;
                      const partnerCurrentAge = partnerAgeVal + i;
                      const _yInflDyn = historicalData[year]?.inflation ?? 0.025;
                      const { annuityReal, dbReal, dbIncomeForHousehold } = getIndexedIncomeStreams({
                          annuityPay, dbPension, annuityIsIndexed, definedBenefitIsIndexed, other,
                          cumulativeInflationIndex: _ciDyn
                      });
                      
                      let currentBalance, pensionAnnual = 0, totalOtherIncome = 0, actualNeedFromSuper = 0;
                      let rentalIncome = 0;
                      let infrastructureDividends = 0;
                      let lumpSumWithdrawal = 0;
                      let bridgeWithdrawal = 0;
                      let contributionApplied = emptyContributionResult();
                      // Balance at the START of this year (after contributions, before this year's return) —
                      // what the statutory minimum / init % are actually calculated on. Shown in the table.
                      let openingBalanceForRow = 0;
                      
                      if (isPhasedRetirement) {
                        // ===== PHASED RETIREMENT: Same logic as Fixed strategy =====
                    const isARetired = currentAge >= retAgeA;
                    const isBRetired = partnerCurrentAge >= retAgeB;
                    const handed = applyAccumulationHandoffPath({
                        handoff: inputs.accumulationHandoff,
                        applied: accumHandoffApplied,
                        currentAge,
                        partnerCurrentAge,
                        retAgeA,
                        retAgeB,
                        balA,
                        balB,
                        isCouple,
                        yearIndex: i,
                        yourAgeStart: yourAgeVal,
                        partnerAgeStart: partnerAgeVal || yourAgeVal,
                    });
                    balA = handed.balA;
                    balB = handed.balB;
                    accumHandoffApplied = handed.applied;
                    const skipGrowthA = !!handed.skipGrowthA;
                    const skipGrowthB = !!handed.skipGrowthB;
                    const canDrawA = !skipGrowthA && canAccessSuperWithBucketStrategy({
                        currentAge, retirementAge: retAgeA, accessAge: yourSuperAccessAge,
                        allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                        enableBridgeStrategy: !!enableBridgeStrategy,
                        householdRetirementTriggered: isARetired || (isCouple && isBRetired),
                    });
                    const canDrawB = isCouple && !skipGrowthB && canAccessSuperWithBucketStrategy({
                        currentAge: partnerCurrentAge, retirementAge: retAgeB, accessAge: partnerSuperAccessAge,
                        allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
                        enableBridgeStrategy: !!enableBridgeStrategy,
                        householdRetirementTriggered: isARetired || (isCouple && isBRetired),
                    });
                    
                        const empIncomeA = isARetired ? 0 : salaryA;
                        const empIncomeB = isBRetired ? 0 : salaryB;
                    const sgA = (isARetired || skipGrowthA) ? 0 : calculateSGContributions(salaryA, sgRate);
                    const sgB = (isBRetired || skipGrowthB) ? 0 : calculateSGContributions(salaryB, sgRate);
                    
                        // Opening balances (1 July) — PMT and ATO minimum use balance BEFORE this year's return
                        const openingLiquid = balA + balB;
                        const openingInfra = infraBalA + infraBalB;
                        openingBalanceForRow = openingLiquid + openingInfra;
                        
                        // Calculate rental income (using fixed 2.5% inflation, same as main calculation)
                        const resVal0 = Number(residentialPropertyValue) || 0;
                        const comVal0 = Number(commercialPropertyValue) || 0;
                        const fixedInflation = 0.025;
                        const resRealG = residentialGrowthRate - fixedInflation;
                        const comRealG = commercialGrowthRate - fixedInflation;
                        const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
                        const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
                        const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
                          ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
                          : 0;
                        const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
                          ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
                          : 0;
                        rentalIncome = currentResidentialRental + currentCommercialRental;
                        
                        infrastructureDividends = openingInfra * (infrastructureRate / 100);
                        
                        const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
                        const estimatedAssetsOpening = otherAssetsValue + Math.max(0, openingLiquid) + totalAlternativeAssets + openingInfra;
                        const employmentIncome = empIncomeA + empIncomeB;
                        const otherIncomeForPension = computePensionAssessableOtherIncome({
                            other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
                        });
                    
                        pensionAnnual = calculateAgePensionForHousehold({
                            totalAssets: estimatedAssetsOpening,
                            otherIncome: otherIncomeForPension,
                            isCouple: true,
                            isHomeowner: homeowner,
                            year: i,
                            pensionGrowthRate: pensionGrowthRateVal,
                            employmentIncome,
                            ageA: currentAge,
                            ageB: partnerCurrentAge,
                            includePension: includeAgePension
                        });
                    
                        totalOtherIncome = computeHouseholdOtherIncome({
                            pensionAnnual,
                            employmentIncome: empIncomeA + empIncomeB,
                            other,
                            otherNonTax,
                            annuityReal,
                            afterTaxDbReal: dbIncomeForHousehold,
                            dbReal: 0,
                            rentalIncome,
                            infrastructureDividends
                        });
                        
                        const expectedReturnRate = initPct;
                        const yearsRemaining = Math.max(1, years - i);
                        const __tbInF = Number(targetFinalBalance) || 0;
                        const targetBalance = __tbInF < 10 ? 10 : __tbInF;
                        const accessibleOpeningDyn = (canDrawA ? balA : 0) + (canDrawB ? balB : 0);
                        const pmtWithdrawal = calculateAnnuityPayment(accessibleOpeningDyn, expectedReturnRate, yearsRemaining, targetBalance);
                        const minimumWithdrawal =
                            (canDrawA ? (balA + infraBalA) * SimulationEngine.getMinimumWithdrawalRate(currentAge) : 0)
                            + (canDrawB ? (balB + infraBalB) * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge) : 0);
                        actualNeedFromSuper = Math.max(pmtWithdrawal, minimumWithdrawal);
                        if (enableBridgeStrategy && (isARetired || isBRetired) && actualNeedFromSuper > 0) {
                            const fromCash = Math.min(actualNeedFromSuper, bridgeCashBalance);
                            bridgeCashBalance -= fromCash;
                            actualNeedFromSuper -= fromCash;
                            const fromInvestment = Math.min(actualNeedFromSuper, bridgeInvestmentBalance);
                            bridgeInvestmentBalance -= fromInvestment;
                            actualNeedFromSuper -= fromInvestment;
                            bridgeWithdrawal = fromCash + fromInvestment;
                        }
                        if (!canDrawA && !canDrawB) actualNeedFromSuper = 0;
                        income = actualNeedFromSuper;
                        
                        if (!skipGrowthA || !skipGrowthB) {
                            const grown = applyPhasedCoupleReturnsWithTax({
                                balA, balB, r, year, inflation,
                                generalTbc: generalTransferBalanceCapVal,
                                yourTbcUsed: yourTbcUsedVal,
                                partnerTbcUsed: partnerTbcUsedVal,
                                portfolio: accumTaxPortfolio,
                                workingA: !isARetired,
                                workingB: !isBRetired,
                            });
                            if (!skipGrowthA) balA = grown.balA;
                            if (!skipGrowthB) balB = grown.balB;
                        }
                        residentialPropValue *= (1 + residentialGrowthRate);
                        commercialPropValue *= (1 + commercialGrowthRate);
                        collectiblesVal *= (1 + collectiblesGrowthRate);
                        otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
                        if (enableBridgeStrategy && bridgeInvestmentBalance > 0) {
                            bridgeInvestmentBalance *= (1 + (Number(bridgeInvestmentReturnRate) || 0) / 100);
                        }
                        currentBalance = balA + balB;
                        
                        // Withdraw proportionally (capped at liquid + infrastructure)
                        if (actualNeedFromSuper > 0) {
                            const withdrawn = applyPhasedSuperWithdrawal({
                                balA, balB, infraBalA, infraBalB, amount: actualNeedFromSuper,
                                allowA: canDrawA,
                                allowB: canDrawB,
                            });
                            balA = withdrawn.balA;
                            balB = withdrawn.balB;
                            infraBalA = withdrawn.infraBalA;
                            infraBalB = withdrawn.infraBalB;
                            actualNeedFromSuper = withdrawn.actualWithdrawal;
                        }
                    
                        // Apply fees to total balance (liquid + infrastructure)
                    ({ balA, balB, infraBalA, infraBalB } = chargeMemberFees({ balA, balB, infraBalA, infraBalB, isCouple, feeInputs }));

                    contributionApplied = applyYearLumpSumContributions({
                        inputs,
                        contributions: lumpContributions,
                        yearIndex: i,
                        yourAge: yourAgeVal,
                        partnerAge: partnerAgeVal,
                        isCouple,
                        balA,
                        balB,
                        sgA,
                        sgB,
                        capStateA,
                        capStateB,
                    });
                    if (!skipGrowthA) balA = contributionApplied.balA;
                    if (!skipGrowthB) balB = contributionApplied.balB;
                    
                        currentBalance = balA + balB;
                        infraBal = infraBalA + infraBalB;
                        
                      } else {
                        // ===== STANDARD (NON-PHASED): Original logic =====
                        // Opening (1 July) pension-phase balance — BEFORE this year's returns. The statutory minimum must be based on this, not the
                        // post-return balance.
                        const openingHusbandRetireDynNP = accounts.husband.retire;
                        const openingWifeRetireDynNP = isCouple ? accounts.wife.retire : 0;
                        const openingLiquidDynNP = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                        const openingInfraDynNP = infraBal;
                        openingBalanceForRow = openingLiquidDynNP + openingInfraDynNP;
                        // CRITICAL FIX: Apply returns in ALL years including year 0 (same as phased)
                        // Returns array represents actual market returns for each year
                        // Apply returns FIRST, then calculate income based on updated balance
                        const husbandRetireEarnings = accounts.husband.retire * r;
                        const husbandAccumEarnings = accounts.husband.accum * r;
                        const wifeRetireEarnings = isCouple ? accounts.wife.retire * r : 0;
                        const wifeAccumEarnings = isCouple ? accounts.wife.accum * r : 0;
                        
                        // Apply earnings tax to accumulation accounts only
                        const portfolio = { sp500: sp500Val, ausShares: ausSharesVal, intlShares: intlSharesVal, bonds: bondsVal, cash: cashVal, ...SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS };
                        const husbandTsbEnd = SimulationEngine.memberTsbAtYearEnd(accounts, 'husband', husbandRetireEarnings, husbandAccumEarnings);
                        const husbandAccumTax = realAccumulationTax(accounts.husband.accum, r, portfolio, husbandTsbEnd, year, inflation);
                        const wifeTsbEnd = isCouple ? SimulationEngine.memberTsbAtYearEnd(accounts, 'wife', wifeRetireEarnings, wifeAccumEarnings) : null;
                        const wifeAccumTax = isCouple ? realAccumulationTax(accounts.wife.accum, r, portfolio, wifeTsbEnd, year, inflation) : 0;
                        
                        // Update balances
                        accounts.husband.retire += husbandRetireEarnings;
                        accounts.husband.accum += husbandAccumEarnings - husbandAccumTax;
                        if (isCouple) {
                          accounts.wife.retire += wifeRetireEarnings;
                          accounts.wife.accum += wifeAccumEarnings - wifeAccumTax;
                        }
                        
                        infraBal = applyFeesToAccounts(accounts, infraBal, isCouple, feeInputs);
                        contributionApplied = applyYearLumpSumContributions({
                            inputs,
                            contributions: lumpContributions,
                            yearIndex: i,
                            yourAge: yourAgeVal,
                            partnerAge: partnerAgeVal,
                            isCouple,
                            balA: (accounts.husband.retire + accounts.husband.accum),
                            balB: isCouple ? (accounts.wife.retire + accounts.wife.accum) : 0,
                            accounts,
                            capStateA,
                            capStateB,
                        });
                        
                        // NOW calculate income based on updated balance
                        currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                        
                        // Calculate rental income (using fixed 2.5% inflation, same as main calculation)
                        const resVal0 = Number(residentialPropertyValue) || 0;
                        const comVal0 = Number(commercialPropertyValue) || 0;
                        const fixedInflation = 0.025;
                        const resRealG = residentialGrowthRate - fixedInflation;
                        const comRealG = commercialGrowthRate - fixedInflation;
                        const currentResidentialValue = resVal0 * Math.pow(1 + resRealG, i);
                        const currentCommercialValue = comVal0 * Math.pow(1 + comRealG, i);
                        const currentResidentialRental = resVal0 > 0 && residentialRentalIncomeAnnual > 0
                          ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
                          : 0;
                        const currentCommercialRental = comVal0 > 0 && commercialRentalIncomeAnnual > 0
                          ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
                          : 0;
                        rentalIncome = currentResidentialRental + currentCommercialRental;
                        
                        // Calculate infrastructure dividends
                        infrastructureDividends = infraBal * (infrastructureRate / 100);
                        
                        // Apply capital growth to alternative assets
                        residentialPropValue *= (1 + residentialGrowthRate);
                        commercialPropValue *= (1 + commercialGrowthRate);
                        collectiblesVal *= (1 + collectiblesGrowthRate);
                        otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
                        
                        const totalAlternativeAssets = residentialPropValue + commercialPropValue + collectiblesVal + otherAlternativesVal;
                        const estimatedAssets = otherAssetsValue + Math.max(0, currentBalance) + totalAlternativeAssets + infraBal;
                        const incomeForPension = computePensionAssessableOtherIncome({
                            other, otherNonTax, annuityReal, dbReal, rentalIncome, infrastructureDividends
                        });
                        const includeAgePensionLocal = !!includePension;
                        const homeownerLocal = !!isHomeowner;
                        const isCoupleLocal = String(relationshipStatus) === 'couple';
                        pensionAnnual = calculateAgePensionForHousehold({
                            totalAssets: estimatedAssets,
                            otherIncome: incomeForPension,
                            isCouple: isCoupleLocal,
                            isHomeowner: homeownerLocal,
                            year: i,
                            pensionGrowthRate: pensionGrowthRateVal,
                            rentPaidAnnual,
                            ageA: currentAge,
                            ageB: partnerCurrentAge || currentAge,
                            includePension: includeAgePensionLocal
                        });
                        totalOtherIncome = computeHouseholdOtherIncome({
                            pensionAnnual,
                            other,
                            otherNonTax,
                            annuityReal,
                            afterTaxDbReal: dbIncomeForHousehold,
                            dbReal: 0,
                            rentalIncome,
                            infrastructureDividends
                        });
                        
                        // Calculate minimum required withdrawals from the OPENING (pre-return) balance
                        const minHusbandWithdrawal = openingHusbandRetireDynNP * SimulationEngine.getMinimumWithdrawalRate(currentAge);
                        const minWifeWithdrawal = isCoupleLocal ? openingWifeRetireDynNP * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge || currentAge) : 0;
                        const totalMinimumRequired = minHusbandWithdrawal + minWifeWithdrawal;
                        
                        // DYNAMIC SWR logic: Use annuity formula PMT(balance, expectedReturn, yearsRemaining, targetFinalBalance)
                        // initPct is the expected RETURN assumption (e.g., 6%), not a fixed withdrawal rate
                        const expectedReturnRate = initPct; // This is the return assumption being optimized
                        // Use the actual simulation period, not arbitrary age 100
                        const yearsRemaining = Math.max(1, years - i);
                        const __tbInG = Number(targetFinalBalance) || 0;
                        const targetBalance = __tbInG < 10 ? 10 : __tbInG;
                        
                        // Calculate annuity payment using PMT formula - only withdraw from amount above target balance
                        const pmtWithdrawal = calculateAnnuityPayment(currentBalance, expectedReturnRate, yearsRemaining, targetBalance);
                        
                        // Use the higher of PMT calculation or regulatory minimum, capped at accessible balance
                        const liquidAccessible = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                        const totalAccessible = liquidAccessible + infraBal;
                        actualNeedFromSuper = Math.min(
                            Math.max(pmtWithdrawal, totalMinimumRequired),
                            totalAccessible
                        );
                        income = actualNeedFromSuper;
                        
                        // Perform withdrawal (liquid first, then infrastructure)
                        const husbandProportion = liquidAccessible > 0
                            ? (accounts.husband.retire + accounts.husband.accum) / liquidAccessible
                            : 0;
                        const fromLiquid = Math.min(actualNeedFromSuper, liquidAccessible);
                        const actualLiquid = SimulationEngine.SuperannuationManager.performWithdrawal(
                            accounts, fromLiquid, husbandProportion
                        );
                        const fromInfra = actualNeedFromSuper - actualLiquid;
                        if (fromInfra > 0) {
                            infraBal = Math.max(0, infraBal - fromInfra);
                        }
                        actualNeedFromSuper = actualLiquid + fromInfra;
                        currentBalance = SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                      }

                      const lumpAppliedDyn = applyYearLumpSumWithdrawals({
                        ...buildBuildLumpSumContext(inputs, lumpSums, i, yourAgeVal, partnerAgeVal),
                        balA, balB, infraBalA, infraBalB, accounts, infraBal,
                      });
                      balA = lumpAppliedDyn.balA;
                      balB = lumpAppliedDyn.balB;
                      infraBalA = lumpAppliedDyn.infraBalA;
                      infraBalB = lumpAppliedDyn.infraBalB;
                      infraBal = lumpAppliedDyn.infraBal;
                      lumpSumWithdrawal = lumpAppliedDyn.lumpSumWithdrawal;
                      currentBalance = isPhasedRetirement
                        ? (balA + balB)
                        : SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                      
                      const yearTotalIncome = totalOtherIncome + bridgeWithdrawal + actualNeedFromSuper + lumpSumWithdrawal;
                      totalIncomeSum += yearTotalIncome;
                      
                      incomeByYear.push(buildStrategyIncomeRow({
                        year: start + i,
                        yearIndex: i + 1,
                        balance: Math.round(currentBalance + infraBal),
                        openingBalance: Math.round(openingBalanceForRow),
                        pension: Math.round(pensionAnnual),
                        ...formatStrategyIncomeDisplay({
                            isPhasedRetirement,
                            currentAge,
                            partnerCurrentAge,
                            retAgeA,
                            retAgeB,
                            salaryA,
                            salaryB,
                            other,
                            otherNonTax,
                            annuityReal,
                            dbIncomeForHousehold,
                            rentalIncome,
                            infrastructureDividends
                        }),
                        realReturn: r,
                        lumpSumContribution: Math.round(contributionApplied.lumpSumContribution || 0),
                        contributionsA: Math.round(contributionApplied.contributionsA || 0),
                        contributionsB: Math.round(contributionApplied.contributionsB || 0),
                        concessionalContributionsA: Math.round(contributionApplied.concessionalContributionsA || 0),
                        concessionalContributionsB: Math.round(contributionApplied.concessionalContributionsB || 0),
                        nonConcessionalContributionsA: Math.round(contributionApplied.nonConcessionalContributionsA || 0),
                        nonConcessionalContributionsB: Math.round(contributionApplied.nonConcessionalContributionsB || 0),
                        contributionFlags: contributionApplied.contributionFlags || [],
                        bridgeWithdrawal: Math.round(bridgeWithdrawal),
                      }, lumpSumWithdrawal, actualNeedFromSuper, totalOtherIncome + bridgeWithdrawal));
                      _ciDyn = advanceInflationIndex(_ciDyn, _yInflDyn);
                    }
                    
                    // Calculate MSI as the average of all years' total income
                    // For phased retirement: exclude working years from average
                    let effectiveYears = loopYears;
                    let effectiveTotalIncome = totalIncomeSum;
                    
                    if (isPhasedRetirement && incomeByYear.length > 0) {
                      // Find the year when BOTH partners are retired
                      const firstFullRetirementYear = incomeByYear.findIndex(row => {
                        const yearAge = yourAgeVal + row.yearIndex - 1;
                        const partnerYearAge = partnerAgeVal + row.yearIndex - 1;
                        return yearAge >= retAgeA && partnerYearAge >= retAgeB;
                      });
                      
                      if (firstFullRetirementYear > 0) {
                        // Exclude working years from average calculation
                        effectiveYears = loopYears - firstFullRetirementYear;
                        effectiveTotalIncome = incomeByYear.slice(firstFullRetirementYear)
                          .reduce((sum, row) => sum + (row.totalIncome || 0), 0);
                      }
                    }
                    
                    const averageIncome = effectiveYears > 0 ? effectiveTotalIncome / effectiveYears : 0;
                    
                    // CRITICAL: Return object with avgIncome, incomeByYear, and finalBalance (not just a number)
                    // Mobile calculator expects this format
                    // Include infrastructure in final balance
                    const finalLiquidBalance = isPhasedRetirement ? (balA + balB) : SimulationEngine.SuperannuationManager.getTotalBalance(accounts);
                    const finalInfraBalance = isPhasedRetirement ? (infraBalA + infraBalB) : infraBal;
                    const finalBalance = finalLiquidBalance + finalInfraBalance;
                    
                    return {
                        avgIncome: Math.round(averageIncome),
                        incomeByYear: incomeByYear,
                        finalBalance: Math.round(finalBalance)
                    };
};


// ============================================================================
// FIXED SUSTAINABLE BINARY SEARCH - DESKTOP SOURCE OF TRUTH
// Extracted from App_advanced_min.js lines 4754-5215 (EXACT COPY with input parameters)
// ============================================================================
export const computeFixedSustainableBinarySearch = (inputs, skipPhase2Detection = false, quietMode = true) => {
    const restoreConsole = quietMode ? (() => {
        const origLog = console.log;
        const origErr = console.error;
        const origWarn = console.warn;
        console.log = () => {};
        console.error = () => {};
        console.warn = () => {};
        return () => {
            console.log = origLog;
            console.error = origErr;
            console.warn = origWarn;
        };
    })() : () => {};

    try {
    if (!quietMode) {
    // ABSOLUTE DEBUG - this MUST show if function is called
    console.error('🚨🚨🚨 FUNCTION CALLED - computeFixedSustainableBinarySearch', {
        timestamp: new Date().toISOString(),
        hasInputs: !!inputs,
        skipPhase2Detection,
        phasedRetirement: inputs?.phasedRetirement,
        relationshipStatus: inputs?.relationshipStatus,
        yourAge: inputs?.yourAge,
        partnerAge: inputs?.partnerAge,
        stack: new Error().stack
    });
    }
    
    // Destructure inputs
    const {
        otherTaxableIncome, otherNonTaxableIncome, otherAssets,
        annuityAnnualPayment, annuityIsIndexed,
        definedBenefitPensionAmount, definedBenefitIsIndexed,
        adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap,
        relationshipStatus, partnerSuper, yourSuper,
        includePension, isHomeowner,
        sp500, ausShares, intlShares, bonds, cash, frankingCredits,
        phasedRetirement, yourRetirementAge, partnerRetirementAge,
        yourAge, partnerAge, yourSalary, partnerSalary,
        yourPartTimeEmploymentIncome, partnerPartTimeEmploymentIncome,
        msiStartsWhenEitherRetires,
        periodLength, startYear, pensionGrowthRate,
        generalTransferBalanceCap, yourTbcUsed, partnerTbcUsed,
        targetFinalBalance,
        monteCarloTargetIncome,
        providedReturns,
        // Alternative Assets from SMSF
        residentialPropertyValue,
        residentialRentalIncome,
        residentialPropertyCapitalGrowth,
        commercialPropertyValue,
        commercialRentalIncome,
        commercialPropertyCapitalGrowth,
        collectiblesValue,
        collectiblesCapitalGrowth,
        otherAlternativesValue,
        otherAlternativesCapitalGrowth,
        // Infrastructure from SMSF
        infrastructureValue,
        infrastructurePct,
        infrastructureReturnRate,
        // Rent Assistance
        rentPaidFortnightly,
        // Super withdrawal age control
        allowWithdrawalsBeforeRetirementAge,
        // Bridge / bucket strategy
        enableBridgeStrategy,
        yourSuperAccessAge,
        partnerSuperAccessAge,
        bridgeCashBucket,
        bridgeInvestmentBucket,
        bridgeInvestmentReturnRate
    } = inputs;
    
    const lumpSums = parseLumpSumWithdrawals(inputs);
    const lumpContributions = parseLumpSumContributions(inputs);
    
    // CRITICAL: Handle pre-retirement period (age < retirement age)
    // Show all years, but calculate MSI recursively for retirement years only.
    // Couples must NEVER use this shortcut: it zeros every year until YOUR retirement
    // age and ignores partner retirement + the either/primary drawdown trigger.
    const yourAgeVal = Number(yourAge) || 50;
    const yourRetirementAgeVal = Number(yourRetirementAge) || yourAgeVal;
    const yearsToRetirement = Math.max(0, yourRetirementAgeVal - yourAgeVal);
    const isCoupleForPreRetire = relationshipStatus === 'couple';
    const usePhasedPreRetirePath = shouldUsePhasedRetirementCalculations({
        phasedRetirement,
        isCouple: isCoupleForPreRetire
    });
    
    if (yearsToRetirement > 0 && !usePhasedPreRetirePath && !isCoupleForPreRetire) {
        // PRE-RETIREMENT (singles only): Simulate growth, then recursively calculate retirement MSI
        const periodLengthVal = Number(periodLength) || 30;
        const retirementYears = periodLengthVal - yearsToRetirement;
        
        if (retirementYears <= 0) {
            // No retirement years in the period - just return growth
            console.warn('⚠️ Period ends before retirement - no MSI to calculate');
            return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
        }
        
        console.log('📅 Pre-retirement: Simulating growth then calculating retirement MSI', {
            currentAge: yourAgeVal,
            retirementAge: yourRetirementAgeVal,
            yearsToRetirement,
            workingYears: yearsToRetirement,
            retirementYears,
            currentSuper: Number(yourSuper) || 0,
            inputStartYear: startYear,
            hasProvidedReturns: !!providedReturns,
            providedReturnsCount: providedReturns ? providedReturns.length : 0
        });
        
        // Step 1: Simulate super growth during working years
        const startYearVal = Number(startYear) || 1980;
        console.log('🔍 Start year processing:', {
            rawStartYear: startYear,
            typeOfStartYear: typeof startYear,
            numberConversion: Number(startYear),
            finalStartYearVal: startYearVal,
            willUseDefault: !startYear || isNaN(Number(startYear))
        });
        const yourSuperVal = Number(yourSuper) || 0;
        const partnerSuperVal = Number(partnerSuper) || 0;
        const yourSalaryVal = Number(yourSalary) || 0;
        const partnerSalaryVal = Number(partnerSalary) || 0;
        const partnerAgeVal = Number(partnerAge) || 0;
        const partnerRetirementAgeVal = Number(partnerRetirementAge) || partnerAgeVal;
        const accumHandoff = inputs.accumulationHandoff;
        const accumTaxPortfolio = buildAccumTaxPortfolio(
            Number(sp500) || 0,
            Number(ausShares) || 0,
            Number(intlShares) || 0,
            Number(bonds) || 0,
            Number(cash) || 0
        );
        const capStateA = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
        const capStateB = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
        let accumHandoffApplied = { you: false, partner: false };
        
        // Get returns for working years
        const workingReturns = providedReturns ? providedReturns.slice(0, yearsToRetirement) : (() => {
            const returns = [];
            for (let i = 0; i < yearsToRetirement; i++) {
                const year = startYearVal + i;
                const yearData = historicalData[year];
                if (!yearData) {
                    console.error(`Missing historical data for year ${year}`);
                    return [];
                }
                const portfolio = { 
                    sp500: Number(sp500) || 0, 
                    ausShares: Number(ausShares) || 0, 
                    intlShares: Number(intlShares) || 0, 
                    bonds: Number(bonds) || 0, 
                    cash: Number(cash) || 0, 
                    frankingCredits: Number(frankingCredits) || 0
                };
                const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
                const histInfl = (historicalData[year]?.inflation) || 0;
                const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
                returns.push({ year, r: netR });
            }
            return returns;
        })();
        
        console.log('📊 Working period returns:', {
            yearsToRetirement,
            hasProvidedReturns: !!providedReturns,
            providedReturnsCount: providedReturns ? providedReturns.length : 0,
            workingReturnsCount: workingReturns.length,
            startYear: startYearVal,
            firstThreeWorkingReturns: workingReturns.slice(0, 3).map(r => `${r.year}: ${(r.r * 100).toFixed(1)}%`).join(', ')
        });
        
        // Simulate growth (or use Adviser accumulation handoff balances at retirement)
        const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
        let balA = yourSuperVal;
        let balB = partnerSuperVal;
        const workingYearsData = [];
        
        // Use the same annual contribution, tax, fee and handoff mechanics as the
        // other accumulation paths.
        for (let i = 0; i < yearsToRetirement; i++) {
            const { year, r, inflation } = workingReturns[i] || { year: startYearVal + i, r: 0, inflation: 0 };
            const currentAge = yourAgeVal + i;
            const partnerCurrentAge = partnerAgeVal + i;
            const handed = applyAccumulationHandoffPath({
                handoff: accumHandoff,
                applied: accumHandoffApplied,
                currentAge,
                partnerCurrentAge,
                retAgeA: yourRetirementAgeVal,
                retAgeB: partnerRetirementAgeVal,
                balA,
                balB,
                isCouple: false,
                yearIndex: i,
                yourAgeStart: yourAgeVal,
                partnerAgeStart: partnerAgeVal || yourAgeVal,
            });
            balA = handed.balA;
            balB = handed.balB;
            accumHandoffApplied = handed.applied;
            const isAWorking = currentAge < yourRetirementAgeVal;
            const isBWorking = partnerCurrentAge < partnerRetirementAgeVal;
            const sgA = (isAWorking && !handed.skipGrowthA) ? calculateSGContributions(yourSalaryVal, sgRate) : 0;
            const sgB = (isBWorking && !handed.skipGrowthB) ? calculateSGContributions(partnerSalaryVal, sgRate) : 0;
            if (!handed.skipGrowthA) {
                balA = applyPhasedMemberReturnWithTax(balA, r, 0, accumTaxPortfolio, year, inflation);
                balA = Math.max(0, balA - calculateAdvancedFees(
                    balA, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap
                ));
            }
            const contributionApplied = applyYearLumpSumContributions({
                inputs,
                contributions: lumpContributions,
                yearIndex: i,
                yourAge: yourAgeVal,
                partnerAge: partnerAgeVal,
                isCouple: false,
                balA,
                balB,
                sgA,
                sgB,
                capStateA,
                capStateB,
            });
            if (!handed.skipGrowthA) balA = contributionApplied.balA;
            if (!handed.skipGrowthB) balB = contributionApplied.balB;
            const { employmentIncome } = resolveEmploymentIncomesForYear({
                isARetired: !isAWorking,
                isBRetired: true,
                salaryA: yourSalaryVal,
                salaryB: 0,
                partTimeA: Number(yourPartTimeEmploymentIncome) || 0,
                partTimeB: 0,
                useAfterTax: true,
            });

            workingYearsData.push({
                year,
                yearIndex: i + 1,
                balance: Math.round(balA + balB),
                balanceWithTargetIncome: null,
                withdrawalFromSuper: 0,
                pension: 0,
                otherTaxableIncome: employmentIncome,
                otherNonTaxableIncome: 0,
                realReturn: r,
                totalIncome: employmentIncome,
                lumpSumContribution: Math.round(contributionApplied.lumpSumContribution || 0),
                contributionFlags: contributionApplied.contributionFlags || [],
                accumulationHandoffApplied: !!(accumHandoffApplied.you || accumHandoffApplied.partner),
            });
        }
        
        const superAtRetirement = balA + balB;
        
        console.log('📊 Growth simulation complete:', {
            startSuper: yourSuperVal + partnerSuperVal,
            superAtRetirement: Math.round(superAtRetirement),
            yearsSimulated: yearsToRetirement
        });
        
        // Step 2: Recursively call for retirement period
        const retirementReturns = providedReturns ? providedReturns.slice(yearsToRetirement) : null;
        
        const retirementInputs = {
            ...inputs,
            yourAge: yourRetirementAgeVal,
            partnerAge: partnerAgeVal + yearsToRetirement,
            yourSuper: balA,
            partnerSuper: balB,
            yourSalary: 0, // Retired
            partnerSalary: 0, // Retired
            periodLength: retirementYears,
            startYear: startYearVal + yearsToRetirement,
            providedReturns: retirementReturns
        };
        
        console.log('🔄 Recursive call for retirement period:', {
            retirementAge: yourRetirementAgeVal,
            retirementYears,
            superAtRetirement: Math.round(superAtRetirement),
            hasProvidedReturns: !!retirementReturns,
            retirementReturnsCount: retirementReturns ? retirementReturns.length : 0,
            firstThreeRetirementReturns: retirementReturns ? retirementReturns.slice(0, 3).map(r => `${r.year}: ${(r.r * 100).toFixed(1)}%`).join(', ') : 'N/A',
            newStartYear: startYearVal + yearsToRetirement,
            originalProvidedReturnsCount: providedReturns ? providedReturns.length : 0,
            sliceStart: yearsToRetirement
        });
        
        const retirementResult = computeFixedSustainableBinarySearch(retirementInputs, skipPhase2Detection);
        
        // Step 3: Combine working years + retirement years
        const combinedIncomeByYear = [
            ...workingYearsData,
            ...retirementResult.incomeByYear.map((year, idx) => ({
                ...year,
                year: startYearVal + yearsToRetirement + idx,
                yearIndex: yearsToRetirement + idx + 1
            }))
        ];
        
        console.log('✅ Combined result:', {
            totalYears: combinedIncomeByYear.length,
            workingYears: workingYearsData.length,
            retirementYears: retirementResult.incomeByYear.length,
            retirementMSI: retirementResult.avgIncome,
            finalBalance: retirementResult.finalBalance
        });
        
        // Return combined result
        return {
            avgIncome: retirementResult.avgIncome, // MSI for retirement years only
            incomeByYear: combinedIncomeByYear,
            finalBalance: retirementResult.finalBalance
        };
    }
    
    // Define all required variables
    const other = Number(otherTaxableIncome) || 0;
    const otherNonTax = Number(otherNonTaxableIncome) || 0;
    const otherAssetsValue = Number(otherAssets) || 0;
    const annuityPay = Number(annuityAnnualPayment) || 0;
    const dbPension = Number(definedBenefitPensionAmount) || 0;
    // DB pensions from untaxed government funds are taxable (with 10% offset).
    // Compute the marginal after-tax value so the simulator credits only spendable dollars.
    const afterTaxDbPension = dbPension > 0
        ? Math.max(0, calculateAfterTaxIncome(other + dbPension) - calculateAfterTaxIncome(other))
        : 0;
    
    // Rent Assistance
    const rentPaidAnnual = (Number(rentPaidFortnightly) || 0) * 26; // Convert fortnightly to annual
    
    // Alternative Assets - Initialize values and growth rates
    let residentialPropValue = Number(residentialPropertyValue) || 0;
    let commercialPropValue = Number(commercialPropertyValue) || 0;
    let collectiblesVal = Number(collectiblesValue) || 0;
    let otherAlternativesVal = Number(otherAlternativesValue) || 0;
    const residentialRentalIncomeAnnual = Number(residentialRentalIncome) || 0;
    const commercialRentalIncomeAnnual = Number(commercialRentalIncome) || 0;
    const residentialGrowthRate = (Number(residentialPropertyCapitalGrowth) || 0) / 100;
    const commercialGrowthRate = (Number(commercialPropertyCapitalGrowth) || 0) / 100;
    const collectiblesGrowthRate = (Number(collectiblesCapitalGrowth) || 0) / 100;
    const otherAlternativesGrowthRate = (Number(otherAlternativesCapitalGrowth) || 0) / 100;
    
    const isCouple = relationshipStatus === 'couple';
    const partnerSuperForCalc = isCouple ? (Number(partnerSuper) || 0) : 0;
    const totalSuper = (Number(yourSuper) || 0) + partnerSuperForCalc;
    // Handoff keeps today's input supers for the UI / pre-retirement path, but MSI is
    // funded from the injected retirement nest egg — bounds and setup estimates must use it.
    const handoffNestEgg = resolveAccumulationHandoffNestEgg(inputs.accumulationHandoff, isCouple);
    const effectiveSuperForMsiBounds = Math.max(totalSuper, handoffNestEgg);
    const includeAgePension = !!includePension;
    const homeowner = !!isHomeowner;
    
    // Super withdrawal age control - default to false (must reach retirement age)
    const allowWithdrawalsBeforeRetirement = allowWithdrawalsBeforeRetirementAge !== undefined 
        ? !!allowWithdrawalsBeforeRetirementAge 
        : false;
    const pensionGrowthRateVal = Number(pensionGrowthRate) || 0;
    const years = Math.max(0, Number(periodLength) || 0);
    const start = Number(startYear) || 1980;
    const bridgeStrategyEnabled = !!enableBridgeStrategy;
    const bridgeInvestmentRealReturn = (Number(bridgeInvestmentReturnRate) || 0) / 100;
    const mortgageConfig = buildMortgageStrategyConfig(inputs);
    
    // Fee calculation function
    const calculateFees = (balance) => {
        return calculateAdvancedFees(balance, adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap);
    };
    const feeInputs = { adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap };
    
    // Generate returns array if not provided - EXACT DESKTOP LOGIC (lines 4669-4686)
    const returnsArray = providedReturns || (() => {
        const sp500Val = Number.isFinite(Number(sp500)) ? Number(sp500) : 0;
        const ausSharesVal = Number.isFinite(Number(ausShares)) ? Number(ausShares) : 0;
        const intlSharesVal = Number.isFinite(Number(intlShares)) ? Number(intlShares) : 0;
        const bondsVal = Number.isFinite(Number(bonds)) ? Number(bonds) : 0;
        const cashVal = Number.isFinite(Number(cash)) ? Number(cash) : 0;
        const frankVal = Number.isFinite(Number(frankingCredits)) ? Number(frankingCredits) : 0;
        
        const returns = [];
        for (let i = 0; i < years; i++) {
            const year = start + i;
            const yearData = historicalData[year];
            if (!yearData) {
                return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
            }
            const portfolio = { 
                sp500: sp500Val, 
                ausShares: ausSharesVal, 
                intlShares: intlSharesVal, 
                bonds: bondsVal, 
                cash: cashVal, 
                frankingCredits: frankVal,
                infrastructurePct: Number(infrastructurePct) || 0,
                infrastructureReturnRate: Number(infrastructureReturnRate) || 0
            };
            const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
            const histInfl = (historicalData[year]?.inflation) || 0;
            // DO NOT deduct investment fees here - calculateFees will apply them
            const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
            returns.push({ year, r: netR });
        }
        return returns;
    })();
    
    if (!returnsArray || !returnsArray.length) {
        return { avgIncome: 0, incomeByYear: [], finalBalance: 0 };
    }
    
    // CRITICAL: Detect three-phase scenario and check if older partner can sustain Phase 2+3 MSI
    let shouldMaximizePhase2Withdrawals = false;
    let phase2Plus3MSI = 0; // Trial MSI: Phase 2+3 combined MSI (the number to use for Phase 2)
    let phase2MSI = 0; // Phase 2-specific MSI when maximizing (calculated by depleting older partner's super)
    let phase2Length = 0; // Length of Phase 2 in years
    let phase3MSI = 0; // Phase 3 MSI when Phase 2 cannot sustain Phase 2+3 MSI (calculated after Phase 2 depletion)
    let phase2Plus3IncomeSeries = null; // Phase 2+3 income series from main calculator when canSustain is true
    // Accumulation handoff already injects each member's nest egg at their retirement year.
    // Phase-2 projection grows today's balances to 60 and then hard-caps MSI at that trial
    // figure — which fights the handoff and flattens rolling / MC income charts.
    const skipPhase2ForHandoff = !!inputs.accumulationHandoff?.enabled;
    // ALWAYS log - helps debug if calculation is running
    console.warn('🔍🔍🔍 PHASE 2 DETECTION - computeFixedSustainableBinarySearch called', {
        skipPhase2Detection,
        skipPhase2ForHandoff,
        phasedRetirement,
        isCouple,
        yourAge,
        partnerAge,
        yourSuper: Number(yourSuper) || 0,
        partnerSuper: partnerSuperForCalc,
        condition: !skipPhase2Detection && !skipPhase2ForHandoff && phasedRetirement && isCouple && yourAge < 60 && partnerAge < 60
    });
    // CRITICAL: Allow same-age partners - they both reach 60 at the same time, so no Phase 2, but Phase 1 still needs handling
    if (!skipPhase2Detection && !skipPhase2ForHandoff && phasedRetirement && isCouple && yourAge < 60 && partnerAge < 60) {
        console.log('✅ Phase 2 Detection: ENTERING detection block', { yourAge, partnerAge, sameAge: yourAge === partnerAge });
        const partnerAIsOlder = yourAge > partnerAge;
        const olderAge = partnerAIsOlder ? yourAge : partnerAge;
        const youngerAge = partnerAIsOlder ? partnerAge : yourAge;
        const olderSuper = partnerAIsOlder ? (Number(yourSuper) || 0) : partnerSuperForCalc;
        const youngerSuper = partnerAIsOlder ? partnerSuperForCalc : (Number(yourSuper) || 0);
        const olderSalary = partnerAIsOlder ? (Number(yourSalary) || 0) : (Number(partnerSalary) || 0);
        const youngerSalary = partnerAIsOlder ? (Number(partnerSalary) || 0) : (Number(yourSalary) || 0);
        
        const yearsToOlder60 = 60 - olderAge;
        const yearsToYounger60 = 60 - youngerAge;
        phase2Length = yearsToYounger60 - yearsToOlder60; // Store for use in isIncomeSustainable
        
        // CRITICAL: If both partners are the same age, phase2Length will be 0 (no Phase 2)
        // In this case, they both reach 60 at the same time, so we go directly from Phase 1 to Phase 3
        // Skip Phase 2 sustainability test and use standard MSI calculation
        if (phase2Length === 0) {
            console.log('✅ Phase 2 Detection: Both partners same age - no Phase 2, using standard MSI for Phase 3');
            // Skip Phase 2 detection logic - will use standard MSI calculation
            // phase2Plus3MSI will be calculated below for Phase 3 (both >= 60)
        }
        
        const projectionTaxPortfolio = buildAccumTaxPortfolio(
            Number(sp500) || 0, Number(ausShares) || 0, Number(intlShares) || 0, Number(bonds) || 0, Number(cash) || 0
        );
        const projectMemberTo60 = (member) => projectWorkingMemberBalance({
            ...member,
            targetAge: 60,
            returns: returnsArray,
            portfolio: projectionTaxPortfolio,
            calculateFees: (balance) => memberFee(balance, feeInputs, isCouple),
        });
        const olderIsYou = partnerAIsOlder;
        const olderBalAt60 = projectMemberTo60({
            startBalance: olderSuper,
            startAge: olderAge,
            salary: olderSalary,
            retirementAge: olderIsYou ? inputs.yourRetirementAge : inputs.partnerRetirementAge,
            carryForwardBalances: olderIsYou ? inputs.carryForwardBalances : inputs.partnerCarryForwardBalances,
            workTestFlag: olderIsYou ? inputs.enableWorkTest : inputs.partnerEnableWorkTest,
        });
        const youngerBalAt60 = projectMemberTo60({
            startBalance: youngerSuper,
            startAge: youngerAge,
            salary: youngerSalary,
            retirementAge: olderIsYou ? inputs.partnerRetirementAge : inputs.yourRetirementAge,
            carryForwardBalances: olderIsYou ? inputs.partnerCarryForwardBalances : inputs.carryForwardBalances,
            workTestFlag: olderIsYou ? inputs.partnerEnableWorkTest : inputs.enableWorkTest,
        });
        
        // Calculate Phase 2+3 combined MSI (30 years: phase2Length + remaining years)
        const phase2Plus3Years = years - yearsToOlder60;
        if (phase2Plus3Years > 0 && olderBalAt60 > 0 && youngerBalAt60 > 0) {
            const phase2Plus3Inputs = {
                ...inputs,
                yourSuper: partnerAIsOlder ? olderBalAt60 : youngerBalAt60,
                partnerSuper: partnerAIsOlder ? youngerBalAt60 : olderBalAt60,
                periodLength: phase2Plus3Years,
                yourAge: 60,
                partnerAge: 60,
                startYear: start + yearsToOlder60,
                providedReturns: returnsArray.slice(yearsToOlder60, yearsToOlder60 + phase2Plus3Years)
            };
            const phase2Plus3Result = computeFixedSustainableBinarySearch(phase2Plus3Inputs, true); // Skip detection in recursive call
            phase2Plus3MSI = phase2Plus3Result.avgIncome || 0; // Store trial MSI for use in Phase 2
            
            // If Phase 2+3 MSI calculation failed, assume older partner can't sustain (be conservative)
            if (phase2Plus3MSI <= 0) {
                shouldMaximizePhase2Withdrawals = true;
                console.log('🔍 Phase 2 Detection: Phase 2+3 MSI calculation failed, maximizing withdrawals');
            } else if (phase2Length === 0) {
                // Both partners same age - no Phase 2, use standard MSI for Phase 3
                shouldMaximizePhase2Withdrawals = false;
                console.log('✅ Phase 2 Detection: Both partners same age - using standard MSI for Phase 3', {
                    phase2Plus3MSI,
                    phase2Plus3Years,
                    yearsToOlder60,
                    incomeSeriesLength: phase2Plus3Result.incomeByYear?.length || 0
                });
                // phase2Plus3MSI is actually Phase 3 MSI (both >= 60), use it directly
                // CRITICAL: Store the income series from the main calculator for Phase 3
                phase2Plus3IncomeSeries = phase2Plus3Result.incomeByYear || [];
                // Normalize income series totalIncome to constant MSI
                phase2Plus3IncomeSeries = phase2Plus3IncomeSeries.map(yearData => ({
                    ...yearData,
                    totalIncome: phase2Plus3MSI // Force constant MSI for all years
                }));
                phase2MSI = phase2Plus3MSI; // Use for Phase 2 (though there is no Phase 2)
                phase3MSI = phase2Plus3MSI; // Use for Phase 3
                console.log('✅ Phase 2 Detection: Same-age partners - phase2Plus3IncomeSeries set', {
                    hasIncomeSeries: !!phase2Plus3IncomeSeries,
                    incomeSeriesLength: phase2Plus3IncomeSeries.length,
                    phase2Plus3MSI
                });
            } else {
                // Test if older partner CAN sustain Phase 2+3 MSI for Phase 2
                // SINGLE SOURCE OF TRUTH: Use main calculator's isIncomeSustainable function
                // Create a temporary isIncomeSustainable function for Phase 2 test with only older partner's super
                const phase2Returns = returnsArray.slice(yearsToOlder60, yearsToOlder60 + phase2Length);
                const phase2TestInputs = {
                    ...inputs,
                    yourSuper: partnerAIsOlder ? olderBalAt60 : 0,
                    partnerSuper: partnerAIsOlder ? 0 : olderBalAt60,
                    periodLength: phase2Length,
                    yourAge: 60,
                    partnerAge: partnerAIsOlder ? (60 - phase2Length) : 60, // Keep younger partner < 60
                    startYear: start + yearsToOlder60,
                    providedReturns: phase2Returns,
                    targetFinalBalance: 0 // CRITICAL: For Phase 2 test, use 0 target to maximize withdrawals
                };
                
                // SINGLE SOURCE OF TRUTH: Use main calculator to test if Phase 2+3 MSI is sustainable
                // Call main calculator with only older partner's super for Phase 2
                // The calculated MSI tells us what's sustainable - if it's less than Phase 2+3 MSI, can't sustain
                const testResult = computeFixedSustainableBinarySearch(phase2TestInputs, true);
                
                // If the MSI sustainable with only older partner's super is less than Phase 2+3 MSI,
                // older partner CANNOT sustain Phase 2+3 MSI
                const sustainableMSIWithOlderPartnerOnly = testResult.avgIncome || 0;
                let canSustain = sustainableMSIWithOlderPartnerOnly >= phase2Plus3MSI * 0.99; // 1% tolerance
                
                // CRITICAL: Recalculate actual Phase 2+3 MSI first, then verify older partner can sustain it
                // The actual MSI might differ from the trial MSI, so we need to verify against the actual value
                // IMPORTANT: Use correct ages - older partner at 60, younger partner at their actual age (still < 60 in Phase 2)
                const phase2Plus3FinalInputs = {
                    ...inputs,
                    yourSuper: partnerAIsOlder ? olderBalAt60 : youngerBalAt60,
                    partnerSuper: partnerAIsOlder ? youngerBalAt60 : olderBalAt60,
                    periodLength: phase2Plus3Years,
                    yourAge: partnerAIsOlder ? 60 : youngerAge + yearsToOlder60, // Older partner at 60, younger at their actual age
                    partnerAge: partnerAIsOlder ? youngerAge + yearsToOlder60 : 60, // Younger partner at their actual age, older at 60
                    startYear: start + yearsToOlder60,
                    providedReturns: returnsArray.slice(yearsToOlder60, yearsToOlder60 + phase2Plus3Years)
                };
                // SINGLE SOURCE OF TRUTH: Main calculator calculates MSI for entire Phase 2+3 period
                // The calculator will correctly handle preservation age (60) - only older partner can access super in Phase 2
                const phase2Plus3FinalResult = computeFixedSustainableBinarySearch(phase2Plus3FinalInputs, true); // Skip detection in recursive call
                const actualPhase2Plus3MSI = phase2Plus3FinalResult.avgIncome || 0; // This is the actual MSI
                
                // CRITICAL: Verify older partner can sustain the ACTUAL MSI (not just the trial MSI)
                // The actual MSI might be different from the trial, so we need to re-check
                canSustain = sustainableMSIWithOlderPartnerOnly >= actualPhase2Plus3MSI * 0.99; // 1% tolerance
                
                // Set flag: maximize withdrawals if older partner CANNOT sustain Phase 2+3 MSI
                shouldMaximizePhase2Withdrawals = !canSustain;
                
                if (canSustain) {
                    // CRITICAL: Older partner CAN sustain Phase 2+3 MSI
                    // Use the actual MSI calculated above
                    phase2Plus3MSI = actualPhase2Plus3MSI; // Use the actual MSI for both Phase 2 and Phase 3
                    phase2Plus3IncomeSeries = phase2Plus3FinalResult.incomeByYear || []; // Store income series from main calculator
                    
                    // CRITICAL: Normalize income series totalIncome to constant MSI
                    // The main calculator's income series may have varying totalIncome due to employment income changes
                    // But the MSI is constant, so we need to ensure all years use the constant MSI as totalIncome
                    console.log('🔍 BEFORE normalization - income series totalIncome values:', 
                        phase2Plus3IncomeSeries.slice(0, 10).map(y => y.totalIncome));
                    phase2Plus3IncomeSeries = phase2Plus3IncomeSeries.map(yearData => ({
                        ...yearData,
                        totalIncome: phase2Plus3MSI // Force constant MSI for all years
                    }));
                    console.log('🔍 AFTER normalization - income series totalIncome values:', 
                        phase2Plus3IncomeSeries.slice(0, 10).map(y => y.totalIncome));
                    console.log('🔍 phase2Plus3MSI:', phase2Plus3MSI);
                    
                    phase2MSI = phase2Plus3MSI; // Use for Phase 2
                    phase3MSI = phase2Plus3MSI; // Use for Phase 3 (same MSI for entire period)
                } else {
                    // Older partner CANNOT sustain Phase 2+3 MSI - maximize Phase 2, then calculate Phase 3
                    // CRITICAL: phase2MSI should maximize income by depleting older partner's super over Phase 2
                    // The testResult already calculated this with targetFinalBalance = 0, so it should maximize withdrawals
                    phase2MSI = sustainableMSIWithOlderPartnerOnly; // This is the MSI achievable in Phase 2 (maximized)
                    console.log('🔍 Phase 2 Maximization:', {
                        olderBalAt60,
                        phase2Length,
                        phase2MSI,
                        sustainableMSIWithOlderPartnerOnly,
                        phase2Plus3MSI,
                        canSustain
                    });
                    
                    // CRITICAL: Calculate Phase 3 MSI after Phase 2 depletion
                    // Phase 3 starts when both partners are 60+ with remaining super balance
                    // SINGLE SOURCE OF TRUTH: Use main calculator to calculate Phase 3 MSI
                    const phase3Years = years - yearsToOlder60 - phase2Length; // Remaining years after Phase 2
                    if (phase3Years > 0) {
                        // Simulate Phase 2 with phase2MSI to get actual ending balance
                        // This ensures we use the correct balance for Phase 3 calculation
                        // CRITICAL: Must match income series logic exactly
                        let simulatedOlderBal = olderBalAt60;
                        const phase2Returns = returnsArray.slice(yearsToOlder60, yearsToOlder60 + phase2Length);
                        const other = (Number(inputs.otherTaxableIncome) || 0);
                        const otherNonTax = (Number(inputs.otherNonTaxableIncome) || 0);
                        const annuityPay = (Number(inputs.annuityAnnualPayment) || 0);
                        const dbPension = (Number(inputs.definedBenefitPensionAmount) || 0);
                        const annuityIsIndexedP2 = inputs.annuityIsIndexed !== false;
                        const definedBenefitIsIndexedP2 = inputs.definedBenefitIsIndexed !== false;
                        const youngerSalary = partnerAIsOlder ? (Number(inputs.partnerSalary) || 0) : (Number(inputs.yourSalary) || 0);
                        const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
                        let _ciPhase2 = 1.0;
                        
                        for (let phase2Year = 0; phase2Year < phase2Length && phase2Year < phase2Returns.length; phase2Year++) {
                            const { year: phase2CalendarYear, r } = phase2Returns[phase2Year];
                            const currentAge = 60 + phase2Year;
                            const youngerCurrentAge = youngerAge + yearsToOlder60 + phase2Year;
                            const _yInflP2 = historicalData[phase2CalendarYear]?.inflation ?? 0.025;
                            const { annuityReal, dbReal, dbIncomeForHousehold } = getIndexedIncomeStreams({
                                annuityPay, dbPension, annuityIsIndexed: annuityIsIndexedP2,
                                definedBenefitIsIndexed: definedBenefitIsIndexedP2, other,
                                cumulativeInflationIndex: _ciPhase2
                            });
                            
                            // Apply growth FIRST (matches income series)
                            simulatedOlderBal *= (1 + r);
                            
                            // Calculate totalOtherIncome the same way as income series
                            // Older partner is retired (60+), younger partner might still be working
                            const isYoungerRetired = youngerCurrentAge >= (partnerAIsOlder ? (Number(inputs.partnerRetirementAge) || 60) : (Number(inputs.yourRetirementAge) || 60));
                            const empIncomeYounger = isYoungerRetired ? 0 : calculateAfterTaxIncome(youngerSalary);
                            const employmentIncome = empIncomeYounger; // Older partner is retired
                            
                            // Pension calculation (matches income series logic)
                            const estimatedAssets = (Number(inputs.otherAssets) || 0) + Math.max(0, simulatedOlderBal);
                            const pensionAnnual = 0; // Phase 2: older partner is 60-65, no pension yet (starts at 67)
                            
                            const totalOtherIncome = computeHouseholdOtherIncome({
                                pensionAnnual,
                                employmentIncome,
                                other,
                                otherNonTax,
                                annuityReal,
                                afterTaxDbReal: dbIncomeForHousehold,
                                dbReal: 0,
                                rentalIncome: 0,
                                infrastructureDividends: 0
                            });
                            
                            // SINGLE SOURCE OF TRUTH: Use phase2MSI directly (same as income series)
                            const annualNeedFromSuper = Math.max(0, phase2MSI - totalOtherIncome);
                            // Enforce minimum withdrawal requirements (matches income series)
                            const minimumWithdrawalRate = SimulationEngine.getMinimumWithdrawalRate(currentAge);
                            const minimumRequired = simulatedOlderBal * minimumWithdrawalRate;
                            const withdrawal = Math.max(Math.min(annualNeedFromSuper, simulatedOlderBal), minimumRequired);
                            simulatedOlderBal -= withdrawal;
                            
                            // Apply fees LAST (matches income series)
                            const fees = memberFee(simulatedOlderBal, feeInputs, isCouple);
                            simulatedOlderBal = Math.max(0, simulatedOlderBal - fees);
                            _ciPhase2 = advanceInflationIndex(_ciPhase2, _yInflP2);
                        }
                        const balanceAfterPhase2 = Math.max(0, simulatedOlderBal);
                        
                        // Phase 3 uses younger partner's balance (at age 60) + any remaining from older partner
                        // Calculate Phase 3 MSI using main calculator with both partners at 60+
                        const phase3Returns = returnsArray.slice(yearsToOlder60 + phase2Length, yearsToOlder60 + phase2Length + phase3Years);
                        const phase3Inputs = {
                            ...inputs,
                            yourSuper: partnerAIsOlder ? youngerBalAt60 : balanceAfterPhase2,
                            partnerSuper: partnerAIsOlder ? balanceAfterPhase2 : youngerBalAt60,
                            periodLength: phase3Years,
                            yourAge: 60,
                            partnerAge: 60,
                            startYear: start + yearsToOlder60 + phase2Length,
                            providedReturns: phase3Returns
                        };
                        // SINGLE SOURCE OF TRUTH: Main calculator handles income sustainability and final balance
                        const phase3Result = computeFixedSustainableBinarySearch(phase3Inputs, true); // Skip detection in recursive call
                        phase3MSI = phase3Result.avgIncome || 0;
                    }
                    
                    // CRITICAL: If Phase 2 MSI > Phase 3 MSI, use unified calculation instead
                    // This handles edge cases where maximizing Phase 2 gives higher income than Phase 3
                    // In this case, the unified calculation will be better
                    if (phase2MSI > phase3MSI && phase2Plus3MSI > 0) {
                        console.log('🔍 Phase 2 MSI > Phase 3 MSI, using unified calculation:', {
                            phase2MSI,
                            phase3MSI,
                            phase2Plus3MSI
                        });
                        // Use unified calculation - recalculate Phase 2+3 MSI
                        const phase2Plus3FinalInputs = {
                            ...inputs,
                            yourSuper: partnerAIsOlder ? olderBalAt60 : youngerBalAt60,
                            partnerSuper: partnerAIsOlder ? youngerBalAt60 : olderBalAt60,
                            periodLength: phase2Plus3Years,
                            yourAge: partnerAIsOlder ? 60 : youngerAge + yearsToOlder60,
                            partnerAge: partnerAIsOlder ? youngerAge + yearsToOlder60 : 60,
                            startYear: start + yearsToOlder60,
                            providedReturns: returnsArray.slice(yearsToOlder60, yearsToOlder60 + phase2Plus3Years)
                        };
                        const phase2Plus3FinalResult = computeFixedSustainableBinarySearch(phase2Plus3FinalInputs, true);
                        phase2Plus3MSI = phase2Plus3FinalResult.avgIncome || 0;
                        phase2Plus3IncomeSeries = phase2Plus3FinalResult.incomeByYear || [];
                        
                        // Normalize income series to constant MSI
                        phase2Plus3IncomeSeries = phase2Plus3IncomeSeries.map(yearData => ({
                            ...yearData,
                            totalIncome: phase2Plus3MSI
                        }));
                        
                        phase2MSI = phase2Plus3MSI;
                        phase3MSI = phase2Plus3MSI;
                        shouldMaximizePhase2Withdrawals = false; // Use unified calculation
                    }
                }
                console.log('🔍 Phase 2 Detection:', { 
                    phase2Plus3MSI, 
                    olderBalAt60, 
                    youngerBalAt60,
                    phase2Length,
                    sustainableMSIWithOlderPartnerOnly: testResult.avgIncome,
                    phase2MSI,
                    phase3MSI,
                    canSustain, 
                    shouldMaximizePhase2Withdrawals
                });
            }
        }
    }
    
    const usePhasedHouseholdInputs = shouldUsePhasedRetirementCalculations({ phasedRetirement, isCouple });
    // Couples with phased retirement: nominated ages + either/primary MSI trigger.
    // Without phased retirement the trigger is inoperative (standard household MSI).
    const useCoupleMsiTrigger = isCouple && !!phasedRetirement;
    const startMsiOnEitherPartner = !useCoupleMsiTrigger
        || (msiStartsWhenEitherRetires !== false);
    const infrastructurePctVal = Number(infrastructurePct) || 0;
    const infrastructureReturnRateVal = Number(infrastructureReturnRate) || 6;
    const fixedTbcCap = resolveTransferBalanceCapForCalendarYear(Number(startYear) || 1980, generalTransferBalanceCap);
    const fixedYourTbcUsed = Number(yourTbcUsed) || 0;
    const fixedPartnerTbcUsed = Number(partnerTbcUsed) || 0;
    const fixedAccumTaxPortfolio = buildAccumTaxPortfolio(
        Number(sp500) || 0,
        Number(ausShares) || 0,
        Number(intlShares) || 0,
        Number(bonds) || 0,
        Number(cash) || 0
    );
    const infrastructureValueFromPct = infrastructurePctVal > 0 ? (totalSuper * (infrastructurePctVal / 100)) : 0;
    const infrastructureVal = Number(infrastructureValue) || infrastructureValueFromPct;
    const liquidSuper = totalSuper - infrastructureVal;
    const superAOriginal = Number(yourSuper) || 0;
    const superBOriginal = partnerSuperForCalc;
    const totalOriginalSuper = superAOriginal + superBOriginal;
    const ownerRatioA = totalOriginalSuper > 0 ? superAOriginal / totalOriginalSuper : 1;
    const ownerRatioB = isCouple ? (1 - ownerRatioA) : 0;
    const retAgeAForHousehold = (usePhasedHouseholdInputs || useCoupleMsiTrigger)
        ? (Number(yourRetirementAge) || yourAge)
        : yourAge;
    const retAgeBForHousehold = (usePhasedHouseholdInputs || useCoupleMsiTrigger)
        ? (Number(partnerRetirementAge) || partnerAge || yourAge)
        : (partnerAge || yourAge);
    const salaryAForHousehold = usePhasedHouseholdInputs ? (Number(yourSalary) || 0) : 0;
    const salaryBForHousehold = usePhasedHouseholdInputs ? (Number(partnerSalary) || 0) : 0;
    const partTimeAForHousehold = Number(yourPartTimeEmploymentIncome) || 0;
    const partTimeBForHousehold = Number(partnerPartTimeEmploymentIncome) || 0;
    const sgRateForHousehold = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;

    const createFixedHouseholdState = () => ({
        balA: Math.max(0, liquidSuper * ownerRatioA),
        balB: Math.max(0, isCouple ? liquidSuper * ownerRatioB : 0),
        infraBalA: Math.max(0, infrastructureVal * ownerRatioA),
        infraBalB: Math.max(0, isCouple ? infrastructureVal * ownerRatioB : 0),
        bridgeCashBalance: bridgeStrategyEnabled ? Math.max(0, Number(bridgeCashBucket) || 0) : 0,
        bridgeInvestmentBalance: bridgeStrategyEnabled ? Math.max(0, Number(bridgeInvestmentBucket) || 0) : 0,
        mortgageBalance: mortgageConfig.balance,
        residentialPropValue: Number(residentialPropertyValue) || 0,
        commercialPropValue: Number(commercialPropertyValue) || 0,
        collectiblesVal: Number(collectiblesValue) || 0,
        otherAlternativesVal: Number(otherAlternativesValue) || 0,
        accumHandoffApplied: { you: false, partner: false },
        capStateA: SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances),
        capStateB: SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances),
    });

    const simulateFixedHouseholdYear = ({
        state,
        i,
        year,
        r,
        inflation,
        targetIncome,
        annuityReal,
        dbReal,
        afterTaxDbReal,
        phasePolicy = true,
        forceMsiContinuity = false
    }) => {
        const currentAge = yourAge + i;
        const partnerCurrentAge = (partnerAge || yourAge) + i;
        const isARetired = currentAge >= retAgeAForHousehold;
        const isBRetired = partnerCurrentAge >= retAgeBForHousehold;
        // Smooth path to each member’s Accumulation nest egg (no cliff at retirement).
        let skipAccumPathGrowthA = false;
        let skipAccumPathGrowthB = false;
        if (inputs.accumulationHandoff?.enabled) {
            const handed = applyAccumulationHandoffPath({
                handoff: inputs.accumulationHandoff,
                applied: state.accumHandoffApplied,
                currentAge,
                partnerCurrentAge,
                retAgeA: retAgeAForHousehold,
                retAgeB: retAgeBForHousehold,
                balA: state.balA,
                balB: state.balB,
                isCouple,
                yearIndex: i,
                yourAgeStart: yourAge,
                partnerAgeStart: partnerAge || yourAge,
            });
            state.balA = handed.balA;
            state.balB = handed.balB;
            state.accumHandoffApplied = handed.applied;
            skipAccumPathGrowthA = !!handed.skipGrowthA;
            skipAccumPathGrowthB = !!handed.skipGrowthB;
        }
        // Lifestyle MSI only once the chosen retirement trigger is met. Before that,
        // accessible super may still take the statutory minimum (e.g. age 65+) but must
        // not be pushed up to the full sustainable-income target.
        const anyoneRetired = isARetired || (isCouple && isBRetired);
        // Couples + phased: either partner retires, or primary only. Otherwise always active.
        const lifestyleMsiActive = !useCoupleMsiTrigger
            || (startMsiOnEitherPartner ? anyoneRetired : isARetired);
        const lifestyleTargetIncome = lifestyleMsiActive ? targetIncome : 0;
        const { empIncomeA, empIncomeB, employmentIncome } = resolveEmploymentIncomesForYear({
            isARetired,
            isBRetired: isCouple ? isBRetired : true,
            salaryA: salaryAForHousehold,
            salaryB: salaryBForHousehold,
            partTimeA: partTimeAForHousehold,
            partTimeB: isCouple ? partTimeBForHousehold : 0,
            useAfterTax: true,
        });
        // Path years already embed SG/earnings; after lock, retired members get no SG.
        const sgA = (isARetired || state.accumHandoffApplied?.you || skipAccumPathGrowthA)
            ? 0
            : calculateSGContributions(salaryAForHousehold, sgRateForHousehold);
        const sgB = (isBRetired || state.accumHandoffApplied?.partner || skipAccumPathGrowthB)
            ? 0
            : calculateSGContributions(salaryBForHousehold, sgRateForHousehold);

        // Total balance at the START of this year (before this year's return) — displayed in the table so "withdrawal ÷ balance shown" matches the real
        // statutory-minimum / MSI rate actually applied, regardless of accessibility.
        const openingTotalBalanceForRow = state.balA + state.balB + (state.infraBalA || 0) + (state.infraBalB || 0);

        const liquidBalABeforeReturn = canAccessSuperWithBucketStrategy({
            currentAge,
            retirementAge: yourRetirementAge,
            accessAge: yourSuperAccessAge,
            allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
            enableBridgeStrategy: bridgeStrategyEnabled,
            householdRetirementTriggered: anyoneRetired,
        })
            ? state.balA : 0;
        const liquidBalBBeforeReturn = isCouple && canAccessSuperWithBucketStrategy({
            currentAge: partnerCurrentAge,
            retirementAge: partnerRetirementAge,
            accessAge: partnerSuperAccessAge,
            allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
            enableBridgeStrategy: bridgeStrategyEnabled,
            householdRetirementTriggered: anyoneRetired,
        })
            ? state.balB : 0;
        const accessibleLiquidBeforeReturn = liquidBalABeforeReturn + liquidBalBBeforeReturn;

        if (!skipAccumPathGrowthA) {
            state.balA = Math.max(0, applyPhasedMemberReturnWithTax(
                state.balA, r, isARetired ? fixedTbcCap - fixedYourTbcUsed : 0, fixedAccumTaxPortfolio, year, inflation
            ));
        }
        if (!skipAccumPathGrowthB) {
            state.balB = Math.max(0, applyPhasedMemberReturnWithTax(
                state.balB, r, isBRetired ? fixedTbcCap - fixedPartnerTbcUsed : 0, fixedAccumTaxPortfolio, year, inflation
            ));
        }
        state.residentialPropValue *= (1 + residentialGrowthRate);
        state.commercialPropValue *= (1 + commercialGrowthRate);
        state.collectiblesVal *= (1 + collectiblesGrowthRate);
        state.otherAlternativesVal *= (1 + otherAlternativesGrowthRate);
        if (bridgeStrategyEnabled && state.bridgeInvestmentBalance > 0) {
            state.bridgeInvestmentBalance = Math.max(0, state.bridgeInvestmentBalance * (1 + bridgeInvestmentRealReturn));
        }

        const resVal0 = Number(residentialPropertyValue) || 0;
        const comVal0 = Number(commercialPropertyValue) || 0;
        const fixedInflation = historicalData[year]?.inflation ?? 0.025;
        const currentResidentialValue = resVal0 * Math.pow(1 + residentialGrowthRate - fixedInflation, i);
        const currentCommercialValue = comVal0 * Math.pow(1 + commercialGrowthRate - fixedInflation, i);
        const rentalIncome = (
            resVal0 > 0 && residentialRentalIncomeAnnual > 0
                ? (residentialRentalIncomeAnnual / resVal0) * currentResidentialValue
                : 0
        ) + (
            comVal0 > 0 && commercialRentalIncomeAnnual > 0
                ? (commercialRentalIncomeAnnual / comVal0) * currentCommercialValue
                : 0
        );
        const infrastructureBalance = state.infraBalA + state.infraBalB;
        const infrastructureDividends = infrastructureBalance * (infrastructureReturnRateVal / 100);
        const liquidBalance = state.balA + state.balB;
        const totalAlternativeAssets = state.residentialPropValue + state.commercialPropValue + state.collectiblesVal + state.otherAlternativesVal;
        const estimatedAssets = otherAssetsValue + Math.max(0, liquidBalance) + infrastructureBalance + totalAlternativeAssets;
        const otherIncomeForPension = other + otherNonTax + annuityReal + dbReal + rentalIncome + infrastructureDividends;
        const pensionAnnual = calculateAgePensionForHousehold({
            totalAssets: estimatedAssets,
            otherIncome: otherIncomeForPension,
            isCouple,
            isHomeowner: homeowner,
            year: i,
            pensionGrowthRate: pensionGrowthRateVal,
            rentPaidAnnual,
            employmentIncome,
            ageA: currentAge,
            ageB: partnerCurrentAge,
            includePension: includeAgePension
        });
        const canAAccessSuper = canAccessSuperWithBucketStrategy({
            currentAge,
            retirementAge: yourRetirementAge,
            accessAge: yourSuperAccessAge,
            allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
            enableBridgeStrategy: bridgeStrategyEnabled,
            householdRetirementTriggered: anyoneRetired,
        });
        const canBAccessSuper = isCouple && canAccessSuperWithBucketStrategy({
            currentAge: partnerCurrentAge,
            retirementAge: partnerRetirementAge,
            accessAge: partnerSuperAccessAge,
            allowWithdrawalsBeforeRetirementAge: allowWithdrawalsBeforeRetirement,
            enableBridgeStrategy: bridgeStrategyEnabled,
            householdRetirementTriggered: anyoneRetired,
        });
        // Accumulation-path years never draw down (balance is the handoff ramp).
        const inPlanDrawdownA = !skipAccumPathGrowthA && canAAccessSuper;
        const inPlanDrawdownB = !skipAccumPathGrowthB && canBAccessSuper;

        const dbIncomeForHousehold = afterTaxDbReal > 0
            ? afterTaxDbReal
            : (dbReal > 0
                ? Math.max(0, calculateAfterTaxIncome(other + dbReal) - calculateAfterTaxIncome(other))
                : 0);
        const totalOtherIncome = computeHouseholdOtherIncome({
            pensionAnnual,
            employmentIncome,
            other,
            otherNonTax,
            annuityReal,
            afterTaxDbReal: dbIncomeForHousehold,
            dbReal: 0,
            rentalIncome,
            infrastructureDividends
        });
        const mortgageYear = applyMortgageYear(state, mortgageConfig, i);
        const mortgagePayoffFromSuper = mortgageConfig.payoffSource === 'super' ? (mortgageYear.mortgagePayoff || 0) : 0;
        const mortgageGeneralOutflow = Math.max(0, (mortgageYear.mortgageOutflow || 0) - mortgagePayoffFromSuper);
        const totalNeedAfterOtherIncome = Math.max(0, lifestyleTargetIncome + mortgageGeneralOutflow - totalOtherIncome);
        let bridgeWithdrawal = 0;
        let bridgeCashWithdrawal = 0;
        let bridgeInvestmentWithdrawal = 0;
        const bridgeBalanceBeforeWithdrawal = (state.bridgeCashBalance || 0) + (state.bridgeInvestmentBalance || 0);
        let needFromSuper = totalNeedAfterOtherIncome;
        if (bridgeStrategyEnabled && needFromSuper > 0) {
            bridgeCashWithdrawal = Math.min(needFromSuper, Math.max(0, state.bridgeCashBalance || 0));
            state.bridgeCashBalance = Math.max(0, (state.bridgeCashBalance || 0) - bridgeCashWithdrawal);
            needFromSuper -= bridgeCashWithdrawal;

            bridgeInvestmentWithdrawal = Math.min(needFromSuper, Math.max(0, state.bridgeInvestmentBalance || 0));
            state.bridgeInvestmentBalance = Math.max(0, (state.bridgeInvestmentBalance || 0) - bridgeInvestmentWithdrawal);
            needFromSuper -= bridgeInvestmentWithdrawal;
            bridgeWithdrawal = bridgeCashWithdrawal + bridgeInvestmentWithdrawal;
        }
        needFromSuper += mortgagePayoffFromSuper;
        const accessibleBalA = inPlanDrawdownA ? state.balA : 0;
        const accessibleBalB = inPlanDrawdownB ? state.balB : 0;
        const accessibleInfraA = inPlanDrawdownA ? state.infraBalA : 0;
        const accessibleInfraB = inPlanDrawdownB ? state.infraBalB : 0;
        const accessibleLiquid = accessibleBalA + accessibleBalB;
        const accessibleInfra = accessibleInfraA + accessibleInfraB;
        const totalAvailable = accessibleLiquid + accessibleInfra;
        const isPhase2 = (inPlanDrawdownA && !inPlanDrawdownB) || (!inPlanDrawdownA && inPlanDrawdownB);
        const partnerAIsOlder = yourAge > (partnerAge || 0);
        const olderPartnerCanAccess = partnerAIsOlder ? inPlanDrawdownA : inPlanDrawdownB;

        let actualNeedFromSuper = needFromSuper;
        let finalWithdrawal = 0;
        let impossible = false;
        let bridgeShortfall = 0;
        let withdrawalFromMemberA = 0;
        let withdrawalFromMemberB = 0;

        if (totalAvailable > 0) {
            // Per-member statutory minimums (SIS): each drawdown account uses that member's age band.
            // Before the lifestyle MSI trigger, primary-only mode must NOT keep drawing the partner's
            // minimum — otherwise “wait until I retire” still shows super withdrawals.
            const startOnEither = startMsiOnEitherPartner;
            const minA = (inPlanDrawdownA && (isARetired || allowWithdrawalsBeforeRetirement))
                ? liquidBalABeforeReturn * SimulationEngine.getMinimumWithdrawalRate(currentAge)
                : 0;
            const minB = (inPlanDrawdownB && (isBRetired || allowWithdrawalsBeforeRetirement))
                ? liquidBalBBeforeReturn * SimulationEngine.getMinimumWithdrawalRate(partnerCurrentAge)
                : 0;
            let minimumRequired;
            if (useCoupleMsiTrigger && !lifestyleMsiActive && !startOnEither) {
                // Primary-only, primary not retired yet: do not take the partner's minimum either.
                minimumRequired = minA;
            } else {
                minimumRequired = minA + minB;
            }

            if (useCoupleMsiTrigger && !lifestyleMsiActive) {
                // Pre-lifestyle-MSI: statutory minimum only (no full MSI target yet).
                finalWithdrawal = Math.max(Math.min(needFromSuper, totalAvailable), minimumRequired);
            } else if (phasePolicy && !shouldMaximizePhase2Withdrawals && phase2Plus3MSI > 0 && targetIncome > phase2Plus3MSI) {
                impossible = true;
            } else if (phasePolicy && isPhase2 && shouldMaximizePhase2Withdrawals && phase2MSI > 0 && targetIncome > phase2MSI) {
                impossible = true;
            } else if (phasePolicy && canAAccessSuper && canBAccessSuper && shouldMaximizePhase2Withdrawals && phase3MSI > 0 && targetIncome > phase3MSI) {
                impossible = true;
            } else if (phasePolicy && !shouldMaximizePhase2Withdrawals && phase2Plus3MSI > 0 && targetIncome > 0) {
                if (isPhase2 && olderPartnerCanAccess) {
                    const needFromSuperForPhase2Plus3 = Math.max(0, phase2Plus3MSI - totalOtherIncome);
                    actualNeedFromSuper = Math.max(0, targetIncome - totalOtherIncome);
                    finalWithdrawal = Math.max(Math.min(needFromSuperForPhase2Plus3, totalAvailable), minimumRequired);
                } else if (canAAccessSuper && canBAccessSuper) {
                    const needFromSuperForPhase2Plus3 = Math.max(0, phase2Plus3MSI - totalOtherIncome);
                    actualNeedFromSuper = Math.max(0, targetIncome - totalOtherIncome);
                    finalWithdrawal = Math.max(Math.min(needFromSuperForPhase2Plus3, totalAvailable), minimumRequired);
                } else {
                    finalWithdrawal = Math.max(Math.min(needFromSuper, totalAvailable), minimumRequired);
                }
            } else if (phasePolicy && isPhase2 && olderPartnerCanAccess && shouldMaximizePhase2Withdrawals && phase2MSI > 0 && targetIncome > 0) {
                const needFromSuperForPhase2 = Math.max(0, phase2MSI - totalOtherIncome);
                actualNeedFromSuper = Math.max(0, targetIncome - totalOtherIncome);
                finalWithdrawal = Math.max(Math.min(needFromSuperForPhase2, totalAvailable), minimumRequired);
            } else if (phasePolicy && canAAccessSuper && canBAccessSuper && shouldMaximizePhase2Withdrawals && phase3MSI > 0 && targetIncome > 0) {
                const needFromSuperForPhase3 = Math.max(0, phase3MSI - totalOtherIncome);
                actualNeedFromSuper = Math.max(0, targetIncome - totalOtherIncome);
                finalWithdrawal = Math.max(Math.min(needFromSuperForPhase3, totalAvailable), minimumRequired);
            } else {
                finalWithdrawal = Math.max(Math.min(needFromSuper, totalAvailable), minimumRequired);
            }

            if (forceMsiContinuity) {
                const fixedMsiGap = Math.max(0, lifestyleTargetIncome + mortgageYear.mortgageOutflow - totalOtherIncome - bridgeWithdrawal);
                if (fixedMsiGap > 0) {
                    finalWithdrawal = Math.max(finalWithdrawal, Math.min(fixedMsiGap, totalAvailable));
                }
            }
        }

        if (forceMsiContinuity && finalWithdrawal <= 0) {
            const fixedMsiGap = Math.max(0, lifestyleTargetIncome + mortgageYear.mortgageOutflow - totalOtherIncome - bridgeWithdrawal);
            if (fixedMsiGap > 0 && totalAvailable > 0) {
                finalWithdrawal = Math.min(fixedMsiGap, totalAvailable);
            }
        }

        if (bridgeStrategyEnabled && needFromSuper > totalAvailable && (!inPlanDrawdownA && !inPlanDrawdownB)) {
            bridgeShortfall = needFromSuper;
        }
        const bridgeBalanceAfterWithdrawal = (state.bridgeCashBalance || 0) + (state.bridgeInvestmentBalance || 0);
        const bridgeDepletedBeforeAccess = bridgeStrategyEnabled
            && !inPlanDrawdownA
            && !inPlanDrawdownB
            && bridgeBalanceBeforeWithdrawal > 0
            && bridgeBalanceAfterWithdrawal <= 1;

        if (finalWithdrawal > 0 && totalAvailable > 0) {
            if (accessibleLiquid >= finalWithdrawal) {
                const ratioA = accessibleLiquid > 0 ? accessibleBalA / accessibleLiquid : 0;
                withdrawalFromMemberA = inPlanDrawdownA ? finalWithdrawal * ratioA : 0;
                withdrawalFromMemberB = inPlanDrawdownB ? finalWithdrawal * (1 - ratioA) : 0;
                if (inPlanDrawdownA) state.balA -= withdrawalFromMemberA;
                if (inPlanDrawdownB) state.balB -= withdrawalFromMemberB;
            } else {
                const fromInfra = finalWithdrawal - accessibleLiquid;
                withdrawalFromMemberA = accessibleBalA + (accessibleInfra > 0 && inPlanDrawdownA
                    ? fromInfra * (accessibleInfraA / accessibleInfra) : 0);
                withdrawalFromMemberB = accessibleBalB + (accessibleInfra > 0 && inPlanDrawdownB
                    ? fromInfra * (accessibleInfraB / accessibleInfra) : 0);
                if (inPlanDrawdownA) state.balA = 0;
                if (inPlanDrawdownB) state.balB = 0;
                if (accessibleInfra > 0) {
                    const infraRatioA = accessibleInfraA / accessibleInfra;
                    if (inPlanDrawdownA) state.infraBalA -= fromInfra * infraRatioA;
                    if (inPlanDrawdownB) state.infraBalB -= fromInfra * (1 - infraRatioA);
                }
            }
        }

        let lumpSumWithdrawal = 0;
        let lumpSumShortfall = 0;
        const lumpRequested = getLumpSumAmountForYear(lumpSums, { yearIndex: i, yourAge, partnerAge: isCouple ? partnerAge : yourAge });
        if (lumpRequested > 0) {
            const accBalA = canAAccessSuper ? state.balA : 0;
            const accBalB = canBAccessSuper ? state.balB : 0;
            const accInfraA = canAAccessSuper ? state.infraBalA : 0;
            const accInfraB = canBAccessSuper ? state.infraBalB : 0;
            const accLiquid = accBalA + accBalB;
            const accInfra = accInfraA + accInfraB;
            const accTotal = accLiquid + accInfra;
            lumpSumWithdrawal = Math.min(lumpRequested, accTotal);
            lumpSumShortfall = lumpRequested - lumpSumWithdrawal;
            if (lumpSumWithdrawal > 0 && accTotal > 0) {
                if (accLiquid >= lumpSumWithdrawal) {
                    const ratioA = accLiquid > 0 ? accBalA / accLiquid : 0;
                    if (canAAccessSuper) state.balA -= lumpSumWithdrawal * ratioA;
                    if (canBAccessSuper) state.balB -= lumpSumWithdrawal * (1 - ratioA);
                } else {
                    const fromInfra = lumpSumWithdrawal - accLiquid;
                    if (canAAccessSuper) state.balA = 0;
                    if (canBAccessSuper) state.balB = 0;
                    if (accInfra > 0) {
                        const infraRatioA = accInfraA / accInfra;
                        if (canAAccessSuper) state.infraBalA -= fromInfra * infraRatioA;
                        if (canBAccessSuper) state.infraBalB -= fromInfra * (1 - infraRatioA);
                    }
                }
            }
        }

        ({ balA: state.balA, balB: state.balB, infraBalA: state.infraBalA, infraBalB: state.infraBalB } = chargeMemberFees({
            balA: state.balA, balB: state.balB, infraBalA: state.infraBalA, infraBalB: state.infraBalB, isCouple, feeInputs
        }));

        const contributionApplied = applyYearLumpSumContributions({
            inputs,
            contributions: lumpContributions,
            yearIndex: i,
            yourAge,
            partnerAge,
            isCouple,
            balA: state.balA,
            balB: state.balB,
            sgA,
            sgB,
            capStateA: state.capStateA,
            capStateB: state.capStateB,
        });
        // Don't let lump-sum contribution helper overwrite the accum path balance.
        if (!skipAccumPathGrowthA) state.balA = contributionApplied.balA;
        if (!skipAccumPathGrowthB) state.balB = contributionApplied.balB;

        const totalBalance = state.balA + state.balB + state.infraBalA + state.infraBalB;
        return {
            currentAge,
            partnerCurrentAge,
            balance: totalBalance,
            balanceMemberA: Math.round((state.balA || 0) + (state.infraBalA || 0)),
            balanceMemberB: Math.round((state.balB || 0) + (state.infraBalB || 0)),
            openingBalance: openingTotalBalanceForRow,
            liquidBalance: state.balA + state.balB,
            pensionAnnual,
            employmentIncome,
            rentalIncome,
            infrastructureDividends,
            annuityReal,
            dbIncomeForHousehold,
            totalOtherIncome,
            needFromSuper,
            actualNeedFromSuper,
            actualWithdrawal: finalWithdrawal,
            withdrawalFromMemberA,
            withdrawalFromMemberB,
            bridgeWithdrawal,
            bridgeCashWithdrawal,
            bridgeInvestmentWithdrawal,
            bridgeCashBalance: state.bridgeCashBalance || 0,
            bridgeInvestmentBalance: state.bridgeInvestmentBalance || 0,
            bridgeShortfall,
            bridgeDepletedBeforeAccess,
            mortgageOpeningBalance: mortgageYear.mortgageOpeningBalance,
            mortgageBalance: mortgageYear.mortgageBalance,
            mortgageInterest: mortgageYear.mortgageInterest,
            mortgageRepayment: mortgageYear.mortgageRepayment,
            mortgagePayoff: mortgageYear.mortgagePayoff,
            mortgagePayoffFromSuper,
            mortgagePayoffSource: mortgageConfig.payoffSource,
            mortgageOutflow: mortgageYear.mortgageOutflow,
            mortgagePaidOff: mortgageYear.mortgagePaidOff,
            lumpSumWithdrawal,
            lumpSumShortfall,
            lumpSumContribution: contributionApplied.lumpSumContribution,
            contributionsA: contributionApplied.contributionsA,
            contributionsB: contributionApplied.contributionsB,
            concessionalContributionsA: contributionApplied.concessionalContributionsA,
            concessionalContributionsB: contributionApplied.concessionalContributionsB,
            nonConcessionalContributionsA: contributionApplied.nonConcessionalContributionsA,
            nonConcessionalContributionsB: contributionApplied.nonConcessionalContributionsB,
            contributionFlags: contributionApplied.contributionFlags,
            totalIncome: totalOtherIncome + bridgeWithdrawal + finalWithdrawal + lumpSumWithdrawal - mortgageYear.mortgageOutflow,
            impossible
        };
    };

    // Keep the requested estate target. If even compulsory minimum withdrawals
    // finish below it, do not drop the target and spend the super to $0.
    const requestedEndingBalance = Number(targetFinalBalance) || 0;
    let requiredEndingBalance = requestedEndingBalance < 10 ? 10 : requestedEndingBalance;
    let endingBalanceUnachievable = false;

    const isIncomeSustainable = (testIncome) => {
      const state = createFixedHouseholdState();
      const targetBalance = requiredEndingBalance;
      
      let _ci = 1.0; // cumulative inflation factor for non-indexed streams
      let latestResult = null;
      for (let i = 0; i < returnsArray.length; i++) {
        const { year, r, inflation } = returnsArray[i];
        const _yInfl = historicalData[year]?.inflation ?? 0.025;
        const _annReal = annuityIsIndexed ? annuityPay : annuityPay / _ci;
        const _dbReal  = definedBenefitIsIndexed ? dbPension : dbPension / _ci;
        const _atDbReal = _dbReal > 0 ? Math.max(0, calculateAfterTaxIncome(other + _dbReal) - calculateAfterTaxIncome(other)) : 0;

        latestResult = simulateFixedHouseholdYear({
            state,
            i,
            year,
            r,
            inflation,
            targetIncome: testIncome,
            annuityReal: _annReal,
            dbReal: _dbReal,
            afterTaxDbReal: _atDbReal,
            phasePolicy: true
        });

        if (latestResult.impossible || latestResult.actualWithdrawal < latestResult.actualNeedFromSuper - 1) {
            return false;
        }
        if ((latestResult.lumpSumShortfall || 0) > 0.5) {
            return false;
        }

        _ci *= (1 + _yInfl); // advance cumulative inflation for next year
      }
      
      const totalFinalBalance = latestResult ? latestResult.balance : 0;
      // CRITICAL: Final balance must be >= target balance
      // Income sustainability in final year is already checked in the loop above
      const isSustainable = totalFinalBalance >= targetBalance;
      
      // Log when $0 income test fails
      if (testIncome === 0 && !isSustainable) {
        const finalYear = returnsArray.length - 1;
        console.warn('⚠️ $0 income test failed:', {
          totalFinalBalance,
          targetBalance,
          liquidBalance: state.balA + state.balB,
          infrastructureBalance: state.infraBalA + state.infraBalB,
          year: finalYear,
          currentAge: yourAge + finalYear,
          partnerCurrentAge: (partnerAge || yourAge) + finalYear
        });
      }
      
      // Store final balance so we can optimize for closeness to target
      isIncomeSustainable._lastFinalBalance = totalFinalBalance;
      isIncomeSustainable._lastTestIncome = testIncome;
      
      return isSustainable;
    };
    
    // Calculate a more realistic upper bound that includes pension and other income
    const initialRentalIncome = residentialRentalIncomeAnnual + commercialRentalIncomeAnnual;
    const { annuityReal: ann0, dbReal: db0, dbIncomeForHousehold: dbInc0 } = getIndexedIncomeStreams({
        annuityPay, dbPension, annuityIsIndexed, definedBenefitIsIndexed, other,
        cumulativeInflationIndex: 1
    });
    const estimatedPension = calculateAgePensionForHousehold({
        totalAssets: otherAssetsValue + effectiveSuperForMsiBounds,
        otherIncome: computePensionAssessableOtherIncome({
            other, otherNonTax, annuityReal: ann0, dbReal: db0, rentalIncome: initialRentalIncome,
            infrastructureDividends: 0
        }),
        isCouple,
        isHomeowner: homeowner,
        year: 0,
        pensionGrowthRate: pensionGrowthRateVal,
        ageA: yourAge,
        ageB: partnerAge || yourAge,
        includePension: includeAgePension
    });
    const totalAvailableIncome = estimatedPension + computeHouseholdOtherIncome({
        pensionAnnual: 0,
        other,
        otherNonTax,
        annuityReal: ann0,
        afterTaxDbReal: dbInc0,
        dbReal: 0,
        rentalIncome: initialRentalIncome,
        infrastructureDividends: 0
    });
    const __tbInI = Number(targetFinalBalance) || 0;
    let targetBalance = __tbInI < 0 ? 0 : __tbInI; // Use 'let' so we can reassign if target is unachievable
    
    console.log('🔍 MSI Binary Search Setup:', {
        totalSuper,
        handoffNestEgg,
        effectiveSuperForMsiBounds,
        other,
        initialRentalIncome,
        estimatedPension,
        totalAvailableIncome,
        targetBalance,
        years,
        yourAge,
        yourRetirementAge,
        startYear: start
    });
    
    // CRITICAL FIX: Different logic for zero vs non-zero target balance
    let low, high;
    let best = 0; // Declare best at function level
    if (targetBalance === 0) {
        // For zero balance: find maximum sustainable income (traditional approach)
        // MINIMAX STRATEGY: Maximize the minimum income across all years
        // This handles cases where super is insufficient to maintain truly fixed income
        
        // Helper function: Calculate actual income achieved in each year for a given target
        const calculateMinimumIncome = (targetIncome) => {
            const state = createFixedHouseholdState();
            let minIncome = Infinity;
            let finalBalance = 0;
            let _ci2 = 1.0; // cumulative inflation for non-indexed streams
            for (let i = 0; i < returnsArray.length; i++) {
                const { year, r, inflation } = returnsArray[i];
                const _yInfl2 = historicalData[year]?.inflation ?? 0.025;
                const _annReal2 = annuityIsIndexed ? annuityPay : annuityPay / _ci2;
                const _dbReal2  = definedBenefitIsIndexed ? dbPension : dbPension / _ci2;
                const _atDbReal2 = _dbReal2 > 0 ? Math.max(0, calculateAfterTaxIncome(other + _dbReal2) - calculateAfterTaxIncome(other)) : 0;
                const currentAge = yourAge + i;
                const partnerCurrentAge = (partnerAge || yourAge) + i;
                const result = simulateFixedHouseholdYear({
                    state,
                    i,
                    year,
                    r,
                    inflation,
                    targetIncome,
                    annuityReal: _annReal2,
                    dbReal: _dbReal2,
                    afterTaxDbReal: _atDbReal2,
                    phasePolicy: true,
                    forceMsiContinuity: false
                });
                finalBalance = result.balance;
                
                // Track minimum income achieved across RETIREMENT years only (for phased retirement)
                if (usePhasedHouseholdInputs) {
                  const isARetired = currentAge >= retAgeAForHousehold;
                  const isBRetired = partnerCurrentAge >= retAgeBForHousehold;
                  // Only count years when BOTH partners are retired
                  if (isARetired && isBRetired) {
                minIncome = Math.min(minIncome, result.totalIncome);
                  }
                } else {
                  minIncome = Math.min(minIncome, result.totalIncome);
                }
                _ci2 *= (1 + _yInfl2); // advance cumulative inflation for next year
            }
            
            return { minIncome, finalBalance };
        };
        
        // Binary search to maximize the minimum income
        const baselineIncome = other + otherNonTax + annuityPay + afterTaxDbPension;
        low = baselineIncome; // Start from at least outside income
        high = baselineIncome + (effectiveSuperForMsiBounds * 0.30); // Outside income + reasonable super contribution
        
        let bestMinIncome = 0;
        let bestTargetIncome = baselineIncome;
        const tolerance = 100;
        
        for (let iter = 0; iter < 50; iter++) {
            const mid = (low + high) / 2;
            const { minIncome, finalBalance } = calculateMinimumIncome(mid);
            
            // For depletion strategy (target balance = 0), we want to use all super
            const balanceOk = finalBalance >= targetBalance - 1000; // Allow small tolerance
            
            // We want to maximize the minimum income achieved
            if (minIncome >= mid - 100 && balanceOk) {
                // This target is achievable - try higher
                bestMinIncome = minIncome;
                bestTargetIncome = mid;
                low = mid;
            } else {
                // Too ambitious - try lower
                high = mid;
            }
            
            if (Math.abs(high - low) < tolerance) break;
        }
        
        // For phased retirement: return actual retirement income only if it's valid (not Infinity)
        // If minIncome is Infinity, no retirement years exist - use target instead
        if (shouldUsePhasedRetirementCalculations({
          phasedRetirement,
          isCouple,
          yourAge,
          partnerAge,
          yourRetirementAge,
          partnerRetirementAge,
          yourSalary,
          partnerSalary
        }) && bestMinIncome !== Infinity && bestMinIncome > 0) {
          best = Math.round(bestMinIncome);
        } else {
          best = Math.round(bestTargetIncome);
        }
        
    } else {
        // For non-zero balance: find income that achieves target balance
        low = 0;
        high = effectiveSuperForMsiBounds + totalAvailableIncome; // Generous upper bound (handoff-aware)
        
        const tolerance = 100;
        
        // Store original target for logging
        const originalTargetBalance = targetBalance;
        
        // If $0 extra income still finishes below the requested balance, no higher
        // withdrawal can leave that balance. Do not switch the target to $0 and
        // spend the super down. The series stays on compulsory minimums, and MSI
        // is the lowest year actually paid.
        if (!isIncomeSustainable(0)) {
            const finalBalanceWithZeroIncome = isIncomeSustainable._lastFinalBalance || 0;
            endingBalanceUnachievable = true;
            best = 0;
            console.warn('⚠️ Target unachievable — compulsory minimum withdrawals finish below the requested final balance. Not spending the balance down.', {
                originalTarget: originalTargetBalance,
                finalBalanceWithZeroIncome,
                difference: originalTargetBalance - finalBalanceWithZeroIncome
            });
        } else {
            // Target is achievable - run normal binary search
            
            // Run binary search to find maximum sustainable income
            for (let iter = 0; iter < 50; iter++) {
                const mid = (low + high) / 2;
                if (isIncomeSustainable(mid)) { 
                    best = mid; 
                    low = mid; 
                } else { 
                    high = mid; 
                }
                if (Math.abs(high - low) < tolerance) break;
            }
            
            // RE-ENABLE target balance optimization, but with proper logging
            // The user WANTS the income to be optimized to hit the target balance
            // The previous implementation was correct in concept, just had bugs
            console.log('🎯 Checking optimization conditions:', { 
                best, 
                targetBalance, 
                willOptimize: (best > 0 && targetBalance >= 0),
                reason: targetBalance >= 0 ? 'Target balance specified' : 'No target balance'
            });
            
            if (best > 0 && targetBalance >= 0) {
            // Test the current best to see final balance
            isIncomeSustainable(best);
            const initialFinalBalance = isIncomeSustainable._lastFinalBalance || 0;
            
            console.log('🎯 Initial state:', {
                income: Math.round(best),
                finalBalance: Math.round(initialFinalBalance),
                targetBalance,
                distance: Math.round(Math.abs(initialFinalBalance - targetBalance)),
                needToIncrease: initialFinalBalance > targetBalance + 5000
            });
            
            // If final balance is significantly higher than target, we should increase income to spend down
            if (initialFinalBalance > targetBalance + 5000) {
                console.log('🎯 Final balance too high, searching for income to spend down to target...');
                
                let testIncome = best;
                let closestIncome = best;
                let closestDistance = Math.abs(initialFinalBalance - targetBalance);
                let closestFinalBalance = initialFinalBalance;
                
                // Binary search for income that hits target balance
                let lowIncome = best;
                let highIncome = best * 3.0; // Max 3x initial income
                
                for (let iter = 0; iter < 30; iter++) {
                    testIncome = (lowIncome + highIncome) / 2;
                    isIncomeSustainable(testIncome);
                    const finalBal = isIncomeSustainable._lastFinalBalance || 0;
                    const distance = Math.abs(finalBal - targetBalance);
                    
                    if (iter < 3 || iter === 29) {
                        console.log(`  Iter ${iter}: income=$${Math.round(testIncome)}, final=$${Math.round(finalBal)}, target=$${targetBalance}, dist=$${Math.round(distance)}`);
                    }
                    
                    if (finalBal < 0) {
                        // Too much income, balance went negative
                        highIncome = testIncome;
                    } else if (finalBal > targetBalance) {
                        // Not enough income, still too much left
                        lowIncome = testIncome;
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestIncome = testIncome;
                            closestFinalBalance = finalBal;
                        }
                    } else {
                        // finalBal <= targetBalance, we've overshot slightly
                        highIncome = testIncome;
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestIncome = testIncome;
                            closestFinalBalance = finalBal;
                        }
                    }
                    
                    // Stop if very close
                    if (distance < 2000 || Math.abs(highIncome - lowIncome) < 100) {
                        console.log(`  Converged at iter ${iter}`);
                        break;
                    }
                }
                
                if (closestIncome !== best && closestDistance < Math.abs(initialFinalBalance - targetBalance)) {
                    console.log('🎯 Optimization improved result:', {
                        originalIncome: Math.round(best),
                        optimizedIncome: Math.round(closestIncome),
                        improvement: Math.round(closestIncome - best),
                        originalFinal: Math.round(initialFinalBalance),
                        optimizedFinal: Math.round(closestFinalBalance),
                        originalDistance: Math.round(Math.abs(initialFinalBalance - targetBalance)),
                        optimizedDistance: Math.round(closestDistance)
                    });
                    best = closestIncome;
                } else {
                    console.log('🎯 No improvement from optimization');
                }
            }
            }
            
            // Ensure we never return 0 if there's other income available
            if (best === 0 && totalAvailableIncome > 0) {
                best = totalAvailableIncome;
            }
        } // End of else block (target is achievable)
    } // End of if-else for zero vs non-zero target
    const sustainable = Math.round(best);
    
    console.log('🔍 Binary Search Complete:', {
        best,
        sustainable,
        targetBalance,
        totalAvailableIncome,
        phase2Plus3MSI,
        shouldMaximizePhase2Withdrawals,
        totalSuper,
        estimatedPension,
        other,
        years
    });
    
    // Safety check: ensure we have a valid result
    if (!isFinite(sustainable) || sustainable < 0) {
        console.error('❌ ERROR: Invalid sustainable income calculated:', sustainable, 'best:', best);
        // Fallback to at least other income
        const fallbackIncome = Math.max(0, totalAvailableIncome);
        return {
            avgIncome: fallbackIncome,
            incomeByYear: [],
            finalBalance: Math.round(totalSuper)
        };
    }
    
    // Build detailed income series with actual balance progression
    let incomeByYear = [];
    let currentBalance = totalSuper;
    const userTargetIncome = Number(monteCarloTargetIncome) || 0;
    const seriesState = createFixedHouseholdState();
    const targetSeriesState = userTargetIncome > 0 ? createFixedHouseholdState() : null;
    
    let _ci3 = 1.0; // cumulative inflation for non-indexed streams
    for (let i = 0; i < years; i++) {
      const { year, r, inflation } = returnsArray[i];
      const _yInfl3 = historicalData[year]?.inflation ?? 0.025;
      const _annReal3 = annuityIsIndexed ? annuityPay : annuityPay / _ci3;
      const _dbReal3  = definedBenefitIsIndexed ? dbPension : dbPension / _ci3;
      const _atDbReal3 = _dbReal3 > 0 ? Math.max(0, calculateAfterTaxIncome(other + _dbReal3) - calculateAfterTaxIncome(other)) : 0;
      const currentAge = yourAge + i;
      const partnerCurrentAge = (partnerAge || yourAge) + i;
      
      // Do not splice in the recursive Phase 2+3 income series here. It can contain
      // a zero withdrawal row while the wrapper forces totalIncome to the MSI target,
      // producing impossible rows where the components do not add to the displayed total.
      // The live loop below recalculates access, minimum withdrawals, balances and fees
      // for each year from the current balances.
      
      const yearResult = simulateFixedHouseholdYear({
        state: seriesState,
        i,
        year,
        r,
        inflation,
        targetIncome: sustainable,
        annuityReal: _annReal3,
        dbReal: _dbReal3,
        afterTaxDbReal: _atDbReal3,
        phasePolicy: true,
        forceMsiContinuity: false
      });

      const actualWithdrawal = yearResult.actualWithdrawal;
      const pensionAnnual = yearResult.pensionAnnual;
      const employmentIncome = yearResult.employmentIncome;
      currentBalance = yearResult.balance;

      // Target-income balance: same engine as MSI, separate cloned state each year.
      let balanceWithTargetIncomeValue = null;
      if (targetSeriesState) {
        const targetYearResult = simulateFixedHouseholdYear({
          state: targetSeriesState,
          i,
          year,
          r,
          inflation,
          targetIncome: userTargetIncome,
          annuityReal: _annReal3,
          dbReal: _dbReal3,
          afterTaxDbReal: _atDbReal3,
          phasePolicy: true,
          forceMsiContinuity: true
        });
        balanceWithTargetIncomeValue = Math.round(targetYearResult.balance);
      }
      
      // Store this year's data AFTER updating balance
      // Fixed MSI is the binary-search target; actual totalIncome can exceed it when
      // statutory minimum withdrawals force a higher super payment in strong years.
      const calculatedTotalIncome = yearResult.totalIncome;
      const displayTotalIncome = Math.round(calculatedTotalIncome);
      const constantIncome = sustainable; // MSI target for comparison, not a substitute for actual components
      
      // DEBUG: Log when income exceeds MSI (minimum drawdown spike years)
      if (calculatedTotalIncome > constantIncome + 1000) {
        console.warn('📈 Minimum drawdown spike year:', {
          year: start + i,
          msiTarget: constantIncome,
          actualTotalIncome: Math.round(calculatedTotalIncome),
          excess: Math.round(calculatedTotalIncome - constantIncome),
          superWithdrawal: Math.round(yearResult.actualWithdrawal || 0)
        });
      }
      
      const mortgagePayoffFromSuperForRow = Math.round(yearResult.mortgagePayoffFromSuper || 0);
      const lifestyleWithdrawalFromSuper = Math.max(0, Math.round(actualWithdrawal) - mortgagePayoffFromSuperForRow);

      incomeByYear.push({
        year: start + i,
        yearIndex: i + 1,
        balance: Math.round(currentBalance),
        balanceMemberA: Math.round(yearResult.balanceMemberA || 0),
        balanceMemberB: Math.round(yearResult.balanceMemberB || 0),
        openingBalance: Math.round(yearResult.openingBalance || 0),
        balanceWithTargetIncome: balanceWithTargetIncomeValue,
        withdrawalFromSuper: lifestyleWithdrawalFromSuper,
        grossWithdrawalFromSuper: Math.round(actualWithdrawal),
        withdrawalFromMemberA: Math.round(yearResult.withdrawalFromMemberA || 0),
        withdrawalFromMemberB: Math.round(yearResult.withdrawalFromMemberB || 0),
        bridgeWithdrawal: Math.round(yearResult.bridgeWithdrawal || 0),
        bridgeCashWithdrawal: Math.round(yearResult.bridgeCashWithdrawal || 0),
        bridgeInvestmentWithdrawal: Math.round(yearResult.bridgeInvestmentWithdrawal || 0),
        bridgeCashBalance: Math.round(yearResult.bridgeCashBalance || 0),
        bridgeInvestmentBalance: Math.round(yearResult.bridgeInvestmentBalance || 0),
        bridgeShortfall: Math.round(yearResult.bridgeShortfall || 0),
        bridgeDepletedBeforeAccess: !!yearResult.bridgeDepletedBeforeAccess,
        mortgageOpeningBalance: Math.round(yearResult.mortgageOpeningBalance || 0),
        mortgageBalance: Math.round(yearResult.mortgageBalance || 0),
        mortgageInterest: Math.round(yearResult.mortgageInterest || 0),
        mortgageRepayment: Math.round(yearResult.mortgageRepayment || 0),
        mortgagePayoff: Math.round(yearResult.mortgagePayoff || 0),
        mortgagePayoffFromSuper: mortgagePayoffFromSuperForRow,
        mortgagePayoffSource: yearResult.mortgagePayoffSource || 'super',
        mortgageOutflow: Math.round(yearResult.mortgageOutflow || 0),
        mortgagePaidOff: !!yearResult.mortgagePaidOff,
        lumpSumWithdrawal: Math.round(yearResult.lumpSumWithdrawal || 0),
        lumpSumContribution: Math.round(yearResult.lumpSumContribution || 0),
        contributionsA: Math.round(yearResult.contributionsA || 0),
        contributionsB: Math.round(yearResult.contributionsB || 0),
        concessionalContributionsA: Math.round(yearResult.concessionalContributionsA || 0),
        concessionalContributionsB: Math.round(yearResult.concessionalContributionsB || 0),
        nonConcessionalContributionsA: Math.round(yearResult.nonConcessionalContributionsA || 0),
        nonConcessionalContributionsB: Math.round(yearResult.nonConcessionalContributionsB || 0),
        contributionFlags: yearResult.contributionFlags || [],
        pension: Math.round(pensionAnnual),
        ...formatStrategyIncomeDisplay({
            isPhasedRetirement: usePhasedHouseholdInputs,
            currentAge: yourAge + i,
            partnerCurrentAge: (partnerAge || yourAge) + i,
            retAgeA: retAgeAForHousehold,
            retAgeB: retAgeBForHousehold,
            salaryA: salaryAForHousehold,
            salaryB: salaryBForHousehold,
            other,
            otherNonTax,
            annuityReal: yearResult.annuityReal ?? _annReal3,
            dbIncomeForHousehold: yearResult.dbIncomeForHousehold ?? _atDbReal3,
            rentalIncome: yearResult.rentalIncome,
            infrastructureDividends: yearResult.infrastructureDividends
        }),
        realReturn: r,
        totalIncome: displayTotalIncome
      });
      
      if (i === 0 || i === 14 || i === 29) {
      }
      _ci3 *= (1 + _yInfl3); // advance cumulative inflation for next year
    }
    
    // Return object format matching other strategies (mobile expects this)
    // CRITICAL: Use the final balance from the last sustainability test (most accurate)
    // The year-by-year loop above is for generating income series, but may use income series data
    // which doesn't update balA/balB correctly. The binary search's last test has the true final balance.
    const finalBalanceFromYearData = currentBalance;
    const finalBalanceFromBinarySearch = isIncomeSustainable._lastFinalBalance || finalBalanceFromYearData;
    
    // Use the last year's balance from incomeByYear if available (most accurate)
    const finalBalanceFromIncomeByYear = incomeByYear.length > 0 ? incomeByYear[incomeByYear.length - 1].balance : finalBalanceFromYearData;
    const finalBalance = finalBalanceFromIncomeByYear;
    
    const __tbInF = Number(targetFinalBalance) || 0;
    const targetBalanceFinal = __tbInF < 10 ? 10 : __tbInF;
    const balanceDifference = Math.abs(finalBalance - targetBalanceFinal);
    
    // CRITICAL: Calculate MSI ONLY for retirement years (age >= retirement age)
    // Don't average over working years or pre-retirement years
    const yourAgeForAdjust = Number(yourAge) || 50;
    const yourRetirementAgeForAdjust = Number(yourRetirementAge) || yourAgeForAdjust;
    const partnerAgeForAdjust = Number(partnerAge) || yourAgeForAdjust;
    const partnerRetirementAgeForAdjust = Number(partnerRetirementAge) || partnerAgeForAdjust;
    const usePhasedAdjust = shouldUsePhasedRetirementCalculations({ phasedRetirement, isCouple });
    
    let retirementYearCount = 0;
    let retirementIncomeSum = 0;
    for (let i = 0; i < incomeByYear.length; i++) {
        const yearData = incomeByYear[i];
        const ageInYear = yourAgeForAdjust + i;
        const partnerAgeInYear = partnerAgeForAdjust + i;
        
        const isRetiredYear = usePhasedAdjust
            ? (ageInYear >= yourRetirementAgeForAdjust && partnerAgeInYear >= partnerRetirementAgeForAdjust)
            : (ageInYear >= yourRetirementAgeForAdjust);
        
        if (isRetiredYear && yearData.totalIncome > 0) {
            retirementYearCount++;
            retirementIncomeSum += yearData.totalIncome;
        }
    }
    
    // Phased couples: MSI is the minimax binary-search target, not the average of
    // year-by-year totals (which vary with employment, pension, and minimum withdrawals).
    const adjustedSustainable = usePhasedAdjust
        ? sustainable
        : ((retirementYearCount === incomeByYear.length)
            ? sustainable
            : (retirementYearCount > 0 ? retirementIncomeSum / retirementYearCount : sustainable));
    
    if (retirementYearCount !== incomeByYear.length && retirementYearCount > 0) {
        console.log('📊 MSI Adjusted for mixed working/retirement period:', {
            originalMSI: Math.round(sustainable),
            adjustedMSI: Math.round(adjustedSustainable),
            totalYears: incomeByYear.length,
            retirementYears: retirementYearCount,
            workingYears: incomeByYear.length - retirementYearCount,
            reason: 'Recalculated to exclude working years'
        });
    }
    
    console.log('📊 MSI Calculation:', {
        originalMSI: Math.round(sustainable),
        adjustedMSI: Math.round(adjustedSustainable),
        totalYears: incomeByYear.length,
        retirementYears: retirementYearCount,
        workingYears: incomeByYear.length - retirementYearCount
    });
    
    // CRITICAL: Verify final balance matches target (within tolerance)
    if (balanceDifference > 1000) {
        console.warn('⚠️ Final balance mismatch:', {
            finalBalance,
            targetBalance: targetBalanceFinal,
            difference: balanceDifference,
            sustainable: adjustedSustainable,
            tolerance: 1000
        });
    }

    incomeByYear = incomeByYear.map((row) => {
        if (
            row.lumpSumContribution !== undefined
            && row.contributionFlags !== undefined
            && row.concessionalContributionsA !== undefined
            && row.nonConcessionalContributionsA !== undefined
        ) {
            return row;
        }
        const i = Math.max(0, Number(row.yearIndex || 1) - 1);
        const currentAge = yourAge + i;
        const partnerCurrentAge = (partnerAge || yourAge) + i;
        const isARetired = currentAge >= retAgeAForHousehold;
        const isBRetired = partnerCurrentAge >= retAgeBForHousehold;
        const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
        const sgA = usePhasedHouseholdInputs && !isARetired ? calculateSGContributions(salaryAForHousehold, sgRate) : 0;
        const sgB = usePhasedHouseholdInputs && isCouple && !isBRetired ? calculateSGContributions(salaryBForHousehold, sgRate) : 0;
        const scheduledContributions = getLumpSumContributionsForYear(lumpContributions, {
            yearIndex: i,
            yourAge,
            partnerAge,
            isCouple,
        });
        const summary = summarizeYearContributions({
            scheduledContributions,
            sgA,
            sgB,
            yourAge: currentAge,
            partnerAge: partnerCurrentAge,
            balA: row.balance || 0,
            balB: isCouple ? row.balance || 0 : 0,
            isCouple,
        });
        return {
            ...row,
            lumpSumContribution: Math.round(summary.totalScheduled),
            contributionsA: Math.round(summary.byMember.you.scheduled + summary.byMember.you.sg),
            contributionsB: isCouple ? Math.round(summary.byMember.partner.scheduled + summary.byMember.partner.sg) : 0,
            concessionalContributionsA: Math.round(summary.byMember.you.concessional),
            concessionalContributionsB: isCouple ? Math.round(summary.byMember.partner.concessional) : 0,
            nonConcessionalContributionsA: Math.round(summary.byMember.you.nonConcessional),
            nonConcessionalContributionsB: isCouple ? Math.round(summary.byMember.partner.nonConcessional) : 0,
            contributionFlags: summary.flags,
        };
    });
    
    // A Fixed result is a straight line, plus years forced above it by the
    // statutory minimum drawdown. If that line cannot be held, do not report $0.
    // MSI is the lowest year actually paid, and the UI explains that it varies.
    //
    // CRITICAL: Only evaluate the Fixed line over years where the retirement MSI
    // applies. Pre-retirement employment years are often below the MSI target
    // (salary < sustainable drawdown) and must not collapse reported MSI / rolling
    // bars / MC income percentiles to after-tax salary (the flat $107k ceiling).
    const msiEvalTotals = [];
    for (let i = 0; i < incomeByYear.length; i++) {
        const ageInYear = yourAgeForAdjust + i;
        const partnerAgeInYear = partnerAgeForAdjust + i;
        let countsForMsi;
        if (useCoupleMsiTrigger) {
            const anyoneRetired = ageInYear >= retAgeAForHousehold
                || (isCouple && partnerAgeInYear >= retAgeBForHousehold);
            const primaryRetired = ageInYear >= retAgeAForHousehold;
            countsForMsi = startMsiOnEitherPartner ? anyoneRetired : primaryRetired;
        } else if (usePhasedAdjust) {
            countsForMsi = ageInYear >= yourRetirementAgeForAdjust
                && partnerAgeInYear >= partnerRetirementAgeForAdjust;
        } else {
            countsForMsi = ageInYear >= yourRetirementAgeForAdjust;
        }
        if (countsForMsi) {
            msiEvalTotals.push(Math.round(Number(incomeByYear[i].totalIncome) || 0));
        }
    }
    const yearTotalsForMsi = msiEvalTotals.length
        ? msiEvalTotals
        : incomeByYear.map((row) => Math.round(Number(row.totalIncome) || 0));
    const minimumYearIncome = yearTotalsForMsi.length ? Math.min(...yearTotalsForMsi) : 0;
    const flatTarget = Math.round(Number(sustainable) || 0);
    const lineHeld = flatTarget > 0 && yearTotalsForMsi.every((total) => total + 500 >= flatTarget);
    const fixedIncomeVaries = endingBalanceUnachievable
        ? minimumYearIncome > 0
        : (!lineHeld && minimumYearIncome > 0);
    const reportedIncome = fixedIncomeVaries
        ? minimumYearIncome
        : Math.round(adjustedSustainable);

    const result = {
        avgIncome: reportedIncome,
        incomeByYear: incomeByYear,
        finalBalance: Math.round(finalBalance),
        fixedIncomeVaries,
        minimumYearIncome,
        endingBalanceUnachievable,
        requestedEndingBalance: Math.round(requestedEndingBalance)
    };
    if (!quietMode) {
    console.log('✅ RETURNING RESULT:', {
        ...result,
        incomeByYearLength: incomeByYear.length,
        firstFewYears: incomeByYear.slice(0, 5),
        lastFewYears: incomeByYear.slice(-5),
        hasValidValues: incomeByYear.every(v => typeof v === 'number' && !isNaN(v))
    });
    }
    return result;
    } finally {
        restoreConsole();
    }
};
